import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import api from '../services/api';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { useAuth } from '../contexts/AuthContext';
import s from './PDV.module.css';
import { gerarCupomVenda, gerarA4Venda, gerarDanfeNfceVenda, gerarPreviewDanfSe, abrirDanfseOficial, abrirDanfeNfePdf, abrirBoleto, fecharComEsc, aguardarImagens, imprimirFechamentoCaixaTermico, codigoItem } from '../utils/impressaoVenda';
import { larguraEfetivaCSS } from '../utils/impressaoMargem';
import { hojeBR } from '../utils/data';
import { lerPesoBalanca } from '../utils/balanca';
import { configEtiqueta, lerEtiquetaBalanca, quantidadeDaEtiqueta, produtoBateEtiqueta } from '../utils/etiquetaBalanca';
import ModalTotaisCaixa from '../components/pdv/ModalTotaisCaixa';
import ModalAbrirCaixa from '../components/pdv/ModalAbrirCaixa';
import ModalCliente from '../components/pdv/ModalCliente';
import ModalFecharCaixa from '../components/pdv/ModalFecharCaixa';
import ModalPreVenda from '../components/pdv/ModalPreVenda';
import ModalDiagnosticoNfce from '../components/pdv/ModalDiagnosticoNfce';
import ModalObservacao from '../components/pdv/ModalObservacao';
import MenuOpcoes from '../components/pdv/MenuOpcoes';
import LayoutTres from '../components/pdv/LayoutTres';
import ModalCompletarCliente from '../components/vendas/ModalCompletarCliente';
import ModalSelecionarCliente from '../components/vendas/ModalSelecionarCliente';
import ModalBuscaProdutos from '../components/pdv/ModalBuscaProdutos';
import ModalBuscarMovimentacao from '../components/pdv/ModalBuscarMovimentacao';
import ModalCorrecaoFiscal from '../components/pdv/ModalCorrecaoFiscal';
import ModalFiscal from '../components/pdv/ModalFiscal';
import ModalSalvarVenda from '../components/pdv/ModalSalvarVenda';
import ModalCobrancaPoint from '../components/pdv/ModalCobrancaPoint';
import ModalVendedor from '../components/pdv/ModalVendedor';
import ModalMesasComandas from '../components/pdv/ModalMesasComandas';
import ModalPesados from '../components/pdv/ModalPesados';
import PainelSincronizacao from '../components/pdv/PainelSincronizacao';
import useOnlineStatus from '../hooks/useOnlineStatus';
import useSyncQueue from '../hooks/useSyncQueue';
import useBuscaProdutosF3, { FILTROS_F3_VAZIOS as FILTROS_VAZIOS } from '../hooks/useBuscaProdutosF3';
import {
  enqueueVenda, cacheProdutos, getProdutosCache,
  cacheCliente, cacheClientes, getClientesCache,
  cacheConfig, getConfig,
} from '../services/offlineDb';
import { calcularPrecoTabela } from '../utils/precoTabela';
import {
  abrirGaveta, listarImpressoras, salvarImpressoraGaveta, impressoraGavetaSalva,
  impressoraImpressaoSalva, salvarImpressoraImpressao, impressaoDiretaAtiva, salvarImpressaoDireta,
} from '../utils/gaveta';
import { getMargemImpressaoMM, setMargemImpressaoMM } from '../utils/impressaoMargem';
import { tentarImprimirDiretoHtml } from '../utils/impressaoDireta';
import { BRAND_SUFIXO_CURTO } from '../brand';
import { prepararWhatsApp } from '../utils/abrirWhatsApp';

const fmtBRL = v =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parsePT = v => parseFloat(String(v).replace(',', '.')) || 0;
// Tolerante a maiúsculas/espaços — mesmo padrão usado no resto do sistema.
const ehServico = t => String(t || '').trim().toLowerCase() === 'serviço';

// ── Rascunho do carrinho (F5 não pode perder itens já lançados) ────────
// Só pra venda avulsa (sem mesa/comanda nem pedido importado — esses têm
// dono no servidor, não faz sentido re-hidratar um estado local velho por
// cima). Guardado no localStorage (não no IndexedDB da fila de sync —
// aqui é sempre síncrono e só interessa a esta aba/PC).
const RASCUNHO_VALIDADE_MS = 12 * 60 * 60 * 1000; // 12h — cobre um turno/dobra, não ressuscita lixo de dias atrás
const chaveRascunhoPdv = usuario => `pdv_rascunho_${usuario?.organizacao_id}_${usuario?.id}`;
function lerRascunhoPdv(usuario) {
  if (!usuario?.organizacao_id || !usuario?.id) return null;
  try {
    const bruto = localStorage.getItem(chaveRascunhoPdv(usuario));
    if (!bruto) return null;
    const dados = JSON.parse(bruto);
    if (!Array.isArray(dados.itens) || dados.itens.length === 0) return null;
    if (!dados.salvoEm || Date.now() - dados.salvoEm > RASCUNHO_VALIDADE_MS) return null;
    return dados;
  } catch {
    return null;
  }
}

const calcularPreco = calcularPrecoTabela;

// Unidade que não fraciona (UN, CX, PC...) só aceita quantidade inteira;
// unidades fracionáveis (LT, KG, MT...) seguem o flag do cadastro de unidades.
// Produto "coringa" (permite_alterar_qtd_preco) é isento dessa checagem —
// vende item avulso ainda sem cadastro próprio, não faz sentido travar
// numa unidade (normalmente UN) que nem é do item de verdade.
function validarQtdFracao(produto, q) {
  if (produto?.permite_alterar_qtd_preco || produto?.unidade_fraciona || Number.isInteger(q)) return true;
  alert(
    `Quantidade ${String(q).replace('.', ',')} não permitida:\n` +
    `a unidade "${produto?.unidade || 'UN'}" de "${produto?.nome}" não fraciona.\n\n` +
    `Informe uma quantidade inteira.`
  );
  return false;
}

// Produto pode ter mais de 1 código de barras (codigos_barras_produto —
// balança, fardo, unidade etc.). O bipe/Enter da busca comparava só com o
// código PRINCIPAL (produto.codigo_barras), então um item cadastrado com um
// código secundário como principal-de-uso não era reconhecido: o backend
// já retorna certo (busca em produtos.codigo_barras OU codigos_barras_produto),
// mas o filtro no cliente descartava o resultado antes de chegar na venda.
function produtoBateCodigo(p, termo) {
  if (String(p.codigo_interno) === termo || p.codigo_barras === termo) return true;
  return Array.isArray(p.codigos_barras) && p.codigos_barras.some(cb => cb.codigo === termo);
}

// Distingue "achou pelo código interno" de "achou por código de barras" —
// usado pelo parâmetro "Pedir quantidade ao lançar por código interno"
// (Organização → Parâmetros → PDV): só se aplica quando o termo digitado é
// mesmo o código interno do produto, não quando bate por EAN/bipe.
function produtoBateCodigoInterno(p, termo) {
  return String(p.codigo_interno) === termo;
}

export default function PDV() {
  const { usuario, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Lido uma vez, síncrono, antes de qualquer state — evita um "flash" de
  // carrinho vazio antes de restaurar. Os 4 useState logo abaixo (itens,
  // cliente, desconto, observação) consomem o mesmo objeto.
  const rascunhoInicial = useMemo(() => lerRascunhoPdv(usuario), []); // eslint-disable-line
  const [avisoRascunho, setAvisoRascunho] = useState(() => !!rascunhoInicial);

  // ── Itens do carrinho ─────────────────────────────────────────
  const [itens, setItens] = useState(() => rascunhoInicial?.itens || []);
  const [itemIdx, setItemIdx] = useState(null);
  // Espelha `itens` pra funções chamadas via setTimeout (ex.: comanda no F1
  // → finalizarComandaNoPDV → verificarEAbrirVenda 30ms depois) não fecharem
  // sobre um `itens` obsoleto — a closure captura o array de ANTES dos itens
  // da comanda entrarem no carrinho, o que faz "if (itens.length === 0)
  // return" abortar silenciosamente mesmo com item já adicionado.
  const itensRef = useRef(itens);
  useEffect(() => { itensRef.current = itens; }, [itens]);
  // Desconto trazido de uma comanda (F1) pra dentro do F9 — ver
  // finalizarComandaNoPDV/o efeito de abertura do modalSalvar logo abaixo.
  // Não pode ser só um setDescontoR direto: o efeito que abre o modalSalvar
  // reseta descontoR pra '' incondicionalmente (toda venda nova começa sem
  // desconto), e como finalizarComandaNoPDV roda ANTES desse reset (a
  // abertura do modal só acontece ~30ms depois, via verificarEAbrirVenda),
  // o desconto da comanda era apagado sem o operador perceber — sobretudo
  // com "ocultar desconto no F9" ligado, que nem mostra a etapa 1 pra
  // reparar o sumiço. Achado ao vivo (cliente com F1 mesa/comanda).
  const descontoComandaRef = useRef(null);
  // Mesma lógica do descontoComandaRef, pra taxa de entrega lançada na comanda.
  const taxaComandaRef = useRef(null);

  // ── Turno / Caixa ────────────────────────────────────────────
  const [turnoAtual, setTurnoAtual]           = useState(undefined); // undefined=carregando, null=fechado
  const [modalAbrirCaixa, setModalAbrirCaixa] = useState(false);
  const [suprimentoCaixa, setSuprimentoCaixa] = useState('0,00');
  const [contasAbertura, setContasAbertura]   = useState([]);
  const [contaAbertura, setContaAbertura]     = useState('');
  const [abrindoCaixa, setAbrindoCaixa]       = useState(false);
  const [erroAbertura, setErroAbertura]       = useState('');
  const [pendingVenda, setPendingVenda]       = useState(false);
  const [modalFecharCaixa, setModalFecharCaixa] = useState(false);
  const [saldoFechamento, setSaldoFechamento]   = useState('');
  const [fechandoCaixa, setFechendoCaixa]       = useState(false);
  const [erroFechamento, setErroFechamento]     = useState('');
  // Turno fechado mais recentemente NESTE computador — permite reimprimir o
  // fechamento pelo próprio PDV sem precisar ir na tela de Fechamento de
  // Caixa. Guardado no localStorage pra sobreviver a um F5 logo depois de
  // fechar o caixa.
  const [ultimoTurnoFechadoId, setUltimoTurnoFechadoId] = useState(
    () => localStorage.getItem('pdv_ultimo_turno_fechado_id') || null
  );

  // ── Ticker CAIXA LIVRE — aparece após 30s de inatividade sem itens ──
  const [caixaLivre, setCaixaLivre] = useState(false);
  const idleTimerRef = useRef(null);
  const iniciarIdleTimer = useCallback((temItens) => {
    clearTimeout(idleTimerRef.current);
    setCaixaLivre(false);
    if (!temItens) {
      idleTimerRef.current = setTimeout(() => setCaixaLivre(true), 30000);
    }
  }, []);
  useEffect(() => {
    const resetar = () => iniciarIdleTimer(itens.length > 0);
    window.addEventListener('mousemove', resetar);
    window.addEventListener('keydown',   resetar);
    window.addEventListener('click',     resetar);
    iniciarIdleTimer(itens.length > 0);
    return () => {
      window.removeEventListener('mousemove', resetar);
      window.removeEventListener('keydown',   resetar);
      window.removeEventListener('click',     resetar);
      clearTimeout(idleTimerRef.current);
    };
  }, [itens.length, iniciarIdleTimer]);

  // ── Campos de entrada ─────────────────────────────────────────
  const [busca, setBusca] = useState('');
  const [qtd, setQtd] = useState('1');
  const [precoUnit, setPrecoUnit] = useState('');
  const [unid, setUnid] = useState('');
  // Produto aguardando Qtd/Preço (parâmetros "pedir quantidade"/"pedir valor unitário")
  const [produtoPendente, setProdutoPendente] = useState(null);
  const [sugestoes, setSugestoes] = useState([]);
  const [idxSug, setIdxSug]       = useState(null);
  // Produto pesado_no_caixa: lê o peso do agente local (agente-balanca) em
  // vez de pedir quantidade digitada.
  const [lendoBalanca, setLendoBalanca] = useState(false);
  const [erroBalanca, setErroBalanca]   = useState('');
  // Liga/desliga a captura automática pela balança física — fica salvo no
  // navegador deste caixa (cada PC pode ter ou não balança conectada), não
  // no cadastro da organização. Desligada, o peso do produto pesado_no_caixa
  // é digitado manualmente em vez de lido do agente local.
  const [balancaAtiva, setBalancaAtiva] = useState(() => localStorage.getItem('pdv_balanca_ativa') !== 'false');
  const toggleBalanca = useCallback(() => {
    setBalancaAtiva(v => {
      const novo = !v;
      localStorage.setItem('pdv_balanca_ativa', String(novo));
      return novo;
    });
  }, []);

  // ── PDV offline (Fase 1) ────────────────────────────────────────
  // "online" aqui já confirma que a API está de pé, não só o link de rede
  // (ver hooks/useOnlineStatus.js). useSyncQueue cuida sozinho de reenviar
  // a fila local assim que a conexão volta.
  const online = useOnlineStatus();
  const { pendentesCount, sincronizarAgora } = useSyncQueue();
  const [avisoOffline, setAvisoOffline] = useState('');
  const [modalPendencias, setModalPendencias] = useState(false);
  useEffect(() => {
    if (!avisoOffline) return;
    const t = setTimeout(() => setAvisoOffline(''), 6000);
    return () => clearTimeout(t);
  }, [avisoOffline]);
  useEffect(() => {
    if (!avisoRascunho) return;
    const t = setTimeout(() => setAvisoRascunho(false), 10000);
    return () => clearTimeout(t);
  }, [avisoRascunho]);

  // ── Cliente ───────────────────────────────────────────────────
  const [cliente, setCliente] = useState(() => rascunhoInicial?.cliente || null);
  const clientePadraoRef = useRef(null); // cliente 1 = VENDA CONSUMIDOR (padrão de toda venda)
  // Fidelidade: saldo/crédito disponível do cliente selecionado — busca de
  // novo toda vez que o cliente muda, pra decidir se "Pontos Fidelidade"
  // aparece como forma de pagamento (só quando tem crédito disponível).
  const [fidelidadeCliente, setFidelidadeCliente] = useState(null);
  useEffect(() => {
    if (!cliente?.id || cliente.id === 1) { setFidelidadeCliente(null); return; }
    let ativo = true;
    api.get(`/clientes/${cliente.id}/fidelidade`)
      .then(({ data }) => { if (ativo) setFidelidadeCliente(data); })
      .catch(() => { if (ativo) setFidelidadeCliente(null); });
    return () => { ativo = false; };
  }, [cliente?.id]);
  // Crédito do cliente (gerado por devolução) disponível pra pagar a venda —
  // mesmo princípio da fidelidade acima.
  const [creditoCliente, setCreditoCliente] = useState(null);
  useEffect(() => {
    if (!cliente?.id || cliente.id === 1) { setCreditoCliente(null); return; }
    let ativo = true;
    api.get('/financeiro/creditos-cliente', { params: { cliente_id: cliente.id } })
      .then(({ data }) => { if (ativo) setCreditoCliente(data); })
      .catch(() => { if (ativo) setCreditoCliente(null); });
    return () => { ativo = false; };
  }, [cliente?.id]);
  const [modalCliente, setModalCliente] = useState(false);
  /* null | { clienteId, faltando, retry } — cadastro incompleto p/ NF-e */
  const [modalCompletarCli, setModalCompletarCli] = useState(null);
  /* null | { vendaId, retry } — venda no VENDA CONSUMIDOR precisa de cliente p/ NF-e */
  const [modalSelecionarCli, setModalSelecionarCli] = useState(null);
  /* Correção fiscal genérica: dados exibidos no modal + retry após salvar */
  const [correcaoDados, setCorrecaoDados] = useState(null);
  const correcaoRetryRef = useRef(null);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clientes, setClientes] = useState([]);
  const [idxCli, setIdxCli] = useState(null);
  const [buscandoCli, setBuscandoCli] = useState(false);
  const [tabelaPrecoCliente, setTabelaPrecoCliente] = useState(null);
  const [subModalNovoCli, setSubModalNovoCli] = useState(false);
  const [novoCliNome, setNovoCliNome]     = useState('');
  const [novoCliCpf, setNovoCliCpf]       = useState('');
  const [novoCliFone, setNovoCliFone]     = useState('');
  const [salvandiNovoCli, setSalvandoNovoCli] = useState(false);
  const [erroNovoCli, setErroNovoCli]     = useState('');

  // ── Modal F3 — Busca avançada de produtos ────────────────────
  const {
    modalF3, setModalF3, filtrosF3, setFiltrosF3, setFiltro,
    produtosF3, setProdutosF3, buscandoF3, totalF3, idxF3, setIdxF3,
    buscaRapidaF3, setBuscaRapidaF3, buscaRapidaRef,
    fecharF3, buscarF3, handleBuscaRapidaF3,
  } = useBuscaProdutosF3({
    aoFechar: () => buscaRef.current?.focus(),
    aoFalhaSemRede: async (termo) => {
      // Sem internet: cai pro catálogo em cache (offlineDb.js), filtrado no
      // navegador pelo mesmo termo — nunca deixa o F3 vazio só porque a
      // rede caiu no meio de uma venda.
      const cache = await getProdutosCache();
      const termoLower = termo.trim().toLowerCase();
      return termoLower
        ? cache.filter(p =>
            p.nome?.toLowerCase().includes(termoLower) ||
            String(p.codigo_interno || '').includes(termoLower) ||
            (p.codigo_barras || '').includes(termoLower) ||
            (Array.isArray(p.codigos_barras) && p.codigos_barras.some(cb => (cb.codigo || '').includes(termoLower))))
        : cache;
    },
  });

  const buscaRef    = useRef(null);
  const qtdRef      = useRef(null);
  const precoRef    = useRef(null);
  const buscaCliRef = useRef(null);
  const gridRef     = useRef(null);
  const debounceRef = useRef(null);

  // ── Menu Opções (Ctrl+F12) ────────────────────────────────────
  const [menuOpcoes, setMenuOpcoes] = useState(false);

  // ── Modal Salvar/Finalizar (F9 / Ctrl+S) ─────────────────────
  const [modalSalvar, setModalSalvar]         = useState(false);
  const [modalVendedor, setModalVendedor]     = useState(false);
  const [vendedoresPdv, setVendedoresPdv]     = useState([]);
  const confirmarBtnRef = useRef(null);
  const [etapaSalvar, setEtapaSalvar]         = useState(1);   // 1=desconto, 2=pagamento
  const [abrirDescontoForcado, setAbrirDescontoForcado] = useState(false); // F6 força etapa 1 mesmo com ocultar_desconto_f9
  const [descontoR, setDescontoR]             = useState(() => rascunhoInicial?.descontoR || '');  // desconto em R$
  // Taxa de entrega (Parâmetros → PDV → "Habilitar taxa de entrega") — soma
  // no total da venda, campo próprio (vendas.taxa_entrega), não é item.
  const [taxaEntregaR, setTaxaEntregaR]       = useState('');
  const [descontoP, setDescontoP]             = useState('');  // desconto em %
  const [formasPgto, setFormasPgto]           = useState([]);
  const formasPgtoRef = useRef([]);
  formasPgtoRef.current = formasPgto;
  // Cobrança em andamento na maquininha Mercado Pago Point (ModalCobrancaPoint)
  const [cobrancaPoint, setCobrancaPoint]     = useState(null);
  const cobrancaPointRef = useRef(null);
  cobrancaPointRef.current = cobrancaPoint;
  const [valorPgtoAtual, setValorPgtoAtual]   = useState('');
  const [formaPgtoSel, setFormaPgtoSel]       = useState('Dinheiro');
  const [subCartao, setSubCartao]             = useState(null); // null | 'choose' | 'credito'
  const [parcelasCartao, setParcelasCartao]   = useState(1);
  const [bandeiraSel, setBandeiraSel]         = useState(null); // { id, nome, tipo }
  const [bandeiras, setBandeiras]             = useState([]);
  // Código de autorização do cartão (cAut) — opcional, digitado do
  // comprovante da maquininha; enriquece o grupo <card> da NF-e/NFC-e.
  const [codigoAutorizacaoCartao, setCodigoAutorizacaoCartao] = useState('');

  // Bandeiras filtradas por tipo (cadastro define débito / crédito / ambos)
  const bandeirasDebito  = bandeiras.filter(b => (b.tipo || 'ambos') !== 'credito');
  const bandeirasCredito = bandeiras.filter(b => (b.tipo || 'ambos') !== 'debito');

  // Limpa bandeira selecionada se incompatível com o tipo de cartão escolhido
  useEffect(() => {
    if (!bandeiraSel) return;
    const t = bandeiraSel.tipo || 'ambos';
    if ((subCartao === 'debito' && t === 'credito') ||
        (subCartao === 'credito' && t === 'debito')) setBandeiraSel(null);
  }, [subCartao, bandeiraSel]);
  // PIX
  const [pixContaId, setPixContaId]           = useState('');
  const [boletoContaId, setBoletoContaId]     = useState('');
  // Crediário
  const [parcelasCrediario, setParcelasCrediario] = useState(1);
  const [parcelasBoleto, setParcelasBoleto]   = useState(1);
  const [salvando, setSalvando]               = useState(false);
  const [erroSalvar, setErroSalvar]           = useState('');
  const [vendaSalva, setVendaSalva]           = useState(null);
  const [itensServicoVenda, setItensServicoVenda] = useState([]); // itens_venda de serviço, p/ emitir NFS-e (1 DPS por item)
  const [nfseEmitindo, setNfseEmitindo]       = useState(null); // item_venda_id em andamento
  const [nfseResultados, setNfseResultados]   = useState({});   // item_venda_id -> resultado da emissão (preview)
  const [modalFiscal, setModalFiscal]         = useState(false);
  const [larguraCupom, setLarguraCupom]       = useState('80mm');
  const [emitindoFiscal, setEmitindoFiscal]   = useState(false);
  const [resultadoFiscal, setResultadoFiscal] = useState(null); // { sucesso, motivo, chave_acesso, protocolo }
  const salvarInputRef  = useRef(null);
  // Foca o valor do pagamento JÁ selecionado: se o campo já tinha o foco
  // (ex.: D adicionou dinheiro e logo depois P), focus() não dispara o
  // onFocus/select e o valor digitado se juntava ao restante preenchido.
  const focarValorPgto = () => { const el = salvarInputRef.current; if (el) { el.focus(); el.select(); } };
  const descontoRRef    = useRef(null);
  const descontoPRef    = useRef(null);
  const salvarLiveRef   = useRef({});

  // ── Modal Buscar Movimentação (F7 / Ctrl+F) ──────────────────
  const [modalBuscarMov, setModalBuscarMov]   = useState(false);
  const FILTROS_MOV_VAZIOS = {
    dataInicio: '', dataFim: '', operador: '', terminal: '',
    excluidas: false, cliente: '', finalizacao: '', // finalizacao = filtro de Doc. Fiscal (nfce|nfe|nfe_nfse|'')
    codigo: '', nfe: '', total: '', operacao: '',    // operacao = tipo_movimento_fiscal_id
  };
  const [filtrosMov, setFiltrosMov]           = useState(FILTROS_MOV_VAZIOS);
  const [movimentacoes, setMovimentacoes]     = useState([]);
  const [buscandoMov, setBuscandoMov]         = useState(false);
  const [tiposMovimentoMov, setTiposMovimentoMov] = useState([]); // TMFs de saída p/ o filtro "Tipo de Movimento"
  const [movSelecionada, setMovSelecionada]   = useState(null);
  const [imprimindoMov, setImprimindoMov]     = useState(false);
  const [emitindoDocMov, setEmitindoDocMov]   = useState(false);
  const [resultadoDocMov, setResultadoDocMov] = useState(null); // { sucesso, motivo, tipo, detalhes_fiscais? }
  const [modalDiagnostico, setModalDiagnostico] = useState(false);
  const [dadosDiagnostico, setDadosDiagnostico] = useState(null);
  const [itensServicoMov, setItensServicoMov] = useState([]); // itens de serviço da movimentação selecionada no F7
  const [nfseEmitindoMov, setNfseEmitindoMov] = useState(null);
  const [nfseResultadosMov, setNfseResultadosMov] = useState({});
  const [modalCorrecao, setModalCorrecao]     = useState(false);
  const [correcaoTipo, setCorrecaoTipo]       = useState(null);  // 'nfce' | 'nfe'
  const [correcoes, setCorrecoes]             = useState({});    // { [produto_id]: { ncm, cfop_saida, csosn } }
  const [salvandoCorrecao, setSalvandoCorrecao] = useState(false);

  // ── Modal Totais em Caixa ─────────────────────────────────────
  const [modalTotais, setModalTotais]         = useState(false);
  const [totaisCaixa, setTotaisCaixa]         = useState(null);
  const [carregandoTotais, setCarregandoTotais] = useState(false);

  // ── Modal Pré-venda (F2) ──────────────────────────────────────
  const [modalPreVenda, setModalPreVenda]     = useState(false);
  const [pedidosAprov, setPedidosAprov]       = useState([]);
  const [devolucoesPend, setDevolucoesPend]   = useState([]);
  const [buscandoPrev, setBuscandoPrev]       = useState(false);
  const [idxPrev, setIdxPrev]                 = useState(null);
  const [pedidoOrigemId, setPedidoOrigemId]   = useState(null);
  const [pedidoTipoMovId, setPedidoTipoMovId] = useState(null);
  const [tipoMovFiscal, setTipoMovFiscal]     = useState(null);

  // ── F1 — Mesas/Comandas (Parâmetros → PDV → Controle de Mesas e Comandas) ──
  const [modalMesas, setModalMesas]           = useState(false);
  const [mesaAtiva, setMesaAtiva]             = useState(null); // {comandaId, mesaId, numero, descricao} | null
  const [modalPesados, setModalPesados]       = useState(false);
  // Venda importada da mesa não aceita item novo — mesma trava do Del.
  const abrirPesados = () => { if (!mesaAtiva) setModalPesados(true); };
  // Aba que o F1 abre da PRÓXIMA vez (ver tentarReabrirMesasComandas) — só
  // usada pelo reabrir automático; F1 manual sempre volta pra 'mesa'
  // (abrirModalMesas reseta antes de abrir).
  const [abaMesasComandas, setAbaMesasComandas] = useState('mesa');
  // Lembra se a venda que ACABOU de fechar era mesa ou comanda avulsa — só
  // setado no sucesso real do POST /vendas (ver confirmarPagamento), lido e
  // limpo em tentarReabrirMesasComandas quando o PDV volta pro carrinho
  // vazio (depois do modal fiscal, com ou sem documento emitido).
  const [ultimaVendaOrigem, setUltimaVendaOrigem] = useState(null); // 'mesa' | 'individual' | null

  // ── Modal Observação ──────────────────────────────────────────
  const [modalObs, setModalObs]               = useState(false);
  const [obsTexto, setObsTexto]               = useState(() => rascunhoInicial?.obsTexto || '');

  // Salva o rascunho do carrinho a cada mudança — só venda avulsa (mesa/
  // comanda e pedido importado têm dono no servidor, não faz sentido
  // re-hidratar um estado local velho por cima deles). Some sozinho do
  // localStorage assim que o carrinho esvazia (venda finalizada, "Limpar
  // Itens", ou virou uma venda de mesa/pedido).
  useEffect(() => {
    if (!usuario?.organizacao_id || !usuario?.id) return;
    const chave = chaveRascunhoPdv(usuario);
    if (mesaAtiva || pedidoOrigemId || itens.length === 0) {
      localStorage.removeItem(chave);
      return;
    }
    localStorage.setItem(chave, JSON.stringify({ itens, cliente, descontoR, obsTexto, salvoEm: Date.now() }));
  }, [itens, cliente, descontoR, obsTexto, mesaAtiva, pedidoOrigemId, usuario]);

  // ── Dados da organização (para DANFE / cupom) ─────────────────
  const [orgData, setOrgData]                 = useState(null);

  // Layout do PDV SEM "flash": o pdv_layout vem do servidor (assíncrono) e o
  // fallback ?? 1 desenhava o layout antigo por ~0,5s antes de trocar. Cache
  // local por organização resolve as próximas aberturas; na primeira (sem
  // cache) uma tela neutra segura até a resposta chegar.
  const [layoutPdv, setLayoutPdv] = useState(() => {
    const cache = localStorage.getItem(`pdv_layout_${usuario?.organizacao_id}`);
    return cache ? parseInt(cache) : null; // null = ainda não sabemos
  });

  // Layout 3 e Layout 4: marcam o tema claro no <body> — os modais
  // (F2/F3/F7/F10) são compartilhados entre os layouts e o PDV.module.css
  // re-estiliza pelo atributo body[data-pdv-tema='claro'] p/ seguir as
  // cores do layout novo. Layout 4 reaproveita a MESMA marcação JSX do
  // Layout 2 (não existe componente próprio) — é o mesmo atributo que
  // reestiliza a sidebar/área direita do Layout 2 pras cores leves dele
  // (ver PDV.module.css, seção "LAYOUT 4").
  useEffect(() => {
    if (layoutPdv === 3 || layoutPdv === 4) document.body.dataset.pdvTema = 'claro';
    else delete document.body.dataset.pdvTema;
    return () => { delete document.body.dataset.pdvTema; };
  }, [layoutPdv]);

  const totalVenda = itens.reduce((acc, i) => acc + i.total, 0);
  const qtdItens   = itens.reduce((acc, i) => acc + parsePT(i.qtd), 0);
  const totalCampo = fmtBRL(parsePT(qtd) * parsePT(precoUnit));

  /* ── Agrupamento Produtos x Serviços (parâmetro organizacoes.habilitar_servicos) ── */
  const temServicoPdv       = itens.some(i => ehServico(i.tipo));
  const agruparPdv          = !!orgData?.habilitar_servicos && temServicoPdv;
  // Rollout controlado (31/08/2026): QZ Tray só pra quem já tem o agente
  // instalado no PC do caixa — liga por pdv_config.gaveta_qz_tray, loja a
  // loja, até validar em produção antes de virar padrão pra todo mundo.
  const gavetaHabilitada    = !!orgData?.pdv_config?.gaveta_qz_tray;
  const subtotalProdutosPdv = itens.filter(i => !ehServico(i.tipo)).reduce((acc, i) => acc + i.total, 0);
  const subtotalServicosPdv = itens.filter(i => ehServico(i.tipo)).reduce((acc, i) => acc + i.total, 0);

  // ── Carrega dados da organização, bandeiras, contas e turno atual ──
  // PDV offline (Fase 1): cada resposta é guardada em cache (IndexedDB) e,
  // se a chamada falhar (sem internet, inclusive na 1ª abertura do dia),
  // cai pro último valor cacheado em vez de deixar a tela sem nada.
  useEffect(() => {
    if (usuario?.organizacao_id) {
      // Cliente padrão 1 = VENDA CONSUMIDOR: só guarda pronto pro primeiro
      // item da venda (ver adicionarItem) — não aplica direto aqui, senão
      // toda venda "nasce" com cliente preenchido mesmo sem nenhum item
      // ainda, dificultando notar se algo de uma venda anterior vazou.
      api.get('/clientes/1')
        .then(({ data }) => { clientePadraoRef.current = data; cacheCliente(data); })
        .catch(async () => {
          const cache = (await getClientesCache()).find(c => c.id === 1);
          if (cache) clientePadraoRef.current = cache;
        });
      api.get(`/organizacoes/${usuario.organizacao_id}`)
        .then(({ data }) => {
          setOrgData(data);
          cacheConfig('orgData', data);
          const lay = data.pdv_layout ?? 1;
          setLayoutPdv(lay);
          localStorage.setItem(`pdv_layout_${usuario.organizacao_id}`, String(lay));
        })
        .catch(async () => {
          const cache = await getConfig('orgData');
          if (cache) {
            setOrgData(cache);
            setLayoutPdv(cache.pdv_layout ?? 1);
          } else {
            setLayoutPdv(prev => prev ?? 1); // sem resposta e sem cache: não trava a tela
          }
        });
      api.get('/financeiro/bandeiras')
        .then(({ data }) => { const b = data.filter(x => x.ativo); setBandeiras(b); cacheConfig('bandeiras', b); })
        .catch(async () => setBandeiras((await getConfig('bandeiras')) || []));
      api.get('/financeiro/contas')
        .then(({ data }) => {
          const caixas = data.filter(c => c.ativo);
          setContasAbertura(caixas);
          cacheConfig('contas', caixas);
          // Caixa padrão cadastrado pro usuário (Cadastro de Usuários) tem
          // prioridade — cai no primeiro da lista só se não tiver um definido.
          const padrao = usuario?.conta_caixa_padrao_id && caixas.some(c => c.id === usuario.conta_caixa_padrao_id)
            ? usuario.conta_caixa_padrao_id
            : caixas[0]?.id;
          if (padrao) setContaAbertura(String(padrao));
        })
        .catch(async () => {
          setContasAbertura((await getConfig('contas')) || []);
        });
      api.get('/financeiro/turnos/aberto')
        .then(({ data }) => setTurnoAtual(data))   // null se não houver
        .catch(() => setTurnoAtual(null));
    }
  }, [usuario?.organizacao_id]);

  // ── Catálogo de produtos em cache (offline) ─────────────────────
  // Atualiza ao abrir o PDV e a cada 5 min enquanto online — é o que
  // alimenta a busca de produtos quando a internet cai (ver F3/busca).
  useEffect(() => {
    if (!online) return;
    let ativo = true;
    const atualizarCatalogo = () => {
      api.get('/produtos', { params: { todos: 'true' } })
        .then(({ data }) => { if (ativo) cacheProdutos(data || []); })
        .catch(() => {});
    };
    atualizarCatalogo();
    const t = setInterval(atualizarCatalogo, 5 * 60 * 1000);
    return () => { ativo = false; clearInterval(t); };
  }, [online]);

  // ── Carrega detalhes do tipo de movimento fiscal ao importar pedido ──
  useEffect(() => {
    if (!pedidoTipoMovId) { setTipoMovFiscal(null); return; }
    api.get(`/fiscal/tipos-movimento/${pedidoTipoMovId}`)
      .then(({ data }) => setTipoMovFiscal(data))
      .catch(() => setTipoMovFiscal(null));
  }, [pedidoTipoMovId]);

  // ── Foco inicial no campo busca ──────────────────────────────
  useEffect(() => { buscaRef.current?.focus(); }, []);

  // ── Foco no modal de cliente ─────────────────────────────────
  useEffect(() => {
    if (modalCliente) setTimeout(() => buscaCliRef.current?.focus(), 50);
  }, [modalCliente]);

  // ── Ao abrir modal F7: carrega tipos de movimento e busca o dia de hoje ──
  useEffect(() => {
    setMovSelecionada(null);
    setResultadoDocMov(null);
    if (!modalBuscarMov) return;

    // Carrega tipos de movimento (saída) para o filtro "Tipo de Movimento"
    api.get('/fiscal/tipos-movimento', { params: { tipo: 'S' } })
      .then(({ data }) => setTiposMovimentoMov(Array.isArray(data) ? data : []))
      .catch(() => setTiposMovimentoMov([]));

    const hoje = hojeBR();
    const filtros = { ...FILTROS_MOV_VAZIOS, dataInicio: hoje, dataFim: hoje };
    setFiltrosMov(filtros);
    buscarMovimentacoes(filtros);
  }, [modalBuscarMov]); // eslint-disable-line

  // ── Busca totais em caixa ao abrir o modal ───────────────────
  useEffect(() => {
    if (!modalTotais) return;
    setCarregandoTotais(true);
    const params = turnoAtual ? { turno_id: turnoAtual.id } : {};
    api.get('/vendas/totais-caixa', { params })
      .then(({ data }) => setTotaisCaixa(data))
      .catch(() => setTotaisCaixa(null))
      .finally(() => setCarregandoTotais(false));
  }, [modalTotais]);

  // ── Atalhos globais ──────────────────────────────────────────
  useEffect(() => {
    const FORMAS_KEY = { b: 'Boleto', c: 'Cartão', d: 'Dinheiro', h: 'Cheque', n: 'Crediário', p: 'PIX' };

    const handler = e => {
      // Menu opções
      if (menuOpcoes) {
        if (e.key === 'Escape') { setMenuOpcoes(false); return; }
        const acao = { '1': () => { setMenuOpcoes(false); setModalPreVenda(true); setBuscandoPrev(true); },
                       '2': () => { setMenuOpcoes(false); setModalTotais(true); },
                       '6': () => { setMenuOpcoes(false); setModalBuscarMov(true); },
                       '9': () => { setMenuOpcoes(false); verificarEAbrirVenda(); } };
        if (acao[e.key]) { e.preventDefault(); acao[e.key](); return; }
        return;
      }

      // Modal F3 busca avançada
      if (modalF3) {
        if (e.key === 'Escape') { fecharF3(); return; }
        if (e.key === 'Enter' && idxF3 !== null) { e.preventDefault(); confirmarF3(produtosF3[idxF3]); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.min(i + 1, produtosF3.length - 1)); return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.max(i - 1, 0)); return; }
        return;
      }

      // Modal Salvar
      if (modalSalvar) {
        // Modal de cliente (F10) aberto por cima: Escape fecha só ele;
        // as demais teclas vão para o campo de busca de cliente
        if (modalCliente) {
          if (e.key === 'Escape') { setModalCliente(false); return; }
          return;
        }
        if (cobrancaPointRef.current) return; // tela da maquininha trata as próprias teclas
        if (e.key === 'Escape') {
          if (subCartao) { setSubCartao(null); return; }
          if (formasPgtoRef.current.some(p => p.mp_point_cobranca_id) &&
              !window.confirm('Esta venda tem pagamento JÁ COBRADO na maquininha. Se sair sem finalizar, o valor fica para estornar em Mercado Pago → Cobranças da maquininha.\n\nSair mesmo assim?')) return;
          setModalSalvar(false); buscaRef.current?.focus(); return;
        }
        if (e.key === 'F10') { e.preventDefault(); setModalCliente(true); return; }

        // Etapa 1 — desconto
        if (etapaSalvar === 1) {
          const tag = document.activeElement?.tagName;
          // Enter fora de input → avança com valor sempre fresco via ref
          if (e.key === 'Enter' && tag !== 'INPUT') {
            e.preventDefault();
            salvarLiveRef.current.avancarParaPgto?.();
            return;
          }
          if (!e.ctrlKey && !e.altKey && tag !== 'INPUT') {
            if (e.key.toLowerCase() === 's') { e.preventDefault(); descontoRRef.current?.focus(); return; }
          }
          return;
        }

        return; // etapa 2: tratado pelo handler de capture no document
      }

      // Modal Buscar Movimentação
      if (modalBuscarMov) {
        if (e.key === 'Escape') { setModalBuscarMov(false); buscaRef.current?.focus(); return; }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (!movimentacoes.length) return;
          const idxAtual = movSelecionada ? movimentacoes.findIndex(m => m.id === movSelecionada.id) : -1;
          const novo = idxAtual < 0 ? 0
            : e.key === 'ArrowDown' ? Math.min(idxAtual + 1, movimentacoes.length - 1)
            : Math.max(idxAtual - 1, 0);
          setMovSelecionada(movimentacoes[novo]);
          setResultadoDocMov(null);
          return;
        }
        return;
      }

      // Modal Totais
      if (modalTotais) {
        if (e.key === 'Escape') { setModalTotais(false); return; }
        return;
      }

      // Modal Pré-venda
      if (modalPreVenda) {
        if (e.key === 'Escape') { setModalPreVenda(false); return; }
        if (e.key === 'Enter' && idxPrev !== null) { e.preventDefault(); importarPreVenda(pedidosAprov[idxPrev]); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setIdxPrev(i => i === null ? 0 : Math.min(i + 1, pedidosAprov.length - 1)); return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setIdxPrev(i => i === null ? 0 : Math.max(i - 1, 0)); return; }
        return;
      }

      // Modal Observação
      if (modalObs) {
        if (e.key === 'Escape') { setModalObs(false); return; }
        return;
      }

      // Modal Cliente
      if (modalCliente) {
        if (e.key === 'Escape') { setModalCliente(false); buscaRef.current?.focus(); return; }
        if (!subModalNovoCli) {
          if (e.key === 'ArrowDown') { e.preventDefault(); setIdxCli(i => i === null ? 0 : Math.min(i + 1, clientes.length - 1)); return; }
          if (e.key === 'ArrowUp')   { e.preventDefault(); setIdxCli(i => i === null ? 0 : Math.max(i - 1, 0)); return; }
          if (e.key === 'Enter' && idxCli !== null && clientes[idxCli]) { e.preventDefault(); selecionarCliente(clientes[idxCli]); return; }
        }
        return;
      }

      // Modal Fiscal — a venda já foi salva quando esta tela aparece (só
      // falta escolher o documento). Esc não pode deixar o carrinho como
      // se a venda ainda estivesse em aberto, senão um Finalizar seguinte
      // grava uma segunda venda duplicada.
      if (modalFiscal) {
        if (e.key === 'Escape') { fecharFiscalELimpar(); return; }
        return;
      }

      // Modal Mesas/Comandas — fullscreen e autocontido, com seu próprio
      // handler de teclado (F3/F4/F6/F9/Delete/Esc); aqui só evita que o
      // handler principal do PDV também reaja às mesmas teclas.
      if (modalMesas) return;
      // Pesados (F4): tela cheia com o próprio tratamento de Esc/Enter.
      if (modalPesados) return;

      // Sem modal aberto
      if (e.key === 'F4' && orgData?.pdv_config?.botao_pesados) { e.preventDefault(); abrirPesados(); return; }
      if (e.ctrlKey && e.key === 'F12') { e.preventDefault(); setMenuOpcoes(true); return; }
      if (e.key === 'F1' && orgData?.pdv_config?.controle_mesas_comandas) { e.preventDefault(); abrirModalMesas(); return; }
      if (e.key === 'F2')  { e.preventDefault(); setModalPreVenda(true); setBuscandoPrev(true); return; }
      if (e.key === 'F9' || (e.ctrlKey && e.key === 's'))  { e.preventDefault(); verificarEAbrirVenda(); return; }
      // forcarDesconto=true — sem isso, F6 nunca fazia diferença nenhuma
      // (chamava exatamente o mesmo que F9, sem forçar a etapa 1 quando
      // "ocultar desconto no F9" está ligado — achado ao vivo revisando o
      // fluxo de comanda/F9).
      if (e.key === 'F6')  { e.preventDefault(); verificarEAbrirVenda(true); return; } // Desconto global (etapa 1 do F9)
      if (e.key === 'F7' || (e.ctrlKey && e.key === 'f'))  { e.preventDefault(); setModalBuscarMov(true); return; }
      if (e.ctrlKey && e.key === 'r') { e.preventDefault(); return; }
      if (e.ctrlKey && e.key === 'd') { e.preventDefault(); return; }
      if (e.ctrlKey && e.key === 'p') { e.preventDefault(); return; }
      if (e.ctrlKey && e.key === 'g') { e.preventDefault(); if (gavetaHabilitada) acionarGaveta(); return; }
      if (e.key === 'F3')  { e.preventDefault(); abrirF3(); return; }
      if (e.key === 'F10') { e.preventDefault(); setModalCliente(true); return; }
      if (e.key === 'Delete' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        if (itemIdx !== null) removerItemPorIdx(itemIdx);
      }
      if (e.key === 'Escape') { sairDoPdvRef.current(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [menuOpcoes, modalCliente, modalF3, modalSalvar, modalBuscarMov,
      modalTotais, modalPreVenda, modalObs, modalFiscal, modalMesas, modalPesados, mesaAtiva,
      itemIdx, idxF3, produtosF3, pedidosAprov,
      etapaSalvar, subCartao, formaPgtoSel, orgData,
      subModalNovoCli, clientes, idxCli, movimentacoes, movSelecionada]);

  // Ctrl+G (manual) e chamado sozinho ao confirmar venda em Dinheiro — ver
  // utils/gaveta.js: requer QZ Tray instalado/aberto no PC do caixa e a
  // impressora configurada 1 vez em ⚙️ Configurar Gaveta. Silencioso quando
  // chamado automaticamente após a venda (silencioso=true): sem QZ
  // Tray/impressora configurada não é motivo pra interromper o fluxo do
  // caixa com um alerta a cada venda em dinheiro.
  const acionarGaveta = async (silencioso = false) => {
    try {
      await abrirGaveta();
    } catch (err) {
      if (!silencioso) alert(err.message || 'Não foi possível abrir a gaveta.');
    }
  };

  // Escolhe (uma vez) qual impressora instalada no Windows tem a gaveta
  // ligada nela — a lista vem do QZ Tray, que precisa estar instalado e
  // aberto no computador do caixa. Fica no Menu de Opções (Ctrl+F12).
  const configurarGaveta = async () => {
    try {
      const impressoras = await listarImpressoras();
      if (!impressoras || impressoras.length === 0) {
        alert('Nenhuma impressora encontrada no Windows — instale a impressora térmica normalmente antes de configurar a gaveta.');
        return;
      }
      const atual = impressoraGavetaSalva();
      const lista = impressoras.map((nome, i) => `${i + 1}) ${nome}`).join('\n');
      const padrao = atual && impressoras.includes(atual) ? String(impressoras.indexOf(atual) + 1) : '1';
      const escolha = window.prompt(`Qual impressora tem a gaveta ligada?\n\n${lista}\n\nDigite o número:`, padrao);
      if (escolha === null) return; // cancelou
      const idx = parseInt(escolha, 10) - 1;
      if (Number.isNaN(idx) || idx < 0 || idx >= impressoras.length) {
        alert('Número inválido.');
        return;
      }
      salvarImpressoraGaveta(impressoras[idx]);
      alert(`Gaveta configurada na impressora "${impressoras[idx]}" — Ctrl+G já deve abrir a gaveta.`);
    } catch (err) {
      alert(err.message || 'Erro ao configurar a gaveta.');
    }
  };

  // Escolhe (uma vez) qual impressora recebe cupom/DANFE quando a
  // Impressão Direta está ligada — mesmo mecanismo/tela da gaveta, só que
  // pra impressão em si. Sem escolher nada aqui, cai na mesma impressora
  // já configurada pra gaveta (caso mais comum: uma térmica só faz tudo).
  const configurarImpressao = async () => {
    try {
      const impressoras = await listarImpressoras();
      if (!impressoras || impressoras.length === 0) {
        alert('Nenhuma impressora encontrada no Windows — instale a impressora normalmente antes de configurar.');
        return;
      }
      const atual = impressoraImpressaoSalva();
      const lista = impressoras.map((nome, i) => `${i + 1}) ${nome}`).join('\n');
      const padrao = atual && impressoras.includes(atual) ? String(impressoras.indexOf(atual) + 1) : '1';
      const escolha = window.prompt(`Qual impressora recebe a Impressão Direta (cupom/DANFE)?\n\n${lista}\n\nDigite o número:`, padrao);
      if (escolha === null) return; // cancelou
      const idx = parseInt(escolha, 10) - 1;
      if (Number.isNaN(idx) || idx < 0 || idx >= impressoras.length) {
        alert('Número inválido.');
        return;
      }
      salvarImpressoraImpressao(impressoras[idx]);
      alert(`Impressão Direta configurada na impressora "${impressoras[idx]}".`);
    } catch (err) {
      alert(err.message || 'Erro ao configurar a impressora de impressão direta.');
    }
  };

  // Liga/desliga a Impressão Direta neste computador (Ctrl+F12 → Opções).
  // Desligada é o padrão — ninguém precisa mexer em nada pra continuar
  // vendo o diálogo "Imprimir" de sempre.
  const alternarImpressaoDireta = () => {
    const ligar = !impressaoDiretaAtiva();
    if (ligar && !impressoraImpressaoSalva()) {
      alert('Configure primeiro a impressora em "⚙️ Configurar Impressão" antes de ligar a Impressão Direta.');
      return;
    }
    salvarImpressaoDireta(ligar);
    alert(ligar
      ? `Impressão Direta LIGADA neste computador — cupom/DANFE vão direto pra "${impressoraImpressaoSalva()}", sem diálogo.`
      : 'Impressão Direta DESLIGADA — volta a mostrar o diálogo "Imprimir" de sempre.');
  };

  // Mesmo campo "Margem impressora (mm)" que já existia dentro do modal de
  // Documento Fiscal (só aparecia depois de finalizar uma venda) — trazido
  // pro F12 também, pra dar pra ajustar sem precisar abrir uma venda antes.
  // Por computador (localStorage), igual gaveta/Impressão Direta.
  const configurarMargemImpressao = () => {
    const atual = getMargemImpressaoMM();
    const valor = window.prompt(
      'Margem de segurança da impressora térmica (mm) — aumenta se o cupom estiver cortando as laterais.\n\nDesconta dos dois lados da largura (58mm/80mm). Vale só neste computador.',
      String(atual)
    );
    if (valor === null) return; // cancelou
    const aplicada = setMargemImpressaoMM(valor);
    alert(`Margem da impressora térmica ajustada pra ${aplicada}mm neste computador.`);
  };

  // ── Modal F3 — funções ───────────────────────────────────────
  const abrirF3 = () => {
    setModalF3(true);
    setIdxF3(null);
    setBuscaRapidaF3('');
    setProdutosF3([]);
    setTimeout(() => buscaRapidaRef.current?.focus(), 60);
  };

  // Handoff do campo de busca principal quando o que foi digitado é texto
  // (não código/barcode): abre o F3 já com a lista montada pro termo digitado,
  // e o cursor segue pro campo de busca rápida do F3 pra continuar digitando.
  const abrirF3ComTexto = (texto) => {
    setModalF3(true);
    setFiltrosF3(FILTROS_VAZIOS);
    setIdxF3(null);
    setBuscaRapidaF3(texto);
    setProdutosF3([]);
    buscarF3(FILTROS_VAZIOS, texto);
    setTimeout(() => {
      const el = buscaRapidaRef.current;
      el?.focus();
      el?.setSelectionRange?.(el.value.length, el.value.length);
    }, 60);
  };

  // ── Desconto bidirecional ─────────────────────────────────────
  const handleDescontoR = e => {
    const v = e.target.value;
    setDescontoR(v);
    const n = parsePT(v);
    setDescontoP(totalVenda > 0 && n > 0 ? (n / totalVenda * 100).toFixed(2) : '');
  };

  const handleDescontoP = e => {
    const v = e.target.value;
    setDescontoP(v);
    const n = parsePT(v);
    setDescontoR(n > 0 ? (totalVenda * n / 100).toFixed(2) : '');
  };

  // Taxa de entrega na etapa de pagamento: só editável antes de lançar
  // pagamento (senão o que já foi pago deixaria de bater com o total).
  const handleTaxaEntrega = v => {
    if (formasPgto.length) return;
    setTaxaEntregaR(v);
    const novoTotal = Math.max(0, totalVenda - parsePT(descontoR)) + parsePT(v);
    setValorPgtoAtual(novoTotal > 0 ? novoTotal.toFixed(2) : '');
  };

  const avancarParaPgto = () => {
    setEtapaSalvar(2);
    setValorPgtoAtual(totalComDesconto > 0 ? totalComDesconto.toFixed(2) : '');
    setTimeout(focarValorPgto, 60);
  };

  // ── Salvar / Finalizar Venda ──────────────────────────────────
  const totalComDesconto = Math.max(0, totalVenda - parsePT(descontoR)) + parsePT(taxaEntregaR);
  const totalPago        = formasPgto.reduce((acc, p) => acc + p.valor, 0);
  const troco            = Math.max(0, totalPago - totalComDesconto);
  const restante         = Math.max(0, totalComDesconto - totalPago);
  const semPagamento     = tipoMovFiscal?.gera_financeiro === false;
  // Tolerância de meio centavo: a soma dos itens em float pode deixar resíduo
  // (ex.: 36.84+65.76 = 102.60000000000001) e travar o Confirmar com resto "0,00"
  const pgtoCompleto     = semPagamento || (totalPago >= totalComDesconto - 0.005 && formasPgto.length > 0);

  const selecionarDebito = () => {
    // Vai para sub-estado 'debito' para escolher bandeira (igual ao crédito)
    setSubCartao('debito');
  };

  const confirmarDebito = () => {
    setFormaPgtoSel('Cartão Débito');
    setSubCartao(null);
    setValorPgtoAtual(restante > 0 ? restante.toFixed(2) : '');
    setTimeout(focarValorPgto, 30);
  };

  const selecionarCredito = (parcelas) => {
    setFormaPgtoSel(parcelas === 1 ? 'Cartão Crédito 1x' : `Cartão Crédito ${parcelas}x`);
    setParcelasCartao(parcelas);
    setSubCartao(null);
    setValorPgtoAtual(restante > 0 ? restante.toFixed(2) : '');
    setTimeout(focarValorPgto, 30);
  };

  // ↑/↓ trocam a bandeira do cartão sem tocar no mouse
  const navegarBandeira = (dir) => {
    const lista = subCartao === 'debito' ? bandeirasDebito : bandeirasCredito;
    if (!lista.length) return;
    const idx = lista.findIndex(b => b.id === bandeiraSel?.id);
    const n = idx < 0 ? 0 : Math.min(Math.max(idx + dir, 0), lista.length - 1);
    setBandeiraSel(lista[n]);
  };

  useEffect(() => {
    if (modalSalvar) {
      // Parâmetros → PDV → "Ocultar tela de desconto ao pressionar F9": pula
      // direto pra etapa 2 (pagamento) sem desconto nenhum aplicado — o
      // operador ainda pode voltar pelo botão "← Desconto" na tela de
      // pagamento (ver ModalSalvarVenda) se precisar mesmo assim.
      const pularDesconto = !abrirDescontoForcado && !!orgData?.pdv_config?.ocultar_desconto_f9;
      setEtapaSalvar(pularDesconto ? 2 : 1);
      // Desconto trazido de uma comanda (F1) — ver descontoComandaRef.
      // Sem isso, toda venda nova (comanda ou não) começa sem desconto.
      const descontoComanda = descontoComandaRef.current;
      descontoComandaRef.current = null;
      setDescontoR(descontoComanda ? String(descontoComanda.toFixed(2)).replace('.', ',') : '');
      setDescontoP('');
      const taxaComanda = taxaComandaRef.current;
      taxaComandaRef.current = null;
      setTaxaEntregaR(taxaComanda > 0 ? taxaComanda.toFixed(2) : '');
      setFormasPgto([]);
      const totalComDescontoInicial = Math.max(0, totalVenda - (descontoComanda || 0)) + (taxaComanda > 0 ? taxaComanda : 0);
      setValorPgtoAtual(pularDesconto && totalComDescontoInicial > 0 ? totalComDescontoInicial.toFixed(2) : '');
      setFormaPgtoSel('Dinheiro');
      setSubCartao(null);
      setParcelasCartao(1);
      setBandeiraSel(null);
      setCodigoAutorizacaoCartao('');
      setErroSalvar('');
      setTimeout(() => (pularDesconto ? salvarInputRef : descontoRRef).current?.focus(), 60);
    }
  }, [modalSalvar]);

  // ── Etapa 2 — atalhos de teclado via capture (funciona com input focado) ──
  useEffect(() => {
    // modalCliente aberto por cima: nada de atalhos (b/c/d/h/n/p iriam trocar a
    // forma de pagamento em vez de digitar na busca de cliente)
    if (!modalSalvar || etapaSalvar !== 2 || modalVendedor || modalCliente) return;

    const handler = e => {
      // Diálogo de senha de administrador aberto → nenhum atalho vale
      if (document.querySelector('[data-senha-admin]')) return;
      // Cobrança na maquininha em andamento: as teclas são da tela dela
      if (cobrancaPointRef.current) return;

      // F9 → confirmar pagamento (sempre interceptado quando modal aberto)
      if (e.key === 'F9') {
        e.preventDefault();
        e.stopPropagation();
        const { pgtoCompleto: completo, confirmarPagamento: confirmar } = salvarLiveRef.current;
        if (completo) confirmar();
        return;
      }

      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // F10 → selecionar cliente (mesmo dentro do modal de pagamento)
      if (e.key === 'F10') { e.preventDefault(); setModalCliente(true); return; }

      const { restante: rest, subCartao: sc, fidelidadeCliente: fidCli, creditoCliente: credCli,
              adicionarPgto: addPgto, selecionarDebito: selDeb,
              confirmarDebito: confDeb } = salvarLiveRef.current;

      // Backspace (fora do input) → volta etapa 1
      if (e.key === 'Backspace' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        setEtapaSalvar(1);
        setTimeout(() => descontoRRef.current?.focus(), 60);
        return;
      }

      // Sub-cartão: escolha débito / crédito
      if (sc === 'choose') {
        if (e.key.toLowerCase() === 'd') { e.preventDefault(); selDeb(); return; }
        if (e.key.toLowerCase() === 'c') { e.preventDefault(); setSubCartao('credito'); return; }
        return;
      }
      // Bandeira por teclado nos dois sub-fluxos (↑/↓)
      if ((sc === 'debito' || sc === 'credito') &&
          (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        salvarLiveRef.current.navegarBandeira(e.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      if (sc === 'debito') {
        if (e.key === 'Enter') { e.preventDefault(); confDeb(); return; }
        return;
      }
      if (sc === 'credito') {
        // 1–9 escolhem as parcelas (0 = 10x) e seguem direto para o valor
        if (/^[0-9]$/.test(e.key)) {
          e.preventDefault();
          salvarLiveRef.current.selecionarCredito(e.key === '0' ? 10 : parseInt(e.key, 10));
        }
        // Enter adiciona direto o restante na parcela destacada (1x por padrão)
        if (e.key === 'Enter') {
          e.preventDefault();
          salvarLiveRef.current.adicionarCreditoDireto(salvarLiveRef.current.parcelasCartao || 1);
        }
        return;
      }

      // Enter (fora do input) → adiciona pagamento; venda completa → confirma
      if (e.key === 'Enter' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        const { pgtoCompleto: completo, confirmarPagamento: confirmar } = salvarLiveRef.current;
        if (completo) confirmar(); else addPgto();
        return;
      }

      // M → cobra na maquininha Point (forma em Cartão e integração ligada)
      if (e.key.toLowerCase() === 'm' && salvarLiveRef.current.pointAtivo &&
          salvarLiveRef.current.formaPgtoSel?.startsWith('Cartão')) {
        e.preventDefault();
        salvarLiveRef.current.cobrarNaMaquininha();
        return;
      }

      // C → abre sub-opção cartão
      if (e.key.toLowerCase() === 'c') {
        e.preventDefault();
        setSubCartao('choose');
        return;
      }

      // B/D/H/N/P → seleciona forma e preenche valor restante (mesmo com input focado)
      const formaMap = { b: 'Boleto', d: 'Dinheiro', h: 'Cheque', n: 'Crediário', p: 'PIX',
        ...(fidCli?.credito_disponivel > 0 ? { f: 'Pontos Fidelidade' } : {}),
        ...(credCli?.total > 0 ? { v: 'Crédito do Cliente' } : {}) };
      const forma = formaMap[e.key?.toLowerCase()];
      if (forma) {
        e.preventDefault();
        setFormaPgtoSel(forma);
        setSubCartao(null);
        const valorMax = forma === 'Pontos Fidelidade' ? Math.min(rest, fidCli.credito_disponivel)
          : forma === 'Crédito do Cliente' ? Math.min(rest, credCli.total)
          : rest;
        setValorPgtoAtual(valorMax > 0 ? valorMax.toFixed(2) : '');
        setTimeout(focarValorPgto, 30);
      }
    };

    document.addEventListener('keydown', handler, true); // capture phase — antes do input processar
    return () => document.removeEventListener('keydown', handler, true);
  }, [modalSalvar, etapaSalvar, modalVendedor, modalCliente]);

  const adicionarPgto = () => {
    if (subCartao) return; // aguarda seleção de débito/crédito
    const v = parsePT(valorPgtoAtual);
    if (!v || v <= 0) return;
    // Crediário/Boleto exigem cliente identificado — trava aqui (fonte única),
    // não só na UI, porque o atalho de teclado Enter chama esta função direto.
    if ((formaPgtoSel === 'Crediário' || formaPgtoSel === 'Boleto' || formaPgtoSel === 'Pontos Fidelidade' || formaPgtoSel === 'Crédito do Cliente') && (!cliente || cliente.id === 1)) {
      setErroSalvar(`${formaPgtoSel} exige cliente identificado (diferente de VENDA CONSUMIDOR) — pressione F10 para escolher.`);
      return;
    }
    // Venda já paga: nada mais a adicionar
    if (restante <= 0.004) {
      setErroSalvar('A venda já está totalmente paga — não há valor restante.');
      return;
    }
    // Troco só existe em Dinheiro: outras formas não passam do restante
    if (formaPgtoSel !== 'Dinheiro' && v > restante + 0.004) {
      setErroSalvar(`Valor acima do restante (R$ ${restante.toFixed(2).replace('.', ',')}) — troco só em Dinheiro.`);
      return;
    }
    setErroSalvar('');
    // Pagamento que completa a venda → foco no Confirmar (Enter finaliza)
    const completaVenda = totalPago + v >= totalComDesconto - 0.004;
    const isPix  = formaPgtoSel === 'PIX';
    const isCred = formaPgtoSel === 'Crediário';
    const isBol  = formaPgtoSel === 'Boleto';
    if (completaVenda) setTimeout(() => confirmarBtnRef.current?.focus(), 60);
    setFormasPgto(prev => {
      const idx = prev.findIndex(p => p.forma === formaPgtoSel && !p.mp_point_cobranca_id);
      if (idx >= 0 && !isCred && !isBol) {
        const novo = [...prev];
        novo[idx] = { ...novo[idx], valor: parseFloat((novo[idx].valor + v).toFixed(2)) };
        return novo;
      }
      return [...prev, {
        forma:      formaPgtoSel,
        valor:      v,
        bandeira_id: bandeiraSel?.id || null,
        codigo_autorizacao: formaPgtoSel.startsWith('Cartão') ? (codigoAutorizacaoCartao.trim() || null) : null,
        parcelas:   isCred ? (parcelasCrediario || 1) : isBol ? (parcelasBoleto || 1) : (parcelasCartao || 1),
        conta_id:   isPix ? (pixContaId ? parseInt(pixContaId) : null)
                  : isBol ? (boletoContaId ? parseInt(boletoContaId) : null)
                  : null,
      }];
    });
    setValorPgtoAtual('');
    setTimeout(focarValorPgto, 30);
  };

  // Enter na tela de parcelas do crédito: adiciona direto o valor restante na
  // parcela destacada (1x por padrão), sem passar pelo campo de valor.
  const adicionarCreditoDireto = (parcelas) => {
    if (restante <= 0.004) {
      setErroSalvar('A venda já está totalmente paga — não há valor restante.');
      return;
    }
    const forma = parcelas === 1 ? 'Cartão Crédito 1x' : `Cartão Crédito ${parcelas}x`;
    const v = parseFloat(restante.toFixed(2));
    setErroSalvar('');
    setParcelasCartao(parcelas);
    setFormaPgtoSel(forma);
    setSubCartao(null);
    setFormasPgto(prev => {
      const idx = prev.findIndex(p => p.forma === forma && !p.mp_point_cobranca_id);
      if (idx >= 0) {
        const novo = [...prev];
        novo[idx] = { ...novo[idx], valor: parseFloat((novo[idx].valor + v).toFixed(2)) };
        return novo;
      }
      return [...prev, {
        forma, valor: v,
        bandeira_id: bandeiraSel?.id || null,
        codigo_autorizacao: codigoAutorizacaoCartao.trim() || null,
        parcelas,
        conta_id: null,
      }];
    });
    setValorPgtoAtual('');
    setTimeout(() => confirmarBtnRef.current?.focus(), 60);
  };

  // ── Maquininha Mercado Pago Point integrada ──────────────────
  // Ligada em Mercado Pago → Maquininha Point (mercadopago_config.point_ativo,
  // módulo Mercado Pago liberado) e só com internet — a cobrança passa pelo servidor.
  const pointAtivo = online && !!orgData?.mercadopago_config?.point_ativo && !!orgData?.modulos_liberados?.mercadopago;

  const cobrarNaMaquininha = async () => {
    if (subCartao || !formaPgtoSel.startsWith('Cartão') || cobrancaPointRef.current) return;
    const v = parseFloat(parsePT(valorPgtoAtual).toFixed(2));
    if (!v || v <= 0) return;
    if (restante <= 0.004) { setErroSalvar('A venda já está totalmente paga — não há valor restante.'); return; }
    if (v > restante + 0.004) {
      setErroSalvar(`Valor acima do restante (R$ ${restante.toFixed(2).replace('.', ',')}).`);
      return;
    }
    setErroSalvar('');
    const tipo = /d[eé]bito/i.test(formaPgtoSel) ? 'debito' : 'credito';
    const base = { valor: v, tipo, parcelas: tipo === 'debito' ? 1 : (parcelasCartao || 1), status: 'enviando' };
    setCobrancaPoint(base);
    try {
      const { data } = await api.post('/mp-point/cobrancas', { valor: v, tipo, parcelas: base.parcelas });
      setCobrancaPoint({ ...base, ...data });
    } catch (err) {
      setCobrancaPoint({ ...base, status: 'erro', erro: err.response?.data?.erro || 'Não foi possível enviar a cobrança para a maquininha.' });
    }
  };

  // Aprovada → vira linha de pagamento (uma só por cobrança). A forma segue o
  // que o cliente fez de fato na maquininha (parcelas podem mudar lá).
  const pagamentoDaCobranca = cob => {
    const n = cob.tipo === 'debito' ? 1 : (parseInt(cob.parcelas, 10) || 1);
    return {
      forma: cob.tipo === 'debito' ? 'Cartão Débito' : `Cartão Crédito ${n}x`,
      valor: parseFloat(cob.valor), bandeira_id: null, codigo_autorizacao: cob.codigo_autorizacao || null,
      parcelas: n, conta_id: null, mp_point_cobranca_id: cob.id,
      mp_point_info: [cob.bandeira, cob.ultimos_digitos ? `****${cob.ultimos_digitos}` : ''].filter(Boolean).join(' '),
    };
  };

  useEffect(() => {
    if (!cobrancaPoint?.id || !['pendente', 'cancelando'].includes(cobrancaPoint.status)) return;
    let vivo = true;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/mp-point/cobrancas/${cobrancaPoint.id}`);
        if (!vivo) return;
        if (data.status === 'pendente') return; // segue esperando (inclusive "cancelando")
        setCobrancaPoint(prev => prev && prev.id === data.id ? { ...prev, ...data } : prev);
        if (data.status === 'aprovado') {
          const pg = pagamentoDaCobranca(data);
          setFormasPgto(prev => prev.some(p => p.mp_point_cobranca_id === data.id) ? prev : [...prev, pg]);
          setValorPgtoAtual('');
          setTimeout(() => {
            setCobrancaPoint(null);
            const { restante: rest } = salvarLiveRef.current;
            (rest <= 0.004 ? confirmarBtnRef : salvarInputRef).current?.focus();
          }, 1500);
        }
      } catch { /* oscilação de rede: tenta de novo no próximo ciclo */ }
    }, 2000);
    return () => { vivo = false; clearInterval(t); };
  }, [cobrancaPoint?.id, cobrancaPoint?.status]);

  const cancelarCobrancaPoint = async () => {
    const cob = cobrancaPointRef.current;
    if (!cob?.id) { setCobrancaPoint(null); return; }
    setCobrancaPoint(prev => ({ ...prev, status: 'cancelando' }));
    try {
      const { data } = await api.post(`/mp-point/cobrancas/${cob.id}/cancelar`);
      setCobrancaPoint(prev => ({ ...prev, ...data, status: data.status === 'pendente' ? 'cancelando' : data.status }));
    } catch (err) {
      setCobrancaPoint(prev => ({ ...prev, status: 'pendente', erro: err.response?.data?.erro || 'Falha ao cancelar — tente de novo ou cancele na maquininha.' }));
    }
  };

  // ✕ numa linha cobrada na maquininha: estorna no cartão antes de tirar da venda
  const removerPgto = async (i) => {
    const p = formasPgto[i];
    if (!p) return;
    if (!p.mp_point_cobranca_id) { setFormasPgto(prev => prev.filter((_, j) => j !== i)); return; }
    if (!window.confirm(`Este pagamento já foi cobrado na maquininha (R$ ${p.valor.toFixed(2).replace('.', ',')}).\n\nEstornar o valor no cartão do cliente e remover da venda?`)) return;
    try {
      await api.post(`/mp-point/cobrancas/${p.mp_point_cobranca_id}/estornar`);
      setFormasPgto(prev => prev.filter(x => x.mp_point_cobranca_id !== p.mp_point_cobranca_id));
    } catch (err) {
      setErroSalvar(err.response?.data?.erro || 'Não foi possível estornar na maquininha.');
    }
  };

  // ── Verifica caixa aberto antes de abrir modal de venda ─────
  // forcarDesconto=true (atalho F6 "Desconto Global") sempre abre na etapa 1,
  // mesmo com "Ocultar tela de desconto ao pressionar F9" ligado — só o F9
  // (finalizar) respeita esse parâmetro.
  const verificarEAbrirVenda = async (forcarDesconto = false) => {
    if (itensRef.current.length === 0) return;
    setAbrirDescontoForcado(forcarDesconto);
    try {
      const { data: turno } = await api.get('/financeiro/turnos/aberto');
      if (turno) {
        setTurnoAtual(turno);
        setModalSalvar(true);
      } else {
        setSuprimentoCaixa('0,00');
        setErroAbertura('');
        setPendingVenda(true);
        setModalAbrirCaixa(true);
      }
    } catch {
      // Falha na API → não bloqueia o operador
      setModalSalvar(true);
    }
  };

  const abrirCaixa = async () => {
    setAbrindoCaixa(true); setErroAbertura('');
    try {
      const valorSup = parsePT(suprimentoCaixa);
      const { data: turno } = await api.post('/financeiro/turnos/abrir', {
        conta_id:       contaAbertura ? parseInt(contaAbertura) : null,
        saldo_abertura: isNaN(valorSup) ? 0 : valorSup,
      });
      setTurnoAtual(turno);
      setModalAbrirCaixa(false);
      if (pendingVenda) {
        setPendingVenda(false);
        setModalSalvar(true);
      }
    } catch (err) {
      setErroAbertura(err.response?.data?.erro || 'Erro ao abrir caixa.');
    } finally {
      setAbrindoCaixa(false);
    }
  };

  const fecharCaixa = async () => {
    if (!turnoAtual) return;
    setFechendoCaixa(true); setErroFechamento('');
    try {
      const valor = parsePT(saldoFechamento);
      const turnoFechadoId = turnoAtual.id;
      await api.put(`/financeiro/turnos/${turnoFechadoId}/fechar`, {
        saldo_fechamento: isNaN(valor) ? null : valor,
      });
      setTurnoAtual(null);
      setModalFecharCaixa(false);
      setSaldoFechamento('');
      setUltimoTurnoFechadoId(turnoFechadoId);
      localStorage.setItem('pdv_ultimo_turno_fechado_id', turnoFechadoId);
      // Imprime o fechamento automaticamente — busca o resumo já com o
      // saldo_fechamento gravado (por isso vem depois do PUT acima, não do
      // turnoAtual antigo). Falha na impressão não desfaz o fechamento —
      // já fechou; o operador reimprime pelo menu se precisar.
      try {
        const { data: resumoFechado } = await api.get(`/financeiro/turnos/${turnoFechadoId}/resumo`);
        imprimirFechamentoCaixaTermico(resumoFechado, larguraCupom);
      } catch { /* impressão é best-effort — caixa já fechou de qualquer forma */ }
    } catch (err) {
      setErroFechamento(err.response?.data?.erro || 'Erro ao fechar caixa.');
    } finally {
      setFechendoCaixa(false);
    }
  };

  // Reimprime o último fechamento de caixa feito NESTE computador (sem
  // precisar sair do PDV e ir na tela Financeiro → Caixa/Fechamento).
  const reimprimirFechamento = async () => {
    if (!ultimoTurnoFechadoId) { alert('Nenhum fechamento de caixa recente pra reimprimir.'); return; }
    try {
      const { data: resumoFechado } = await api.get(`/financeiro/turnos/${ultimoTurnoFechadoId}/resumo`);
      imprimirFechamentoCaixaTermico(resumoFechado, larguraCupom);
    } catch {
      alert('Erro ao buscar o fechamento pra reimprimir.');
    }
  };

  const salvarNovoCli = async () => {
    if (!novoCliNome.trim()) { setErroNovoCli('Nome é obrigatório.'); return; }
    setSalvandoNovoCli(true); setErroNovoCli('');
    try {
      const { data } = await api.post('/clientes', {
        nome: novoCliNome.trim(),
        cpf_cnpj: novoCliCpf.trim() || undefined,
        fone1: novoCliFone.trim() || undefined,
      });
      selecionarCliente(data);
      setSubModalNovoCli(false);
      setNovoCliNome(''); setNovoCliCpf(''); setNovoCliFone('');
    } catch (err) {
      setErroNovoCli(err.response?.data?.erro || err.response?.data?.erros?.[0]?.msg || 'Erro ao salvar cliente.');
    } finally {
      setSalvandoNovoCli(false);
    }
  };

  // Com "Solicitar vendedor" ligado (Parâmetros → PDV), a 1ª chamada (botão/F9,
  // que passa evento ou nada) abre a escolha; a confirmação do ModalVendedor
  // chama de novo com o id (número) ou null explícito (= operador).
  const confirmarPagamento = async (vendedorArg) => {
    const vendedorEscolhido =
      (typeof vendedorArg === 'number' || vendedorArg === null) ? vendedorArg : undefined;
    if (salvando) return;
    if (orgData?.pdv_config?.solicitar_vendedor && vendedorEscolhido === undefined) {
      try {
        const { data } = await api.get('/usuarios');
        setVendedoresPdv((data || []).filter(u => u.ativo && u.exibir_pdv));
      } catch { setVendedoresPdv([]); }
      setModalVendedor(true);
      return;
    }
    setSalvando(true);
    setErroSalvar('');
    // PDV offline (Fase 1): uuid gerado agora, antes do 1º POST — vira a
    // chave de idempotência. Se essa venda ficar na fila local e for
    // reenviada depois (ou reenviada aqui mesmo pelo loop de senha admin
    // abaixo), o backend nunca duplica (vendasController.criar).
    const uuid = crypto.randomUUID();
    // Precisa estar acessível também no catch (fora do try) pra poder
    // enfileirar exatamente o último payload tentado se a rede cair.
    let tentativa = null;
    try {
      const desconto = parseFloat(parsePT(descontoR).toFixed(2));
      const payload = {
        cliente_id:               cliente?.id || null,
        desconto:                 desconto,
        taxa_entrega:             parseFloat(parsePT(taxaEntregaR).toFixed(2)) || 0,
        total_pago:               totalPago,
        troco:                    troco,
        observacao:               obsTexto || null,
        tipo_fiscal:              'sem',
        tipo_movimento_fiscal_id: pedidoTipoMovId || null,
        pedido_origem_id:         pedidoOrigemId  || null,
        comanda_id:               mesaAtiva?.comandaId || null,
        // Sem escolha explícita de vendedor (F10/"Solicitar vendedor"), uma
        // venda que veio de mesa/comanda mantém quem abriu — senão o
        // backend atribuiria a venda a quem está operando o caixa agora
        // (vendasController.criar cai pro usuário logado quando vendedor_id
        // vem null), roubando a comissão de quem realmente atendeu.
        vendedor_id:              vendedorEscolhido ?? mesaAtiva?.comandaOperadorId ?? null,
        chave_idempotencia:       uuid,
        itens: itens.map(i => ({
          produto_id:     i.id,
          descricao:      i.descricao,
          unidade:        i.un,
          quantidade:     i.qtd,
          preco_unitario: i.preco_unit,
          preco_tabela:   i.preco_tabela ?? i.preco_unit,
          total_item:     i.total,
        })),
        pagamentos: formasPgto.map(p => ({
          forma_pagamento: p.forma,
          valor:           p.valor,
          bandeira_id:     p.bandeira_id || null,
          codigo_autorizacao: p.codigo_autorizacao || null,
          parcelas:        p.parcelas || 1,
          conta_id:        p.conta_id   || null,
          mp_point_cobranca_id: p.mp_point_cobranca_id || null,
        })),
      };
      let data;
      tentativa = { ...payload };
      for (let i = 0; i < 6; i++) {
        try {
          ({ data } = await api.post('/vendas', tentativa));
          break;
        } catch (err) {
          const d = err.response?.data || {};
          if (d.exige_admin_desconto) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Desconto acima do máximo permitido nos produtos.',
              { acao: 'liberar_desconto_maximo', detalhes: d.erro });
            if (senha === null) { setSalvando(false); return; }
            tentativa = { ...tentativa, senha_admin_desconto: senha };
            continue;
          }
          if (d.exige_senha_estoque_insuficiente) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Estoque insuficiente para finalizar a venda.',
              { acao: 'liberar_estoque_insuficiente', detalhes: d.erro });
            if (senha === null) { setSalvando(false); return; }
            tentativa = { ...tentativa, senha_estoque_insuficiente: senha };
            continue;
          }
          if (d.exige_senha_limite_credito) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Limite de crédito do cliente insuficiente para esta venda a prazo.',
              { acao: 'liberar_limite_credito', detalhes: d.erro });
            if (senha === null) { setSalvando(false); return; }
            tentativa = { ...tentativa, senha_limite_credito: senha };
            continue;
          }
          if (d.exige_senha_atraso_cliente) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Cliente com títulos vencidos — esta venda também é a prazo.',
              { acao: 'liberar_cliente_inadimplente', detalhes: d.erro });
            if (senha === null) { setSalvando(false); return; }
            tentativa = { ...tentativa, senha_atraso_cliente: senha };
            continue;
          }
          if (d.aviso_atraso_cliente) {
            if (!window.confirm(`${d.erro}\n\nDeseja continuar mesmo assim?`)) { setSalvando(false); return; }
            tentativa = { ...tentativa, ciente_atraso_cliente: true };
            continue;
          }
          throw err;
        }
      }
      setVendaSalva(data);
      // Abre a gaveta sozinha quando dinheiro entrou no pagamento (mesmo com
      // pagamento dividido — dinheiro + cartão, por exemplo) — silencioso
      // (sem alerta) se não tiver impressora/gaveta configurada, pra não
      // interromper o caixa por causa disso a cada venda.
      if (gavetaHabilitada && formasPgto.some(p => p.forma === 'Dinheiro')) acionarGaveta(true);
      // Guarda a origem ANTES de zerar mesaAtiva — usado só quando o PDV
      // voltar pro carrinho vazio de vez (tentarReabrirMesasComandas),
      // depois que o operador passar pelo modal fiscal.
      if (mesaAtiva) setUltimaVendaOrigem(mesaAtiva.tipoOrigem === 'individual' ? 'individual' : 'mesa');
      setMesaAtiva(null); // comanda já fechou no backend junto com a venda
      try {
        const { data: vendaCompleta } = await api.get(`/vendas/${data.id}`);
        setItensServicoVenda((vendaCompleta.itens || []).filter(i => ehServico(i.tipo_item)));
      } catch { setItensServicoVenda([]); }
      setNfseResultados({});
      setModalSalvar(false);
      setModalFiscal(true);
    } catch (err) {
      // Sem resposta do servidor (rede caiu) numa venda avulsa (sem mesa/
      // comanda vinculada) — não trava o caixa: guarda na fila local e
      // segue como se tivesse dado certo. Documento fiscal fica pendente
      // (mesmo estado de "Salvar sem imprimir" — emite depois pelo F7,
      // quando essa venda já tiver sincronizado de verdade).
      // Mesa/comanda fica de fora: fechar a comanda é uma operação atômica
      // do servidor (POST /vendas), não dá pra fingir que "deu certo" sem
      // ele — nesse caso mostra o erro normal e pede pra tentar de novo.
      if (!err.response && !mesaAtiva && tentativa) {
        try {
          await enqueueVenda(uuid, tentativa);
          setModalSalvar(false);
          setAvisoOffline('Venda salva offline — será sincronizada quando a internet voltar.');
          limparPDV();
        } catch {
          setErroSalvar('Não foi possível salvar a venda (nem online, nem na fila local). Tente novamente.');
        }
      } else if (!err.response && mesaAtiva) {
        setErroSalvar('Sem conexão — vendas de mesa/comanda exigem internet pra fechar corretamente. Tente novamente quando a conexão voltar.');
      } else {
        setErroSalvar(err.response?.data?.erro || 'Erro ao salvar venda. Tente novamente.');
      }
    } finally {
      setSalvando(false);
    }
  };

  // Mantém ref com valores e funções frescos para o handler de capture
  salvarLiveRef.current = {
    totalComDesconto, totalPago, restante, pgtoCompleto, subCartao, fidelidadeCliente, creditoCliente,
    adicionarPgto, selecionarDebito, confirmarDebito, avancarParaPgto, confirmarPagamento,
    selecionarCredito, navegarBandeira, parcelasCartao, adicionarCreditoDireto,
    pointAtivo, cobrarNaMaquininha, formaPgtoSel,
  };

  // "Gerar DANFE" por item de serviço — assina e transmite de verdade
  // (fiscalController.emitirNfse); autorizada → abre o DANFSe oficial vindo
  // do próprio Sistema Nacional (abrirDanfseOficial); rejeitada/erro → cai
  // no preview local (gerarPreviewDanfSe), que mostra o motivo real.
  const emitirNfseItem = async (item) => {
    if (!vendaSalva?.id) return;
    setNfseEmitindo(item.id);
    // Aberta AQUI, antes de qualquer await — ver comentário em
    // impressaoVenda.js#abrirDanfseOficial (window.open depois do await não
    // conta mais como resposta direta a um clique, o navegador bloqueia).
    const winDanfse = window.open('', '_blank');
    try {
      const { data } = await api.post('/fiscal/nfse', { venda_id: vendaSalva.id, item_venda_id: item.id });
      setNfseResultados(prev => ({ ...prev, [item.id]: data }));
      if (data?.sucesso) await abrirDanfseOficial(data, orgData || {}, winDanfse);
      else if (data?.dados) await gerarPreviewDanfSe(data, orgData || {}, winDanfse);
      else { try { winDanfse?.close(); } catch (_) {} }
    } catch (err) {
      const data = err.response?.data || { erro: 'Erro ao gerar o DANFSe.' };
      setNfseResultados(prev => ({ ...prev, [item.id]: data }));
      // "Este item já possui NFS-e autorizada" — não é bem um erro, é a
      // proteção contra duplicar; reabre o DANFSe oficial já emitido em
      // vez de só mostrar o aviso sem nenhuma ação (senão não tinha como
      // reimprimir/rebaixar depois da 1ª vez).
      if (data?.documento_id) await abrirDanfseOficial({ ...data, sucesso: true }, orgData || {}, winDanfse);
      else if (data?.dados) await gerarPreviewDanfSe(data, orgData || {}, winDanfse);
      else { try { winDanfse?.close(); } catch (_) {} }
    } finally {
      setNfseEmitindo(null);
    }
  };

  // F7 — venda selecionada na busca de movimentação: se tiver item de serviço
  // sem NFS-e ainda, oferece emitir depois (venda pode ter sido salva "sem
  // imprimir" ou com outro doc fiscal, sem passar pela NFS-e na hora).
  useEffect(() => {
    setNfseResultadosMov({});
    if (!movSelecionada?.id) { setItensServicoMov([]); return; }
    let cancelado = false;
    api.get(`/vendas/${movSelecionada.id}`)
      .then(({ data }) => {
        if (!cancelado) setItensServicoMov((data.itens || []).filter(i => ehServico(i.tipo_item)));
      })
      .catch(() => { if (!cancelado) setItensServicoMov([]); });
    return () => { cancelado = true; };
  }, [movSelecionada?.id]);

  const emitirNfseItemMov = async (item) => {
    if (!movSelecionada?.id) return;
    setNfseEmitindoMov(item.id);
    // Aberta AQUI, antes de qualquer await — ver comentário em
    // impressaoVenda.js#abrirDanfseOficial (window.open depois do await não
    // conta mais como resposta direta a um clique, o navegador bloqueia).
    const winDanfse = window.open('', '_blank');
    try {
      const { data } = await api.post('/fiscal/nfse', { venda_id: movSelecionada.id, item_venda_id: item.id });
      setNfseResultadosMov(prev => ({ ...prev, [item.id]: data }));
      if (data?.sucesso) await abrirDanfseOficial(data, orgData || {}, winDanfse);
      else if (data?.dados) await gerarPreviewDanfSe(data, orgData || {}, winDanfse);
      else { try { winDanfse?.close(); } catch (_) {} }
    } catch (err) {
      const data = err.response?.data || { erro: 'Erro ao gerar o DANFSe.' };
      setNfseResultadosMov(prev => ({ ...prev, [item.id]: data }));
      // "Este item já possui NFS-e autorizada" — não é bem um erro, é a
      // proteção contra duplicar; reabre o DANFSe oficial já emitido em
      // vez de só mostrar o aviso sem nenhuma ação (senão não tinha como
      // reimprimir/rebaixar depois da 1ª vez).
      if (data?.documento_id) await abrirDanfseOficial({ ...data, sucesso: true }, orgData || {}, winDanfse);
      else if (data?.dados) await gerarPreviewDanfSe(data, orgData || {}, winDanfse);
      else { try { winDanfse?.close(); } catch (_) {} }
    } finally {
      setNfseEmitindoMov(null);
    }
  };

  const limparPDV = () => {
    setItens([]);
    setItensServicoVenda([]);
    setNfseResultados({});
    setItemIdx(null);
    setFormasPgto([]);
    // Não cai direto no 1-VENDA CONSUMIDOR aqui — mesma lógica do primeiro
    // carregamento (ver adicionarItem): só quando o primeiro item da
    // PRÓXIMA venda for inserido é que o cliente padrão entra, nunca antes.
    setCliente(null);
    setTabelaPrecoCliente(null);
    setObsTexto('');
    setDescontoR('');
    setDescontoP('');
    setTaxaEntregaR('');
    setMesaAtiva(null);
    setVendaSalva(null);
    setBusca('');
    // Pedido de origem/tipo de movimento de uma pré-venda importada NÃO podem
    // vazar pra próxima venda — senão ela nasce vinculada a um pedido já
    // importado e o backend recusa com "Pedido de origem já está importado".
    setPedidoOrigemId(null);
    setPedidoTipoMovId(null);
    buscaRef.current?.focus();
  };

  // "Sair" (Menu de Opções e Esc na tela principal, sem modal aberto): com
  // itens lançados, pede confirmação antes de descartar a venda em
  // andamento (nunca desloga sem avisar); sem itens não tem o que perder —
  // sai direto pra tela de login.
  const sairDoPdv = () => {
    if (itens.length > 0) {
      if (window.confirm('Deseja desistir desta venda?')) limparPDV();
      return;
    }
    logout();
  };
  // O handler de Esc/atalhos globais é montado num efeito com deps fixas
  // (não inclui `itens`, que muda a cada item bipado) — mesmo padrão de
  // salvarLiveRef pra não precisar re-registrar o listener a cada scan.
  const sairDoPdvRef = useRef(sairDoPdv);
  sairDoPdvRef.current = sairDoPdv;

  const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');

  const fetchLogoBase64 = async (logoUrl) => {
    if (!logoUrl) return '';
    const src = logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}${logoUrl}`;
    try {
      const resp = await fetch(src);
      if (!resp.ok) return '';
      const blob = await resp.blob();
      return await new Promise(res => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = () => res('');
        reader.readAsDataURL(blob);
      });
    } catch { return ''; }
  };

  const buildCupomBase = (largura) => {
    const org      = orgData || {};
    const nome     = org.nome_fantasia || org.razao_social || 'EMPRESA';
    const cnpj     = (org.cnpj || '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    const endereco = [org.logradouro, org.numero, org.bairro].filter(Boolean).join(', ');
    const cidadeUF = [org.cidade, org.uf].filter(Boolean).join('/');
    const fone     = org.telefone ? `Tel: ${org.telefone}` : '';
    const dataHora = vendaSalva?.data_hora
      ? new Date(vendaSalva.data_hora).toLocaleString('pt-BR')
      : new Date().toLocaleString('pt-BR');
    const nrVenda  = vendaSalva?.id || '—';
    const subtotal = itens.reduce((acc, i) => acc + i.total, 0);
    const desconto = parsePT(descontoR);
    const taxaEnt  = parsePT(taxaEntregaR);
    const total    = Math.max(0, subtotal - desconto) + taxaEnt;
    const fs       = largura === '58mm' ? '9px' : '10px';
    const fsGrand  = largura === '58mm' ? '11px' : '13px';
    const fsTitulo = largura === '58mm' ? '12px' : '14px';
    return { nome, cnpj, endereco, cidadeUF, fone, dataHora, nrVenda, subtotal, desconto, taxaEnt, total, fs, fsGrand, fsTitulo };
  };

  // Código do item nas impressões da finalização — itens do carrinho (ainda
  // não salvos) usam campos diferentes (codigo/id) dos itens de uma venda já
  // salva (codigo_interno/produto_id, ver codigoItem importado de
  // impressaoVenda.js). Mesma prioridade: código interno primeiro.
  const codigoItemCarrinho = (i, idx) => i.codigo || i.id || (idx + 1);

  const gerarCupom = async (largura) => {
    const { nome, cnpj, endereco, cidadeUF, fone, dataHora, nrVenda, subtotal, desconto, taxaEnt, total, fs, fsGrand, fsTitulo } = buildCupomBase(largura);
    const larguraCSS = larguraEfetivaCSS(largura);
    const totalPagoVal = formasPgto.reduce((s, p) => s + parsePT(p.valor), 0);
    const trocoVal = Math.max(0, totalPagoVal - total);
    const logoData = await fetchLogoBase64(orgData?.logo_url || '');
    const logoHtml = logoData ? `<div class="c" style="margin-bottom:4px;"><img src="${logoData}" style="max-width:180px;max-height:70px;object-fit:contain;" /></div>` : '';

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>Cupom #${nrVenda}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',Courier,monospace;font-size:${fs};font-weight:600;width:${larguraCSS};padding:4px;}
  .c{text-align:center;}.b{font-weight:bold;}
  .hr{border:none;border-top:1px dashed #000;margin:4px 0;}
  .row{display:flex;justify-content:space-between;}
  .it{margin:2px 0;}.grand{font-size:${fsGrand};}
  @media print{@page{margin:0;size:${larguraCSS} auto;}}
</style></head><body>
  ${logoHtml}
  <p class="c b" style="font-size:${fsTitulo};margin-bottom:2px;">${nome}</p>
  <p class="c">CNPJ: ${cnpj}</p>
  ${endereco ? `<p class="c">${endereco}</p>` : ''}
  ${cidadeUF ? `<p class="c">${cidadeUF}</p>` : ''}
  ${fone     ? `<p class="c">${fone}</p>` : ''}
  <hr class="hr">
  <p class="c b">CUPOM NÃO FISCAL</p>
  <p class="c">${dataHora}</p>
  <p class="row"><span>Venda nº:</span><span class="b">${nrVenda}</span></p>
  <p class="row"><span>Cliente:</span><span>${cliente?.nome || 'Consumidor Final'}</span></p>
  <hr class="hr">
  <p class="b">ITENS</p>
  ${itens.map((i, idx) => `
  <div class="it">
    <p><b>${codigoItemCarrinho(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.qtd).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.un} × R$ ${fmtBRL(i.preco_unit)}</span>
      <span>R$ ${fmtBRL(i.total)}</span>
    </p>
    ${(i.preco_tabela ?? i.preco_unit) > i.preco_unit ? `<p class="row" style="font-size:9px;color:#555;"><span>de R$ ${fmtBRL(i.preco_tabela)} — desconto item</span><span>– R$ ${fmtBRL((i.preco_tabela - i.preco_unit) * i.qtd)}</span></p>` : ''}
  </div>`).join('')}
  <hr class="hr">
  <p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></p>
  ${desconto > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desconto)}</span></p>` : ''}
  ${taxaEnt > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaEnt)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(total)}</span></p>
  <hr class="hr">
  <p class="b">PAGAMENTOS</p>
  ${formasPgto.map(p => `<p class="row"><span>${p.forma}</span><span>R$ ${fmtBRL(p.valor)}</span></p>`).join('')}
  <hr class="hr">
  <p class="row"><span>Total pago</span><span>R$ ${fmtBRL(totalPagoVal)}</span></p>
  ${trocoVal > 0 ? `<p class="row"><span>Troco</span><span>R$ ${fmtBRL(trocoVal)}</span></p>` : ''}
  <hr class="hr">
  <p class="c" style="margin-top:6px;font-size:9px;">Obrigado pela preferência!</p>
  <p class="c" style="font-size:8px;">Cupom não fiscal</p>
</body></html>`;

    if (await tentarImprimirDiretoHtml(html)) return;
    const win = window.open('', '_blank', `width=${largura === '58mm' ? 320 : 400},height=700`);
    if (!win) { alert('Permita pop-ups para imprimir o cupom.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    await aguardarImagens(win);
    win.focus();
    win.print();
  };

  // DANFE NFC-e (térmico)
  const gerarDanfeNfce = async (resultado, largura) => {
    // Abre o popup imediatamente (contexto de gesto do usuário) para não ser bloqueado
    const win = window.open('', '_blank', `width=${largura==='58mm'?320:400},height=700`);
    if (!win) { alert('Permita pop-ups para imprimir o DANFE.'); return; }
    win.document.write('<html><body style="font-family:monospace;padding:8px;">Gerando DANFE…</body></html>');

    const { nome, cnpj, endereco, cidadeUF, fone, dataHora, nrVenda, fs, fsGrand, fsTitulo } = buildCupomBase(largura);
    const larguraCSS = larguraEfetivaCSS(largura);
    // NFC-e é documento de MERCADORIA — item de serviço nunca entra no DANFE
    // (serviço vai por NFS-e, ainda não implementada). Desconto E PAGAMENTOS
    // reajustados proporcionalmente à parte de mercadoria, igual ao que o
    // backend fez na emissão real (o XML já transmitido já está correto; aqui
    // é só o papel combinando com o que foi realmente autorizado pela SEFAZ
    // — senão o troco calculado aqui mostraria a parte do serviço como troco).
    const todosItensDanfe = itens;
    const itensNfceDanfe = todosItensDanfe.filter(i => !ehServico(i.tipo));
    const removeuServicoDanfe = itensNfceDanfe.length < todosItensDanfe.length;
    const subtotalTodosDanfe = todosItensDanfe.reduce((acc, i) => acc + i.total, 0);
    const subtotal = itensNfceDanfe.reduce((acc, i) => acc + i.total, 0);
    const proporcaoDanfe = subtotalTodosDanfe > 0 ? subtotal / subtotalTodosDanfe : 1;
    const descontoOriginalDanfe = parsePT(descontoR);
    const desconto = removeuServicoDanfe ? +(descontoOriginalDanfe * proporcaoDanfe).toFixed(2) : descontoOriginalDanfe;
    const taxaEnt = parsePT(taxaEntregaR);
    const total = Math.max(0, subtotal - desconto) + taxaEnt;
    const pgtosDanfe = removeuServicoDanfe
      ? formasPgto.map(p => ({ ...p, valor: +(parsePT(p.valor) * proporcaoDanfe).toFixed(2) }))
      : formasPgto;
    const chave    = resultado.chave_acesso || '';
    const proto    = resultado.protocolo    || '';
    const qrUrl    = resultado.qr_code_url  || '';
    const urlChave = resultado.url_consulta || '';
    const emit     = resultado.emitente     || {};
    const emNome   = emit.nome_fantasia || emit.razao_social || nome;
    const emCnpj   = emit.cnpj ? emit.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : cnpj;
    const emEnd    = [emit.logradouro, emit.numero, emit.bairro].filter(Boolean).join(', ') || endereco;
    const emCidade = [emit.cidade, emit.uf].filter(Boolean).join('/') || cidadeUF;
    const isHomol  = resultado.ambiente === 'homologacao';
    const totalPagoVal = pgtosDanfe.reduce((s, p) => s + parsePT(p.valor), 0);
    const trocoVal = Math.max(0, totalPagoVal - total);

    const chaveFormatada = chave.replace(/(\d{4})/g, '$1 ').trim();

    let qrDataUrl = '';
    if (qrUrl) {
      try { qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 200, margin: 2, errorCorrectionLevel: 'M' }); } catch (_) {}
    }
    const logoData = await fetchLogoBase64(orgData?.logo_url || '');
    const logoHtml = logoData ? `<div class="c" style="margin-bottom:4px;"><img src="${logoData}" style="max-width:180px;max-height:70px;object-fit:contain;" /></div>` : '';

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>DANFE NFC-e #${nrVenda}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',Courier,monospace;font-size:${fs};font-weight:600;width:${larguraCSS};padding:4px;}
  .c{text-align:center;}.b{font-weight:bold;}.r{text-align:right;}
  .hr{border:none;border-top:1px dashed #000;margin:4px 0;}
  .row{display:flex;justify-content:space-between;}
  .it{margin:2px 0;}.grand{font-size:${fsGrand};}
  .chave{word-break:break-all;font-size:8px;letter-spacing:0.5px;}
  .homol{background:#fff3cd;border:1px solid #ffc107;padding:2px 4px;text-align:center;font-size:8px;margin:3px 0;}
  @media print{@page{margin:0;size:${larguraCSS} auto;}}
</style></head><body>
  ${isHomol ? '<div class="homol">AMBIENTE DE HOMOLOGAÇÃO — SEM VALOR FISCAL</div>' : ''}
  ${logoHtml}
  <p class="c b" style="font-size:${fsTitulo};margin-bottom:2px;">${emNome}</p>
  <p class="c">CNPJ: ${emCnpj}</p>
  ${emEnd    ? `<p class="c">${emEnd}</p>` : ''}
  ${emCidade ? `<p class="c">${emCidade}</p>` : ''}
  ${fone     ? `<p class="c">${fone}</p>` : ''}
  <hr class="hr">
  <p class="c b">DANFE NFC-e</p>
  <p class="c" style="font-size:8px;">Documento Auxiliar da NFC-e</p>
  <p class="c">${dataHora}</p>
  <p class="row"><span>NFC-e nº:</span><span class="b">${resultado.numero ?? '—'}</span> <span>Série: ${resultado.serie || '1'}</span></p>
  ${cliente?.nome ? `<p class="row"><span>Cliente:</span><span>${cliente.nome}</span></p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">ITENS</p>
  ${itensNfceDanfe.map((i, idx) => `
  <div class="it">
    <p><b>${codigoItemCarrinho(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.qtd).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.un} × R$ ${fmtBRL(i.preco_unit)}</span>
      <span class="b">R$ ${fmtBRL(i.total)}</span>
    </p>
    ${(i.preco_tabela ?? i.preco_unit) > i.preco_unit ? `<p class="row" style="font-size:9px;color:#555;"><span>de R$ ${fmtBRL(i.preco_tabela)} — desconto item</span><span>– R$ ${fmtBRL((i.preco_tabela - i.preco_unit) * i.qtd)}</span></p>` : ''}
  </div>`).join('')}
  <hr class="hr">
  <p class="row"><span>Qtd. itens:</span><span>${itensNfceDanfe.length}</span></p>
  ${(desconto > 0 || taxaEnt > 0) ? `<p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></p>` : ''}${desconto > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desconto)}</span></p>` : ''}${taxaEnt > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaEnt)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(total)}</span></p>
  ${removeuServicoDanfe ? `<p style="font-size:8px;color:#666;margin-top:2px;">* Total desta NFC-e é só de mercadoria. Itens de serviço da venda não entram aqui (NFS-e ainda não implementada).</p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">PAGAMENTOS</p>
  ${pgtosDanfe.map(p => `<p class="row"><span>${p.forma}</span><span>R$ ${fmtBRL(p.valor)}</span></p>`).join('')}
  <p class="row"><span>Total pago</span><span>R$ ${fmtBRL(totalPagoVal)}</span></p>
  ${trocoVal > 0 ? `<p class="row b"><span>TROCO</span><span>R$ ${fmtBRL(trocoVal)}</span></p>
  <p class="row b" style="border-top:1px dashed #999;padding-top:4px;"><span>TOTAL LÍQUIDO</span><span>R$ ${fmtBRL(totalPagoVal - trocoVal)}</span></p>` : ''}
  ${removeuServicoDanfe ? `<p style="font-size:8px;color:#666;">(valores pagos rateados — esta venda inclui serviço, fora desta NFC-e)</p>` : ''}
  <hr class="hr">
  <p class="c b" style="font-size:9px;">AUTORIZADO O USO DA NFC-e</p>
  <p class="c" style="font-size:8px;margin-top:2px;">Protocolo: ${proto}</p>
  <p class="chave" style="margin:4px 0;">${chaveFormatada}</p>
  ${qrDataUrl ? `<div class="c" style="margin:6px 0;"><img src="${qrDataUrl}" width="150" height="150" style="image-rendering:pixelated;" alt="QR Code"/></div>` : ''}
  ${urlChave ? `<p class="c" style="font-size:8px;">Consulte em: ${urlChave}</p>` : ''}
</body></html>`;

    // O popup já foi aberto lá em cima (contexto de gesto) — se a
    // impressão direta der certo, ele é só fechado sem nunca mostrar nada.
    if (await tentarImprimirDiretoHtml(html, win)) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    await aguardarImagens(win);
    win.focus();
    win.print();
  };

  // DANFE NF-e (A4 simplificado)
  // Abre PDF de DANFE NF-e do backend em nova aba — util compartilhado (impressaoVenda)

  const gerarDanfeNfe = async (resultado) => {
    const docId = resultado.documento_id;
    if (docId) {
      await abrirDanfeNfePdf(`/fiscal/nfe/${docId}/danfe`);
    }
  };

  const MARCA_AGUA = 'NÃO TRANSMITIDO — SEM VALOR FISCAL';

  const gerarPreviewDanfeNfce = async (largura) => {
    const { nome, cnpj, endereco, cidadeUF, fone, dataHora, nrVenda, fs, fsGrand, fsTitulo } = buildCupomBase(largura);
    const larguraCSS = larguraEfetivaCSS(largura);
    // NFC-e é documento de MERCADORIA — item de serviço nunca entra na prévia
    // (serviço vai por NFS-e, ainda não implementada). Desconto E PAGAMENTOS
    // reajustados proporcionalmente à parte de mercadoria, igual à emissão
    // real (senão o troco calculado aqui mostraria a parte do serviço).
    const todosItens = itens;
    const itensNfce = todosItens.filter(i => !ehServico(i.tipo));
    const removeuServico = itensNfce.length < todosItens.length;
    const subtotalTodos = todosItens.reduce((acc, i) => acc + i.total, 0);
    const subtotal = itensNfce.reduce((acc, i) => acc + i.total, 0);
    const proporcao = subtotalTodos > 0 ? subtotal / subtotalTodos : 1;
    const descontoOriginal = parsePT(descontoR);
    const desconto = removeuServico ? +(descontoOriginal * proporcao).toFixed(2) : descontoOriginal;
    const taxaEnt = parsePT(taxaEntregaR);
    const total = Math.max(0, subtotal - desconto) + taxaEnt;
    const logoData = await fetchLogoBase64(orgData?.logo_url || '');
    const logoHtml = logoData ? `<div class="c" style="margin-bottom:4px;"><img src="${logoData}" style="max-width:180px;max-height:70px;object-fit:contain;" /></div>` : '';
    const pgtosPrevia = removeuServico
      ? formasPgto.map(p => ({ ...p, valor: +(parsePT(p.valor) * proporcao).toFixed(2) }))
      : formasPgto;
    const totalPagoVal = pgtosPrevia.reduce((s, p) => s + parsePT(p.valor), 0);
    const trocoVal = Math.max(0, totalPagoVal - total);

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>Prévia DANFE NFC-e</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',Courier,monospace;font-size:${fs};font-weight:600;width:${larguraCSS};padding:4px;}
  .c{text-align:center;}.b{font-weight:bold;}
  .hr{border:none;border-top:1px dashed #000;margin:4px 0;}
  .row{display:flex;justify-content:space-between;}
  .it{margin:2px 0;}.grand{font-size:${fsGrand};}
  .chave{word-break:break-all;font-size:8px;letter-spacing:0.5px;color:#aaa;}
  .nao-transmitido{
    background:#fff3cd;border:2px solid #e65c00;
    padding:4px;text-align:center;font-weight:bold;
    font-size:9px;color:#b83000;margin:4px 0;letter-spacing:0.5px;
  }
  @media print{@page{margin:0;size:${larguraCSS} auto;}}
</style></head><body>
  <div class="nao-transmitido">${MARCA_AGUA}</div>
  ${logoHtml}
  <p class="c b" style="font-size:${fsTitulo};margin-bottom:2px;">${nome}</p>
  <p class="c">CNPJ: ${cnpj}</p>
  ${endereco ? `<p class="c">${endereco}</p>` : ''}
  ${cidadeUF ? `<p class="c">${cidadeUF}</p>` : ''}
  ${fone     ? `<p class="c">${fone}</p>` : ''}
  <hr class="hr">
  <p class="c b">DANFE NFC-e — PRÉVIA</p>
  <p class="c" style="font-size:8px;">Documento Auxiliar da NFC-e</p>
  <p class="c">${dataHora} — Venda nº ${nrVenda}</p>
  ${cliente?.nome ? `<p class="row"><span>Cliente:</span><span>${cliente.nome}</span></p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">ITENS</p>
  ${!itensNfce.length ? `<p style="color:#b83000;font-size:10px;">Esta venda só tem serviço(s) — NFC-e não pode ser emitida sem nenhum item de mercadoria.</p>` : ''}
  ${itensNfce.map((i, idx) => `
  <div class="it">
    <p><b>${codigoItemCarrinho(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.qtd).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.un} × R$ ${fmtBRL(i.preco_unit)}</span>
      <span class="b">R$ ${fmtBRL(i.total)}</span>
    </p>
    ${(i.preco_tabela ?? i.preco_unit) > i.preco_unit ? `<p class="row" style="font-size:9px;color:#555;"><span>de R$ ${fmtBRL(i.preco_tabela)} — desconto item</span><span>– R$ ${fmtBRL((i.preco_tabela - i.preco_unit) * i.qtd)}</span></p>` : ''}
  </div>`).join('')}
  <hr class="hr">
  <p class="row"><span>Qtd. itens:</span><span>${itensNfce.length}</span></p>
  ${(desconto > 0 || taxaEnt > 0) ? `<p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></p>` : ''}${desconto > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desconto)}</span></p>` : ''}${taxaEnt > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaEnt)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(total)}</span></p>
  ${removeuServico ? `<p style="font-size:8px;color:#666;margin-top:2px;">* Total desta NFC-e é só de mercadoria. Itens de serviço da venda não entram aqui (NFS-e ainda não implementada).</p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">PAGAMENTOS</p>
  ${pgtosPrevia.map(p => `<p class="row"><span>${p.forma}</span><span>R$ ${fmtBRL(p.valor)}</span></p>`).join('')}
  <p class="row"><span>Total pago</span><span>R$ ${fmtBRL(totalPagoVal)}</span></p>
  ${trocoVal > 0 ? `<p class="row b"><span>TROCO</span><span>R$ ${fmtBRL(trocoVal)}</span></p>
  <p class="row b" style="border-top:1px dashed #999;padding-top:4px;"><span>TOTAL LÍQUIDO</span><span>R$ ${fmtBRL(totalPagoVal - trocoVal)}</span></p>` : ''}
  ${removeuServico ? `<p style="font-size:8px;color:#666;">(valores pagos rateados — esta venda inclui serviço, fora desta NFC-e)</p>` : ''}
  <hr class="hr">
  <p class="chave" style="margin:4px 0;">Chave: (será gerada após transmissão)</p>
  <div class="nao-transmitido" style="margin-top:6px;">${MARCA_AGUA}</div>
</body></html>`;

    if (await tentarImprimirDiretoHtml(html)) return;
    const win = window.open('', '_blank', `width=${largura==='58mm'?320:400},height=700`);
    if (!win) { alert('Permita pop-ups para imprimir.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    await aguardarImagens(win);
    win.focus();
    win.print();
  };

  const gerarPreviewDanfeNfe = async () => {
    // Se a venda já foi salva, usa o backend para gerar PDF oficial com marca d'água
    if (vendaSalva?.id) {
      await abrirDanfeNfePdf('/fiscal/nfe/danfe/preview', 'post', { venda_id: vendaSalva.id });
    } else {
      alert('Finalize a venda primeiro para gerar a prévia NF-e.');
    }
  };

  const finalizarVenda = async (tipo) => {
    if (tipo === 'imprimir') {
      gerarCupom(larguraCupom);
      setModalFiscal(false);
      limparPDV();
      tentarReabrirMesasComandas();
      return;
    }
    if (tipo === 'sem') {
      setModalFiscal(false);
      limparPDV();
      tentarReabrirMesasComandas();
      return;
    }
    if (tipo === 'a4') {
      if (vendaSalva?.id) {
        try {
          const { data: v } = await api.get(`/vendas/${vendaSalva.id}`);
          await gerarA4Movimentacao(v);
        } catch { alert('Erro ao gerar A4.'); }
      }
      setModalFiscal(false);
      limparPDV();
      tentarReabrirMesasComandas();
      return;
    }
    // NFC-e ou NF-e
    if (!vendaSalva?.id) return;
    setEmitindoFiscal(true);
    setResultadoFiscal(null);
    try {
      const endpoint = tipo === 'nfce' ? '/fiscal/nfce' : '/fiscal/nfe';
      const { data } = await api.post(endpoint, { venda_id: vendaSalva.id });
      setResultadoFiscal({ tipo, ...data });
      if (data.sucesso && tipo === 'nfce') gerarDanfeNfce(data, larguraCupom);
      if (data.sucesso && tipo === 'nfe')  gerarDanfeNfe(data);
    } catch (err) {
      const d = err.response?.data || {};
      console.error('[NFC-e frontend]', d);
      const msg = d.motivo || d.erro || err.message;
      // Cadastro do cliente incompleto p/ NF-e → modal p/ completar e reemitir
      if (d.cliente_incompleto) {
        setModalCompletarCli({ clienteId: d.cliente_id, faltando: d.faltando || [], retry: () => finalizarVenda(tipo) });
      }
      // Venda no VENDA CONSUMIDOR → seleciona o cliente na hora e reemite
      if (d.exige_cliente && vendaSalva?.id) {
        setModalSelecionarCli({ vendaId: vendaSalva.id, retry: () => finalizarVenda(tipo) });
      }
      // Dados fiscais de PRODUTOS faltando → correção direto no cadastro
      const detFiscais = Array.isArray(d?.detalhes) && d.detalhes.length > 0
        && typeof d.detalhes[0] === 'object' ? d.detalhes : null;
      if (detFiscais) abrirCorrecaoFiscal(tipo, detFiscais, () => finalizarVenda(tipo));
      setResultadoFiscal({ tipo, sucesso: false, motivo: msg });
      if (d.xml_gerado || d.sefaz_resposta) {
        setDadosDiagnostico({ ...d, tipo });
      }
    } finally {
      setEmitindoFiscal(false);
    }
  };

  const imprimirBoleto = (boleto) => abrirBoleto(boleto);

  const fecharFiscalELimpar = () => {
    setModalFiscal(false);
    setResultadoFiscal(null);
    limparPDV();
    tentarReabrirMesasComandas();
  };

  // ── Teclado na tela de Documento Fiscal (1-5) ─────────────────
  useEffect(() => {
    if (!modalFiscal || emitindoFiscal || resultadoFiscal) return;
    const handler = e => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const mapa = { '1': 'nfce', '2': 'nfe', '3': 'imprimir', '4': 'sem', '5': 'a4' };
      // Cliente pessoa jurídica: NFC-e não fica disponível para transmissão
      if (mapa[e.key] === 'nfce' && cliente?.tipo_pessoa === 'J') return;
      if (mapa[e.key]) { e.preventDefault(); finalizarVenda(mapa[e.key]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modalFiscal, emitindoFiscal, resultadoFiscal, cliente]); // eslint-disable-line

  // ── Buscar Movimentações ──────────────────────────────────────
  const setFiltroMov = (campo, valor) =>
    setFiltrosMov(prev => ({ ...prev, [campo]: valor }));

  const buscarMovimentacoes = async (filtrosOverride) => {
    const f = filtrosOverride || filtrosMov;
    setBuscandoMov(true);
    try {
      const params = {};
      if (f.dataInicio)  params.dataInicio        = f.dataInicio;
      if (f.dataFim)     params.dataFim           = f.dataFim;
      if (f.cliente)     params.cliente           = f.cliente;
      if (f.codigo)      params.codigo            = f.codigo;
      if (f.finalizacao) params.doc_fiscal_filtro = f.finalizacao;
      if (f.operacao)    params.tipo_movimento_id = f.operacao;
      if (f.excluidas)   params.excluidas         = 'true';
      params.limite = 200;
      const { data } = await api.get('/vendas', { params });
      const lista = Array.isArray(data) ? data : [];
      setMovimentacoes(lista);
      setMovSelecionada(lista[0] || null);
    } catch { setMovimentacoes([]); setMovSelecionada(null); }
    finally { setBuscandoMov(false); }
  };

  // ── Impressão de movimentação selecionada ────────────────────
  const gerarCupomMovimentacao = async (v, largura) => gerarCupomVenda(v, largura, orgData || {});

  const gerarPreviewDanfeNfceMov = async (v, largura) => {
    const org   = orgData || {};
    const nome  = org.nome_fantasia || org.razao_social || 'EMPRESA';
    const cnpj  = (org.cnpj || '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    const end   = [org.logradouro, org.numero, org.bairro].filter(Boolean).join(', ');
    const cidUF = [org.cidade, org.uf].filter(Boolean).join('/');
    const fone  = org.telefone ? `Tel: ${org.telefone}` : '';
    const logoM = await fetchLogoBase64(org.logo_url || '');
    const dataHora = new Date(v.criado_em).toLocaleString('pt-BR');
    // NFC-e é documento de MERCADORIA — item de serviço nunca entra na prévia
    // (serviço vai por NFS-e, ainda não implementada). Desconto E PAGAMENTOS
    // reajustados proporcionalmente à parte de mercadoria, igual à emissão
    // real (senão o troco calculado aqui mostraria a parte do serviço).
    const todosItensMov = v.itens || [];
    const itensV   = todosItensMov.filter(i => !ehServico(i.tipo_item));
    const removeuServicoMov = itensV.length < todosItensMov.length;
    const subtotalTodosMov = todosItensMov.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
    const subtotal = itensV.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
    const proporcaoMov = subtotalTodosMov > 0 ? subtotal / subtotalTodosMov : 1;
    const descontoOriginalMov = parseFloat(v.desconto || 0);
    const desconto = removeuServicoMov ? +(descontoOriginalMov * proporcaoMov).toFixed(2) : descontoOriginalMov;
    const taxaEnt  = parseFloat(v.taxa_entrega || 0);
    const total    = removeuServicoMov ? +(subtotal - desconto + taxaEnt).toFixed(2) : parseFloat(v.total || 0);
    const pgtosTodosMov = v.pagamentos || [];
    const pgtos    = removeuServicoMov
      ? pgtosTodosMov.map(p => ({ ...p, valor: +(parseFloat(p.valor || 0) * proporcaoMov).toFixed(2) }))
      : pgtosTodosMov;
    const totalPago = pgtos.reduce((s, p) => s + parseFloat(p.valor || 0), 0);
    const troco    = Math.max(0, totalPago - total);
    const fs       = largura === '58mm' ? '9px' : '10px';
    const fsGrand  = largura === '58mm' ? '11px' : '13px';
    const fsTitulo = largura === '58mm' ? '12px' : '14px';
    const larguraCSS = larguraEfetivaCSS(largura);

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>Prévia DANFE NFC-e #${v.id}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',Courier,monospace;font-size:${fs};font-weight:600;width:${larguraCSS};padding:4px;}
  .c{text-align:center;}.b{font-weight:bold;}
  .hr{border:none;border-top:1px dashed #000;margin:4px 0;}
  .row{display:flex;justify-content:space-between;}
  .it{margin:2px 0;}.grand{font-size:${fsGrand};}
  .nao-transmitido{background:#fff3cd;border:2px solid #e65c00;padding:4px;text-align:center;font-weight:bold;font-size:9px;color:#b83000;margin:4px 0;letter-spacing:0.5px;}
  @media print{@page{margin:0;size:${larguraCSS} auto;}}
</style></head><body>
  <div class="nao-transmitido">${MARCA_AGUA}</div>
  ${logoM ? `<div class="c" style="margin-bottom:4px;"><img src="${logoM}" style="max-width:180px;max-height:70px;object-fit:contain;" /></div>` : ''}
  <p class="c b" style="font-size:${fsTitulo};margin-bottom:2px;">${nome}</p>
  <p class="c">CNPJ: ${cnpj}</p>
  ${end    ? `<p class="c">${end}</p>` : ''}
  ${cidUF  ? `<p class="c">${cidUF}</p>` : ''}
  ${fone   ? `<p class="c">${fone}</p>` : ''}
  <hr class="hr">
  <p class="c b">DANFE NFC-e — PRÉVIA</p>
  <p class="c" style="font-size:8px;">Documento Auxiliar da NFC-e</p>
  <p class="c">${dataHora} — Venda nº ${v.id}</p>
  ${v.cliente_nome ? `<p class="row"><span>Cliente:</span><span>${v.cliente_nome}</span></p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">ITENS</p>
  ${!itensV.length ? `<p style="color:#b83000;font-size:10px;">Esta venda só tem serviço(s) — NFC-e não pode ser emitida sem nenhum item de mercadoria.</p>` : ''}
  ${itensV.map((i, idx) => `
  <div class="it">
    <p><b>${codigoItem(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.unidade||''} × R$ ${fmtBRL(parseFloat(i.preco_unitario))}</span>
      <span class="b">R$ ${fmtBRL(parseFloat(i.subtotal))}</span>
    </p>
    ${parseFloat(i.desconto || 0) > 0 ? `<p class="row" style="font-size:9px;color:#555;"><span>de R$ ${fmtBRL(parseFloat(i.preco_tabela))} — desconto item</span><span>– R$ ${fmtBRL(parseFloat(i.desconto))}</span></p>` : ''}
  </div>`).join('')}
  <hr class="hr">
  <p class="row"><span>Qtd. itens:</span><span>${itensV.length}</span></p>
  ${(desconto > 0 || taxaEnt > 0) ? `<p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></p>` : ''}${desconto > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desconto)}</span></p>` : ''}${taxaEnt > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaEnt)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(total)}</span></p>
  ${removeuServicoMov ? `<p style="font-size:8px;color:#666;margin-top:2px;">* Total desta NFC-e é só de mercadoria. Itens de serviço da venda não entram aqui (NFS-e ainda não implementada).</p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">PAGAMENTOS</p>
  ${pgtos.map(p => `<p class="row"><span>${p.forma_pagamento}</span><span>R$ ${fmtBRL(parseFloat(p.valor))}</span></p>`).join('')}
  <p class="row"><span>Total pago</span><span>R$ ${fmtBRL(totalPago)}</span></p>
  ${troco > 0 ? `<p class="row b"><span>TROCO</span><span>R$ ${fmtBRL(troco)}</span></p>` : ''}
  ${removeuServicoMov ? `<p style="font-size:8px;color:#666;">(valores pagos rateados — esta venda inclui serviço, fora desta NFC-e)</p>` : ''}
  <hr class="hr">
  <p style="color:#aaa;font-size:8px;margin:4px 0;">Chave: (será gerada após transmissão)</p>
  <div class="nao-transmitido" style="margin-top:6px;">${MARCA_AGUA}</div>
</body></html>`;

    // Prévia disparada do F7 — nunca imprime direto, sempre abre a tela
    // de impressão normal pro usuário escolher impressora/PDF.
    const win = window.open('', '_blank', `width=${largura==='58mm'?320:400},height=700`);
    if (!win) { alert('Permita pop-ups para imprimir.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    await aguardarImagens(win);
    win.focus();
    win.print();
  };

  const gerarPreviewDanfeNfeMov = async (v) => {
    await abrirDanfeNfePdf('/fiscal/nfe/danfe/preview', 'post', { venda_id: v.id });
  };

  const previewDanfeMov = async (tipo) => {
    if (!movSelecionada) return;
    setImprimindoMov(true);
    try {
      if (tipo === 'nfce') {
        const { data: v } = await api.get(`/vendas/${movSelecionada.id}`);
        gerarPreviewDanfeNfceMov(v, larguraCupom);
      } else {
        await gerarPreviewDanfeNfeMov({ id: movSelecionada.id });
      }
    } catch { alert('Erro ao gerar prévia DANFE.'); }
    finally { setImprimindoMov(false); }
  };

  const gerarA4Movimentacao = async (v) => gerarA4Venda(v, orgData || {});

  const imprimirMovimentacao = async (tipo) => {
    if (!movSelecionada) return;
    setImprimindoMov(true);
    try {
      const { data: v } = await api.get(`/vendas/${movSelecionada.id}`);
      if (tipo === 'termica') gerarCupomMovimentacao(v, larguraCupom);
      else gerarA4Movimentacao(v);
    } catch { alert('Erro ao buscar dados da venda.'); }
    finally { setImprimindoMov(false); }
  };

  // ── Emissão fiscal de venda já salva (F7) ───────────────────
  // Reimpressão do DANFE NFC-e no F7: monta com os dados da VENDA + QR/protocolo
  // extraídos do documento gravado (o carrinho do PDV está vazio aqui)
  const imprimirDanfeNfceMov = async () => {
    const alvo = movSelecionada;
    if (!alvo?.doc_fiscal_id) return;
    setImprimindoMov(true);
    try {
      const [{ data: dados }, { data: v }] = await Promise.all([
        api.get(`/fiscal/documentos/${alvo.doc_fiscal_id}/danfe-dados`),
        api.get(`/vendas/${alvo.id}`),
      ]);
      await gerarDanfeNfceVenda(v, dados, larguraCupom, orgData || {});
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao montar o DANFE da NFC-e.');
    } finally {
      setImprimindoMov(false);
    }
  };

  const emitirDocFiscalMov = async (tipo) => {
    if (!movSelecionada) return;
    setEmitindoDocMov(true);
    setResultadoDocMov(null);
    try {
      const endpoint = tipo === 'nfce' ? '/fiscal/nfce' : '/fiscal/nfe';
      const { data } = await api.post(endpoint, { venda_id: movSelecionada.id });
      const resultado = { tipo, ...data };
      setResultadoDocMov(resultado);
      // Atualiza a linha na grade com o novo status — inclusive o ID e o número
      // do documento (a reemissão cria um NOVO doc; cancelar/imprimir usam o id)
      const atualizar = m => ({
        ...m,
        doc_fiscal_tipo:      tipo,
        doc_fiscal_status:    data.sucesso ? 'autorizada' : 'rejeitada',
        doc_fiscal_id:        data.documento_id || m.doc_fiscal_id,
        doc_fiscal_numero:    data.numero       || m.doc_fiscal_numero,
        doc_fiscal_chave:     data.chave_acesso || m.doc_fiscal_chave,
        doc_fiscal_protocolo: data.protocolo    || m.doc_fiscal_protocolo,
      });
      setMovimentacoes(prev => prev.map(m => m.id === movSelecionada.id ? atualizar(m) : m));
      setMovSelecionada(prev => prev ? atualizar(prev) : prev);
      if (data.sucesso && tipo === 'nfce') {
        // DANFE com os dados da VENDA (o carrinho do PDV está vazio no F7)
        try {
          const { data: v } = await api.get(`/vendas/${movSelecionada.id}`);
          await gerarDanfeNfceVenda(v, data, larguraCupom, orgData || {});
        } catch { gerarDanfeNfce(data, larguraCupom); }
      }
      if (data.sucesso && tipo === 'nfe')  gerarDanfeNfe(data);
    } catch (err) {
      const errData = err.response?.data || {};
      console.error('[NFC-e frontend F7]', errData);
      const msg = errData.motivo || errData.erro || err.message;
      const detalhesFiscais = Array.isArray(errData?.detalhes) && errData.detalhes.length > 0
        && typeof errData.detalhes[0] === 'object' ? errData.detalhes : null;
      // Cadastro do cliente incompleto p/ NF-e → modal p/ completar e reemitir
      if (errData.cliente_incompleto) {
        setModalCompletarCli({ clienteId: errData.cliente_id, faltando: errData.faltando || [], retry: () => emitirDocFiscalMov(tipo) });
      }
      // Venda no VENDA CONSUMIDOR → seleciona o cliente na hora e reemite
      if (errData.exige_cliente && movSelecionada?.id) {
        setModalSelecionarCli({ vendaId: movSelecionada.id, retry: () => emitirDocFiscalMov(tipo) });
      }
      setResultadoDocMov({ tipo, sucesso: false, motivo: msg, detalhes_fiscais: detalhesFiscais });
      if (errData.xml_gerado || errData.sefaz_resposta) {
        setDadosDiagnostico({ ...errData, tipo });
      }
    } finally {
      setEmitindoDocMov(false);
    }
  };

  // Cancelamento de NF-e/NFC-e autorizada (evento 110111) — F7
  const cancelarDocFiscalMov = async (mov = null) => {
    const alvo = (mov && mov.doc_fiscal_id) ? mov : movSelecionada;
    if (!alvo?.doc_fiscal_id) return;
    const tipoDoc = alvo.doc_fiscal_tipo === 'nfe' ? 'NF-e' : 'NFC-e';
    // Motivo padrão configurado em Organizações → NFC-e/NF-e → "Motivo de
    // Cancelamento Padrão" já vem sugerido no campo — operador só confirma
    // ou edita, não precisa digitar do zero toda vez.
    const motivoPadrao = orgData?.[alvo.doc_fiscal_tipo]?.motivo_cancelamento_padrao || '';
    const just = window.prompt(
      `Cancelar a ${tipoDoc} nº ${alvo.doc_fiscal_numero || ''} da venda #${alvo.codigo || alvo.id} na SEFAZ?\n\n` +
      `Informe a justificativa (mínimo 15 caracteres):`,
      motivoPadrao
    );
    if (just === null) return;
    if (just.trim().length < 15) { alert('Justificativa deve ter no mínimo 15 caracteres.'); return; }
    const senha = await pedirSenhaAdmin(
      `Cancelar ${tipoDoc} nº ${alvo.doc_fiscal_numero || ''} na SEFAZ.`,
      { acao: 'cancelar_documento_fiscal', detalhes: `${tipoDoc} nº ${alvo.doc_fiscal_numero || ''} — venda #${alvo.codigo || alvo.id}. Justificativa: ${just.trim()}` });
    if (senha === null) return;
    setEmitindoDocMov(true);
    setResultadoDocMov(null);
    try {
      const { data } = await api.post(
        `/fiscal/documentos/${alvo.doc_fiscal_id}/cancelar`,
        { justificativa: just.trim(), senha_admin: senha }
      );
      if (data.sucesso) {
        setResultadoDocMov({ sucesso: true,
          motivo: `${tipoDoc} cancelada na SEFAZ. Protocolo: ${data.protocolo}${data.fora_do_prazo ? ' (registrado fora do prazo)' : ''}` });
        setMovimentacoes(prev => prev.map(m =>
          m.id === alvo.id ? { ...m, doc_fiscal_status: 'cancelada' } : m));
        setMovSelecionada(prev => (prev && prev.id === alvo.id) ? { ...prev, doc_fiscal_status: 'cancelada' } : prev);
      } else {
        setResultadoDocMov({ sucesso: false,
          motivo: `Cancelamento não homologado: ${data.motivo || ''} (cStat ${data.cStat || '—'})` });
      }
    } catch (err) {
      const d = err.response?.data || {};
      setResultadoDocMov({ sucesso: false, motivo: d.erro || d.motivo || 'Erro ao cancelar documento.' });
    } finally {
      setEmitindoDocMov(false);
    }
  };

  /* ── Cancelar a VENDA inteira (estoque volta, financeiro desfeito) ──
     Doc fiscal autorizado é cancelado antes na SEFAZ (motivo obrigatório);
     prazo da SEFAZ excedido ou recebimento já efetivado → devolução. */
  const cancelarVendaMov = async () => {
    const alvo = movSelecionada;
    if (!alvo?.id) return;
    // doc_fiscal_tipo/status vêm COALESCIDOS entre NF-e/NFC-e e NFS-e (ver F7
    // — vendasController.listar) — só pede justificativa de cancelamento na
    // SEFAZ quando o documento é NF-e/NFC-e de verdade. Achado ao vivo
    // (RBDIGITAL): venda 100% serviço (só NFS-e autorizada) caía no mesmo
    // prompt dizendo "e a NFC-e nº 269 na SEFAZ", mas o número 269 era da
    // NFS-e — o backend já foi corrigido pra nunca tentar cancelar NFS-e por
    // esse fluxo (vendasController.cancelar), mas o texto continuava
    // afirmando algo que não ia acontecer de verdade.
    const temNfeNfce = alvo.doc_fiscal_tipo === 'nfce' || alvo.doc_fiscal_tipo === 'nfe';
    const ehFiscalAutorizada = alvo.doc_fiscal_status === 'autorizada' && temNfeNfce;
    // Motivo é sempre pedido junto da senha (pedido explícito) — quando tem
    // NF-e/NFC-e autorizada, o mesmo texto vira a justificativa enviada à
    // SEFAZ (regra deles: mínimo 15 caracteres); sem isso, só um motivo
    // qualquer (mínimo 5) já serve, é só pro histórico da loja.
    let motivo = null;
    if (ehFiscalAutorizada) {
      const tipoDoc = alvo.doc_fiscal_tipo === 'nfe' ? 'NF-e' : 'NFC-e';
      // Motivo padrão configurado em Organizações → NFC-e/NF-e (mesmo campo
      // usado por cancelarDocFiscalMov) — só cai no texto fixo de sempre
      // quando a organização não configurou nada.
      const motivoPadrao = orgData?.[alvo.doc_fiscal_tipo]?.motivo_cancelamento_padrao || 'Cancelado pelo cliente';
      motivo = window.prompt(
        `Cancelar a venda #${alvo.codigo} e a ${tipoDoc} nº ${alvo.doc_fiscal_numero || ''} na SEFAZ?\n\n` +
        `O estoque será devolvido e o rastro financeiro desfeito.\n` +
        `Motivo do cancelamento (mínimo 15 caracteres):`,
        motivoPadrao
      );
      if (motivo === null) return;
      if (motivo.trim().length < 15) { alert('Motivo deve ter no mínimo 15 caracteres.'); return; }
    } else {
      const avisoNfse = (alvo.doc_fiscal_status === 'autorizada' && alvo.doc_fiscal_tipo === 'nfse')
        ? `\n\n⚠ Esta venda tem uma NFS-e autorizada (nº ${alvo.doc_fiscal_numero || ''}) — cancelamento de NFS-e ainda não existe no sistema, ela continuará autorizada mesmo cancelando a venda. Se precisar, cancele manualmente pelo Portal Contribuinte (nfse.gov.br).`
        : '';
      motivo = window.prompt(
        `Cancelar a venda #${alvo.codigo}?\n\nO estoque será devolvido e o rastro financeiro desfeito.${avisoNfse}\n\n` +
        `Motivo do cancelamento (mínimo 5 caracteres):`
      );
      if (motivo === null) return;
      if (motivo.trim().length < 5) { alert('Motivo deve ter no mínimo 5 caracteres.'); return; }
    }
    const senha = await pedirSenhaAdmin(
      `Cancelar a venda #${alvo.codigo}.`,
      { acao: 'cancelar_venda', detalhes: `Venda #${alvo.codigo} — R$ ${fmtBRL(alvo.total)}. Motivo: ${motivo.trim()}` });
    if (senha === null) return;
    setEmitindoDocMov(true);
    setResultadoDocMov(null);
    try {
      const { data } = await api.patch(`/vendas/${alvo.id}/cancelar`, {
        senha_admin: senha,
        motivo: motivo.trim(),
      });
      // Só marca o doc como cancelado na grade se ele REALMENTE foi cancelado
      // na SEFAZ (NF-e/NFC-e) — uma NFS-e autorizada não é tocada por este
      // fluxo (ver vendasController.cancelar) e continua autorizada de
      // verdade, então a grade não pode dizer "Cancelada" pra ela.
      const aplicar = m => ({
        ...m, status: 'cancelada',
        doc_fiscal_status: (m.doc_fiscal_status === 'autorizada' && (m.doc_fiscal_tipo === 'nfce' || m.doc_fiscal_tipo === 'nfe'))
          ? 'cancelada' : m.doc_fiscal_status,
      });
      setResultadoDocMov({ sucesso: true, motivo: data.mensagem || 'Venda cancelada.' });
      setMovimentacoes(prev => prev.map(m => m.id === alvo.id ? aplicar(m) : m));
      setMovSelecionada(prev => (prev && prev.id === alvo.id) ? aplicar(prev) : prev);
    } catch (err) {
      const d = err.response?.data || {};
      const msg = d.erro || 'Erro ao cancelar venda.';
      if (d.prazo_excedido || d.exige_devolucao) alert(msg);
      setResultadoDocMov({ sucesso: false, motivo: msg });
    } finally {
      setEmitindoDocMov(false);
    }
  };

  // "Enviar WhatsApp"/"Enviar E-mail" no F7 — mesma ideia do botão homônimo
  // em Contas a Receber, aqui a partir da VENDA selecionada (ver
  // vendasController.compartilhar/enviarEmailDocumentos). Boleto: quando a
  // venda tem mais de uma parcela/boleto, pega só o mais recente — pra
  // mandar uma parcela específica, o botão equivalente em Contas a Receber
  // (por título) não tem essa ambiguidade.
  const enviarWhatsappMov = async () => {
    const alvo = movSelecionada;
    if (!alvo?.id) return;
    const whats = prepararWhatsApp(); // antes do await — ver aviso de pop-up em impressaoVenda.js
    try {
      const { data } = await api.get(`/vendas/${alvo.id}/compartilhar`);
      if (data.whatsapp_url) { whats.abrir(data.whatsapp_url); return; }
      whats.cancelar();
      alert(data.whatsapp_motivo_indisponivel || 'Não foi possível montar o link do WhatsApp.');
    } catch (err) {
      whats.cancelar();
      alert(err.response?.data?.erro || 'Erro ao montar o link do WhatsApp.');
    }
  };

  const [enviandoEmailMov, setEnviandoEmailMov] = useState(false);
  const enviarEmailMov = async () => {
    const alvo = movSelecionada;
    if (!alvo?.id) return;
    const destino = window.prompt('E-mail de destino (deixe em branco para usar o e-mail cadastrado do cliente):', '');
    if (destino === null) return;
    setEnviandoEmailMov(true);
    try {
      const { data } = await api.post(`/vendas/${alvo.id}/enviar-email`, destino.trim() ? { email: destino.trim() } : {});
      alert(`✅ E-mail enviado para ${data.enviado_para}.\nAnexo(s): ${data.anexos.join(', ')}`);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.erro || 'Erro ao enviar o e-mail.'));
    } finally {
      setEnviandoEmailMov(false);
    }
  };

  // Genérica: F7 (padrão) e pós-venda passam os detalhes + o que reemitir.
  // As correções são gravadas no CADASTRO do produto (PATCH /produtos/:id/fiscal).
  const abrirCorrecaoFiscal = (tipo, detalhes = null, retryFn = null) => {
    const lista = detalhes || resultadoDocMov?.detalhes_fiscais || [];
    const inicial = {};
    lista.forEach(d => { if (d.produto_id) inicial[d.produto_id] = {}; });
    setCorrecoes(inicial);
    setCorrecaoTipo(tipo);
    setCorrecaoDados({ detalhes_fiscais: lista });
    correcaoRetryRef.current = retryFn || (() => emitirDocFiscalMov(tipo));
    setModalCorrecao(true);
  };

  const salvarEReemitir = async () => {
    setSalvandoCorrecao(true);
    try {
      for (const [prodId, vals] of Object.entries(correcoes)) {
        if (Object.keys(vals).length > 0)
          await api.patch(`/produtos/${prodId}/fiscal`, vals);
      }
      setModalCorrecao(false);
      setResultadoDocMov(null);
      await correcaoRetryRef.current?.();
    } catch (err) {
      alert('Erro ao salvar correções: ' + (err.response?.data?.erro || err.message));
    } finally {
      setSalvandoCorrecao(false);
    }
  };

  // ── Pré-venda: carregar pedidos aprovados + devoluções pendentes ──
  useEffect(() => {
    if (!modalPreVenda) return;
    (async () => {
      setBuscandoPrev(true);
      try {
        const { data } = await api.get('/vendas/pedidos', { params: { status: 'aprovado', limite: 50 } });
        setPedidosAprov(Array.isArray(data) ? data : (data.pedidos || []));
      } catch { setPedidosAprov([]); }
      try {
        const { data: devs } = await api.get('/devolucoes/pendentes-caixa');
        setDevolucoesPend(Array.isArray(devs) ? devs : []);
      } catch { setDevolucoesPend([]); }
      finally { setBuscandoPrev(false); }
    })();
  }, [modalPreVenda]);

  // ── Devolução pendente: reembolso pelo turno + NF-e ───────────
  const finalizarDevolucaoCaixa = async (dev) => {
    if (!turnoAtual) { alert('Abra o caixa antes de finalizar a devolução.'); return; }
    if (!window.confirm(
      `Finalizar devolução #${dev.id} (venda #${dev.venda_id})?\n\n` +
      `• Reembolso de R$ ${fmtBRL(dev.total)} SAI do caixa deste turno\n` +
      `• Em seguida você pode transmitir a NF-e de devolução`
    )) return;
    try {
      await api.post(`/devolucoes/${dev.id}/finalizar-caixa`);
      setDevolucoesPend(prev => prev.filter(d => d.id !== dev.id));
      if (window.confirm('Reembolso registrado no caixa!\n\nTransmitir a NF-e de devolução agora?')) {
        try {
          const { data } = await api.post('/fiscal/nfe-devolucao', { devolucao_id: dev.id });
          if (data.sucesso) {
            alert(`NF-e de devolução nº ${data.numero} autorizada!\nProtocolo: ${data.protocolo}`);
            abrirDanfeNfePdf(`/fiscal/nfe/${data.documento_id}/danfe`);
          } else {
            alert(`NF-e rejeitada: ${data.motivo || data.status}`);
          }
        } catch (err) {
          const d = err.response?.data || {};
          alert(d.erro || d.motivo || 'Erro ao transmitir NF-e de devolução.');
        }
      }
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao finalizar devolução no caixa.');
    }
  };

  const importarPreVenda = async (pedidoLista) => {
    if (!pedidoLista?.id) return;
    setBuscandoPrev(true);
    try {
      // Busca o pedido completo com itens
      const { data: pedido } = await api.get(`/vendas/pedidos/${pedidoLista.id}`);

      // Limpa carrinho atual
      setItens([]);
      setItemIdx(null);

      // Define cliente a partir dos dados do pedido
      if (pedido.cliente_id) {
        const clienteObj = {
          id:                pedido.cliente_id,
          nome:              pedido.cliente_nome       || '',
          cpf_cnpj:          pedido.cliente_cpf_cnpj   || '',
          logradouro:        pedido.cl_logradouro      || '',
          numero:            pedido.cl_numero          || '',
          complemento:       pedido.cl_complemento     || '',
          bairro:            pedido.cl_bairro          || '',
          cidade:            pedido.cl_cidade          || '',
          estado:            pedido.cl_uf              || '',
          cep:               pedido.cl_cep             || '',
          fone1:             pedido.cl_fone            || '',
          email:             pedido.cl_email           || '',
          ie:                pedido.cl_ie              || '',
          tipo_pessoa:       pedido.cl_tipo_pessoa     || '',
          tipo_contribuinte: pedido.cl_tipo_contribuinte || '',
        };
        setCliente(clienteObj);
        try {
          const { data: tp } = await api.get(`/clientes/${pedido.cliente_id}/tabela-preco`);
          setTabelaPrecoCliente(tp || null);
        } catch { setTabelaPrecoCliente(null); }
      } else {
        setCliente(clientePadraoRef.current || null); // pedido sem cliente → 1-VENDA CONSUMIDOR
        setTabelaPrecoCliente(null);
      }

      // Adiciona cada item do pedido ao carrinho.
      // O subtotal é a fonte da verdade (vale p/ pedidos antigos — preço cheio
      // com desconto só no subtotal — e novos — preço efetivo gravado);
      // o preço de tabela é reconstruído pelo desconto_perc p/ exibir o desconto.
      (pedido.itens || []).forEach(item => {
        const qtd      = parseFloat(item.quantidade) || 1;
        const descPerc = parseFloat(item.desconto_perc) || 0;
        const sub      = parseFloat(item.subtotal);
        const precoEfetivo = (qtd > 0 && !isNaN(sub) && sub > 0)
          ? parseFloat((sub / qtd).toFixed(4))
          : parseFloat(item.preco_unitario) || 0;
        const precoTabela = (descPerc && descPerc < 100)
          ? parseFloat((precoEfetivo / (1 - descPerc / 100)).toFixed(4))
          : precoEfetivo;
        adicionarItem(
          {
            id:             item.produto_id || null,
            codigo_interno: item.codigo_produto || '',
            nome:           item.descricao      || '',
            unidade:        item.unidade        || 'UN',
            tipo:           item.tipo_item      || 'mercadoria',
          },
          qtd,
          precoEfetivo,
          precoTabela,
        );
      });

      setPedidoOrigemId(pedido.id);
      setPedidoTipoMovId(pedido.tipo_movimento_fiscal_id || null);
      const descPedido = parseFloat(pedido.desconto || 0);
      setDescontoR(descPedido > 0 ? String(descPedido.toFixed(2)).replace('.', ',') : '');

      // Pedido pago por fora (ex: checkout do Catálogo Online via Mercado
      // Pago) — pré-preenche o pagamento pra não pedir de novo no F9; o
      // operador só confirma. Pedido normal: garante que não sobrou nada de
      // uma importação anterior.
      setFormasPgto(pedido.pago_externamente ? [{
        forma:       pedido.forma_pagamento_externa || 'Mercado Pago',
        valor:       parseFloat(pedido.valor_pago_externo ?? pedido.total),
        bandeira_id: null,
        parcelas:    parseInt(pedido.parcelas_pagamento_externo) || 1,
        conta_id:    null,
      }] : []);

      setModalPreVenda(false);
    } catch (err) {
      console.error('Erro ao importar pré-venda:', err);
    } finally {
      setBuscandoPrev(false);
    }
  };

  // ── F1 — Mesas/Comandas ───────────────────────────────────────
  // O pedido inteiro é montado dentro do próprio modal (produtos, itens da
  // comanda, observação, cancelar item) — o carrinho principal do PDV só
  // entra em cena no F9 (finalizarComandaNoPDV), como hand-off pro pagamento.
  // Mesa/comanda foi carregada no carrinho (F9 dentro do modal de mesas) mas
  // a venda foi abandonada (Escape/voltou sem finalizar) antes de concluir —
  // ao reabrir Mesas/Comandas, limpa o carrinho: senão os itens/cliente
  // daquela comanda ficam pendurados no PDV pra próxima operação, inclusive
  // uma venda comum de balcão que nada tem a ver com a mesa.
  const abrirModalMesas = () => {
    if (mesaAtiva) limparPDV();
    setAbaMesasComandas('mesa'); // F1 manual sempre começa na aba Mesas
    setModalMesas(true);
  };

  // Chamado depois que o PDV volta pro carrinho vazio ao final de uma venda
  // de mesa/comanda (todos os caminhos pós-modal-fiscal: imprimiu, não
  // imprimiu, gerou A4, ou emitiu/fechou NFC-e/NF-e) — reabre o F1 sozinho
  // na aba certa (Mesas ou Comandas) se a organização tiver essa config
  // ligada. Desligada por padrão nas duas (pdv_config novo = sempre
  // desligado até alguém marcar, pra não mudar o comportamento de quem já
  // usa o sistema). Preferências independentes: dá pra ligar só uma.
  const tentarReabrirMesasComandas = () => {
    const origem = ultimaVendaOrigem;
    setUltimaVendaOrigem(null);
    if (origem === 'mesa' && pdvCfg.abrir_mesas_ao_finalizar_mesa) {
      setAbaMesasComandas('mesa');
      setModalMesas(true);
    } else if (origem === 'individual' && pdvCfg.abrir_comandas_ao_finalizar_comanda) {
      setAbaMesasComandas('individual');
      setModalMesas(true);
    }
  };

  // Login de usuário Garçom manda direto pra cá (Login.jsx navega com
  // state.abrirMesas) — abre o F1 já na entrada, uma única vez. Limpa o
  // state em seguida pra um F5 não reabrir o modal sozinho depois que o
  // usuário já fechou.
  useEffect(() => {
    if (location.state?.abrirMesas) {
      abrirModalMesas();
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []); // eslint-disable-line

  // Chamado pelo modal ao apertar F9 numa mesa ocupada: carrega os itens da
  // comanda no carrinho de verdade (mesmo formato de importarPreVenda) e
  // abre o fluxo de pagamento padrão do PDV.
  const finalizarComandaNoPDV = (comanda) => {
    setItens([]);
    setItemIdx(null);
    if (comanda.cliente_id) {
      setCliente({ id: comanda.cliente_id, nome: comanda.cliente_nome || '' });
    }
    (comanda.itens || []).forEach(item => {
      adicionarItem(
        {
          id: item.produto_id || null,
          codigo_interno: item.codigo_interno || '',
          nome: item.descricao || '',
          unidade: item.unidade || item.produto_unidade || 'UN',
        },
        parseFloat(item.quantidade) || 0,
        parseFloat(item.valor_unitario) || 0,
        parseFloat(item.valor_unitario) || 0,
      );
    });
    // Guardado na ref (não setDescontoR direto) — ver comentário em
    // descontoComandaRef: o reset do modalSalvar ao abrir apagaria isso.
    descontoComandaRef.current = comanda.desconto || null;
    taxaComandaRef.current = parseFloat(comanda.taxa_entrega) || null;
    setMesaAtiva({
      comandaId: comanda.id, mesaId: comanda.mesa_id,
      numero: comanda.mesa_numero, descricao: comanda.mesa_descricao,
      // 'mesa' | 'individual' — vem do ModalMesasComandas (qual aba estava
      // aberta), não dá pra confiar em mesaId pra isso (comanda avulsa
      // também tem mesa_id preenchido, é uma linha em mesas com tipo=
      // 'comanda' — ver comentário em ModalMesasComandas.jsx#finalizar).
      tipoOrigem: comanda.tipo_venda || 'mesa',
      // Quem abriu a mesa/comanda (garçom, geralmente pelo celular) continua
      // sendo o vendedor da venda pra fins de comissão — só é trocado se o
      // operador do caixa escolher outro vendedor explicitamente abaixo.
      // Nome junto pro card "Vendedor" (LayoutTres) não ficar mostrando
      // sempre quem está logado no caixa, mesmo quando o vendedor de
      // verdade é outra pessoa.
      comandaOperadorId: comanda.operador_id ?? null,
      comandaOperadorNome: comanda.operador_nome || null,
    });
    setModalMesas(false);
    setTimeout(() => verificarEAbrirVenda(), 30);
  };

  // ── Busca de produtos (debounce 300ms) ───────────────────────
  // Este campo é pensado pra bipagem de código/código de barras: número
  // digitado reconhece o produto direto por código exato, sem misturar com
  // resultados que só "contém" aquele dígito no nome. Texto (letra) digitado
  // é entendido como busca por descrição — passa a mão pro F3 (busca
  // avançada), que já tem toda a UI de lista/filtros pronta.
  const buscarProdutosPorCodigo = async termo => {
    try {
      const { data } = await api.get('/produtos', { params: { busca: termo, limite: 8 } });
      return data.filter(p => produtoBateCodigo(p, termo));
    } catch (err) {
      if (err.response) throw err; // erro de verdade do servidor, não é caso de offline
      // Sem internet: cai pro catálogo em cache.
      const cache = await getProdutosCache();
      return cache.filter(p => produtoBateCodigo(p, termo));
    }
  };

  const handleBuscaChange = e => {
    const v = e.target.value;
    // Também aceita o multiplicador "quantidade*código" (ex: 3*789..., 1,250*15),
    // inclusive no meio da digitação ("1," / "1,25" antes do "*").
    const numerico = /^\d*$/.test(v.trim()) || /^\d+([.,]\d*)?(\*\d*)?$/.test(v.trim());
    if (!numerico) {
      setBusca('');
      setSugestoes([]);
      setIdxSug(null);
      clearTimeout(debounceRef.current);
      abrirF3ComTexto(v.trim());
      return;
    }
    // Código de barras: SEM sugestão enquanto digita/bipa — é sempre um
    // leitor (ou digitação manual seguida de Enter mesmo), nunca uma busca
    // "ao vivo". Quem decide é só o Enter (handleBuscaKeyDown): acha e
    // insere direto na venda, ou avisa que não achou. Nada de dropdown no
    // meio do caminho.
    setBusca(v);
    clearTimeout(debounceRef.current);
  };

  // ── Adicionar item ao carrinho ───────────────────────────────
  // precoTabela = preço de referência do produto; quando o preço cobrado é
  // menor (valor digitado no lançamento guiado), a diferença vira desconto.
  // Parâmetros → PDV → Comportamento: "gerar_linha_por_lancamento" — quando
  // marcado, cada lançamento do mesmo produto (pesagem ou bipe) vira uma
  // linha nova em vez de somar na já existente. Vale pra pesado e por
  // unidade igual.
  const gerarLinhaPorLancamento = !!orgData?.pdv_config?.gerar_linha_por_lancamento;

  const adicionarItem = useCallback((produto, quantidade, preco, precoTabela) => {
    setItens(prev => {
      // Primeiro item da venda: se ainda não tem cliente escolhido, cai no
      // 1-VENDA CONSUMIDOR — nunca antes disso, pra não mascarar cliente de
      // uma venda anterior que porventura não tenha sido limpo.
      if (prev.length === 0) setCliente(c => c || clientePadraoRef.current);
      const idx = gerarLinhaPorLancamento ? -1 : prev.findIndex(i => i.id === produto.id);
      if (idx >= 0) {
        const novo = [...prev];
        const novaQtd = parsePT(String(novo[idx].qtd)) + quantidade;
        // Arredonda pra centavos aqui — produto pesado (qtd = peso da balança,
        // várias casas decimais) multiplicado pelo preço gera total com mais
        // de 2 casas (ex: 0,615 * 50,07 = 30,79305); sem arredondar, a tela
        // mostra "R$ 30,80" (fmtBRL só arredonda pra exibição) mas o restante
        // usado na validação do pagamento fica com a fração escondida, e o
        // cliente pagando o valor exibido tomava "valor acima do restante".
        novo[idx] = { ...novo[idx], qtd: novaQtd, preco_unit: preco, total: Math.round(novaQtd * preco * 100) / 100,
          preco_tabela: precoTabela ?? novo[idx].preco_tabela ?? preco };
        setItemIdx(idx);
        return novo;
      }
      setItemIdx(prev.length);
      return [...prev, {
        id:           produto.id,
        codigo:       produto.codigo_interno,
        descricao:    produto.nome,
        tipo:         produto.tipo || 'mercadoria',
        qtd:          quantidade,
        un:           produto.unidade || '',
        preco_unit:   preco,
        preco_tabela: precoTabela ?? preco,
        total:        Math.round(quantidade * preco * 100) / 100,
        // Parâmetro "senha pra excluir item após X segundos" (Organização →
        // Parâmetros → PDV) — precisa do momento exato do lançamento.
        criadoEm:     Date.now(),
      }];
    });
    setTimeout(() => {
      if (gridRef.current) gridRef.current.scrollTop = gridRef.current.scrollHeight;
    }, 30);
  }, [gerarLinhaPorLancamento]);

  // ── Lançamento guiado (Organização → Parâmetros → PDV) ────────
  // Com "pedir quantidade"/"pedir valor unitário" marcados, o Enter no produto
  // não grava direto: o item fica pendente e o cursor percorre Qtd → Preço → grava.
  const pdvCfg = orgData?.pdv_config || {};
  const lancamentoGuiado = !!(pdvCfg.pedir_quantidade || pdvCfg.pedir_valor_unitario);
  const pedirQtdCodigoInterno = !!pdvCfg.pedir_quantidade_codigo_interno;

  const iniciarPendencia = useCallback((produto, preco, { forcarQtd = false, qtdInformada = null } = {}) => {
    setProdutoPendente({ ...produto, _preco_tabela: preco });
    setPrecoUnit(String(preco));
    setUnid(produto.unidade || '');
    setBusca(produto.nome || '');
    setSugestoes([]);
    setIdxSug(null);
    // Quantidade já veio no multiplicador "qtd*código": não lê balança nem
    // pede Qtd de novo, só o preço.
    if (qtdInformada != null) {
      setQtd(String(qtdInformada));
      setTimeout(() => precoRef.current?.focus(), 0);
      return;
    }
    // Balança desligada: não tenta ler o agente local — deixa o campo Qtd
    // pronto pro operador digitar o peso na mão.
    const pesarManual = produto.pesado_no_caixa && !balancaAtiva;
    if (pesarManual) setQtd('');
    // Produto "coringa" (permite_alterar_qtd_preco): quantidade sempre
    // digitada na mão, mesmo que também esteja marcado como pesado no caixa
    // com balança ligada — não faz sentido ler peso automático de um item
    // que ainda nem tem cadastro próprio.
    if (produto.pesado_no_caixa && balancaAtiva && !produto.permite_alterar_qtd_preco) {
      setLendoBalanca(true);
      setErroBalanca('');
      lerPesoBalanca()
        .then(peso => setQtd(String(peso)))
        .catch(err => setErroBalanca(err.message))
        .finally(() => setLendoBalanca(false));
    }
    setTimeout(() => {
      (pdvCfg.pedir_quantidade || forcarQtd || pesarManual || produto.permite_alterar_qtd_preco ? qtdRef : precoRef).current?.focus();
    }, 0);
  }, [pdvCfg.pedir_quantidade, balancaAtiva]);

  // Pesados (F4): mesmo preço do bipe (tabela do cliente / custo do TMF).
  const precoPesado = useCallback(
    produto => calcularPreco(produto, tabelaPrecoCliente, tipoMovFiscal?.usa_preco_custo),
    [tabelaPrecoCliente, tipoMovFiscal]
  );
  const fecharPesados = useCallback(() => {
    setModalPesados(false);
    setTimeout(() => buscaRef.current?.focus(), 0);
  }, []);

  const gravarPendente = useCallback(() => {
    if (!produtoPendente) return;
    const q = parsePT(qtd) || 1;
    if (!validarQtdFracao(produtoPendente, q)) return;
    const p = parsePT(precoUnit) || 0;
    adicionarItem(produtoPendente, q, p, parseFloat(produtoPendente._preco_tabela) || p);
    setProdutoPendente(null);
    setQtd('1');
    setBusca('');
    buscaRef.current?.focus();
  }, [produtoPendente, qtd, precoUnit, adicionarItem]);

  const cancelarPendente = useCallback(() => {
    setProdutoPendente(null);
    setQtd('1');
    setBusca('');
    buscaRef.current?.focus();
  }, []);

  // ── Produto pesado_no_caixa (sem lançamento guiado): lê o peso do
  // agente local e adiciona direto ao carrinho, sem pedir quantidade.
  const adicionarProdutoPesado = useCallback(async (produto, preco) => {
    setLendoBalanca(true);
    setErroBalanca('');
    try {
      const peso = await lerPesoBalanca();
      if (!validarQtdFracao(produto, peso)) return;
      adicionarItem(produto, peso, preco);
      setPrecoUnit(String(preco));
      setUnid(produto.unidade || '');
      setSugestoes([]);
      setIdxSug(null);
      setBusca('');
      setQtd('1');
      buscaRef.current?.focus();
    } catch (err) {
      setErroBalanca(err.message);
    } finally {
      setLendoBalanca(false);
    }
  }, [adicionarItem]);

  // ── Confirmar seleção no modal F3 ────────────────────────────
  const confirmarF3 = useCallback(produto => {
    if (!produto || lendoBalanca) return;
    const p = calcularPreco(produto, tabelaPrecoCliente, tipoMovFiscal?.usa_preco_custo);
    // Produto "coringa" (Cad. Produtos → Atributos → Permite alterar
    // quantidade/preço no PDV): sempre pede Qtd/Preço na hora, mesmo com o
    // lançamento guiado desligado nos Parâmetros — item ainda sem cadastro
    // próprio, preço varia a cada venda.
    if (produto.permite_alterar_qtd_preco) { fecharF3(); iniciarPendencia(produto, p); return; }
    if (lancamentoGuiado) { fecharF3(); iniciarPendencia(produto, p); return; }
    if (produto.pesado_no_caixa) {
      fecharF3();
      if (balancaAtiva) adicionarProdutoPesado(produto, p);
      else iniciarPendencia(produto, p);
      return;
    }
    const q = parsePT(qtd) || 1;
    if (!validarQtdFracao(produto, q)) return;
    adicionarItem(produto, q, p);
    setPrecoUnit(String(p));
    setUnid(produto.unidade || '');
    setQtd('1');
    fecharF3();
  }, [qtd, adicionarItem, tabelaPrecoCliente, tipoMovFiscal, lancamentoGuiado, iniciarPendencia, lendoBalanca, adicionarProdutoPesado, balancaAtiva]);

  // ── Selecionar produto das sugestões ─────────────────────────
  // viaCodigoInterno: true quando veio do Enter na busca principal e o termo
  // digitado bateu com o CÓDIGO INTERNO do produto (não código de barras) —
  // ver produtoBateCodigoInterno e o parâmetro "Pedir quantidade ao lançar
  // por código interno" (Organização → Parâmetros → PDV).
  const selecionarProduto = useCallback((produto, { viaCodigoInterno = false, qtdInformada = null } = {}) => {
    if (lendoBalanca) return;
    const p = calcularPreco(produto, tabelaPrecoCliente, tipoMovFiscal?.usa_preco_custo);
    // Multiplicador "qtd*código": a quantidade digitada vale como está — até
    // pra produto pesado no caixa (é o peso) — e não pede Qtd de novo. Só
    // ainda pede o preço se o produto/parâmetro exigir.
    if (qtdInformada != null) {
      if (produto.permite_alterar_qtd_preco || pdvCfg.pedir_valor_unitario) {
        iniciarPendencia(produto, p, { qtdInformada });
        return;
      }
      if (validarQtdFracao(produto, qtdInformada)) {
        adicionarItem(produto, qtdInformada, p);
        setPrecoUnit(String(p));
        setUnid(produto.unidade || '');
      }
      setSugestoes([]);
      setIdxSug(null);
      setBusca('');
      setQtd('1');
      buscaRef.current?.focus();
      return;
    }
    // Ver comentário em confirmarF3 — mesma regra do produto "coringa".
    if (produto.permite_alterar_qtd_preco) { iniciarPendencia(produto, p); return; }
    if (lancamentoGuiado) { iniciarPendencia(produto, p); return; }
    if (produto.pesado_no_caixa) {
      if (balancaAtiva) adicionarProdutoPesado(produto, p);
      else iniciarPendencia(produto, p);
      return;
    }
    if (viaCodigoInterno && pedirQtdCodigoInterno) { iniciarPendencia(produto, p, { forcarQtd: true }); return; }
    const q = parsePT(qtd) || 1;
    if (!validarQtdFracao(produto, q)) return;
    adicionarItem(produto, q, p);
    setPrecoUnit(String(p));
    setUnid(produto.unidade || '');
    setSugestoes([]);
    setIdxSug(null);
    setBusca('');
    setQtd('1');
    buscaRef.current?.focus();
  }, [qtd, adicionarItem, tabelaPrecoCliente, tipoMovFiscal, lancamentoGuiado, pedirQtdCodigoInterno, iniciarPendencia, lendoBalanca, adicionarProdutoPesado, balancaAtiva, pdvCfg.pedir_valor_unitario]);

  // ── Remover item selecionado ──────────────────────────────────
  // Prazo pra excluir sem senha (Organização → Parâmetros → PDV →
  // "Prazo pra excluir item sem senha"). O carrinho do PDV é só estado
  // local (nunca vira chamada de rede até a venda ser salva), então a
  // senha é validada num endpoint genérico (POST /liberacoes/validar-
  // senha) em vez de embutida numa rota de exclusão que não existe aqui.
  const removerItemPorIdx = useCallback(async idx => {
    // Venda importada de uma mesa/comanda finalizada — os produtos já foram
    // consumidos no salão, não fazem mais sentido serem alterados aqui.
    if (mesaAtiva) return;
    const item = itens[idx];
    const limiteSeg = parseInt(orgData?.pdv_config?.tempo_senha_exclusao_item) || 0;
    if (item?.criadoEm && limiteSeg > 0) {
      const decorridoSeg = (Date.now() - item.criadoEm) / 1000;
      if (decorridoSeg > limiteSeg) {
        const senha = await pedirSenhaAdmin(
          `Item lançado há mais de ${limiteSeg}s — excluir exige senha de administrador ou gerente.`,
          { acao: 'excluir_item_pdv_apos_prazo', detalhes: `${item.descricao} (${item.qtd} ${item.un})` }
        );
        if (senha === null) return;
        try {
          await api.post('/liberacoes/validar-senha', {
            senha, acao: 'excluir_item_pdv_apos_prazo', detalhes: item.descricao,
          });
        } catch {
          alert('Senha inválida — item não foi excluído.');
          return;
        }
      }
    }
    setItens(prev => prev.filter((_, i) => i !== idx));
    setItemIdx(prev => {
      if (prev === null) return null;
      if (prev === idx) return idx > 0 ? idx - 1 : null;
      return prev > idx ? prev - 1 : prev;
    });
    buscaRef.current?.focus();
  }, [mesaAtiva, itens, orgData]);

  // ── Enter/setas no campo busca ────────────────────────────────
  // ── Etiqueta de balança (Parâmetros → PDV → Etiqueta de balança) ──
  const cfgEtiqueta = configEtiqueta(pdvCfg);

  const buscarProdutoDaEtiqueta = async etiqueta => {
    const bate = p => produtoBateEtiqueta(p, etiqueta, cfgEtiqueta);
    try {
      const { data } = await api.get('/produtos', {
        params: { busca: String(Number(etiqueta.codigoProduto)), produto_balanca: 'true', limite: 8 },
      });
      return data.find(bate) || null;
    } catch (err) {
      if (err.response) throw err;
      return (await getProdutosCache()).find(bate) || null;
    }
  };

  // O valor da etiqueta manda: a quantidade é o peso (valor ÷ preço/kg do
  // cadastro, o mesmo que foi pra balança), sem ler o agente local nem pedir
  // Qtd/Preço. Não passa por validarQtdFracao — o peso veio da balança.
  const lancarEtiqueta = (produto, etiqueta) => {
    if (lendoBalanca) return;
    const p = calcularPreco(produto, tabelaPrecoCliente, tipoMovFiscal?.usa_preco_custo);
    const q = quantidadeDaEtiqueta(etiqueta, parseFloat(produto.preco_venda) || p);
    if (!q) {
      alert(`Etiqueta de balança de "${produto.nome}" não pôde ser lida: produto sem preço de venda no cadastro.`);
    } else {
      adicionarItem(produto, q, p);
      setPrecoUnit(String(p));
      setUnid(produto.unidade || '');
    }
    setSugestoes([]);
    setIdxSug(null);
    setBusca('');
    setQtd('1');
    buscaRef.current?.focus();
  };

  const handleBuscaKeyDown = async e => {
    // Item pendente de Qtd/Preço: a busca fica travada até gravar ou cancelar (Esc)
    if (produtoPendente) {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelarPendente(); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        (pdvCfg.pedir_quantidade || produtoPendente.permite_alterar_qtd_preco ? qtdRef : precoRef).current?.focus();
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      let termo = busca.trim();
      if (!termo) return;
      let qtdInformada = null;
      if (/[.,]/.test(termo) && !termo.includes('*')) {
        alert('Use quantidade * código.\nEx.: 3*7891234567890 ou 1,250*15');
        return;
      }
      if (termo.includes('*')) {
        const m = termo.match(/^(\d+(?:[.,]\d+)?)\*(\d+)$/);
        const q = m ? parsePT(m[1]) : 0;
        if (!q) {
          alert('Use quantidade * código.\nEx.: 3*7891234567890 ou 1,250*15');
          return;
        }
        qtdInformada = q;
        termo = m[2];
      }
      // Código de barras: um Enter só, direto — do leitor (que já manda o
      // Enter sozinho) ou digitado na mão. Acha e insere na venda; se não
      // achar, avisa (mais de 1 resultado exato é caso raro de colisão de
      // código entre produtos — insere o primeiro em vez de travar o caixa
      // pedindo pra escolher).
      try {
        // Etiqueta impressa pela balança (EAN-13 com "2" + código + valor):
        // só vale pra produto marcado "Produto de balança"; sem produto, segue
        // a busca normal (pode ser um EAN interno da loja começando com 2).
        const etiqueta = qtdInformada == null ? lerEtiquetaBalanca(termo, cfgEtiqueta) : null;
        if (etiqueta) {
          const prodEtiqueta = await buscarProdutoDaEtiqueta(etiqueta);
          if (prodEtiqueta) { lancarEtiqueta(prodEtiqueta, etiqueta); return; }
        }
        const exatos = await buscarProdutosPorCodigo(termo);
        if (exatos.length) {
          // Código de barras de embalagem (caixa com 24 = quantidade 24 no
          // cadastro): lança as unidades da caixa — "2*caixa" = 2 caixas.
          const qtdEmb = parseFloat(exatos[0].codigos_barras?.find(cb => cb.codigo === termo)?.quantidade);
          if (qtdEmb > 1) qtdInformada = (qtdInformada || 1) * qtdEmb;
          selecionarProduto(exatos[0], { viaCodigoInterno: produtoBateCodigoInterno(exatos[0], termo), qtdInformada });
        } else {
          alert('Produto não cadastrado ou não encontrado.');
          setBusca('');
          buscaRef.current?.focus();
        }
      } catch {}
    }
    // Com texto digitado, o 1º Esc só limpa o campo (não deixa passar pro
    // handler global) — só um Esc "de verdade" (campo já vazio) vira Sair.
    if (e.key === 'Escape' && busca) { e.stopPropagation(); setBusca(''); }
  };

  // ── Busca de clientes ────────────────────────────────────────
  const handleBuscaClienteChange = e => {
    setBuscaCliente(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!e.target.value.trim()) { setClientes([]); setIdxCli(null); return; }
      setBuscandoCli(true);
      try {
        const { data } = await api.get('/clientes', { params: { busca: e.target.value, limite: 12 } });
        setClientes(data);
        setIdxCli(data.length ? 0 : null);
        cacheClientes(data);
      } catch (err) {
        if (err.response) { setClientes([]); setIdxCli(null); }
        else {
          // Sem internet: busca só entre os clientes já vistos neste PC
          // (últimos usados/buscados enquanto online).
          const termo = e.target.value.trim().toLowerCase();
          const cache = (await getClientesCache()).filter(c =>
            c.nome?.toLowerCase().includes(termo) || (c.cpf_cnpj || '').includes(termo));
          setClientes(cache);
          setIdxCli(cache.length ? 0 : null);
        }
      }
      finally { setBuscandoCli(false); }
    }, 300);
  };

  // Recalcula o preço dos itens já lançados quando a tabela de preço em
  // vigor muda (troca de cliente) — busca o produto fresco de cada item
  // (o carrinho não guarda o preço de cadastro "puro", só a referência do
  // momento em que foi lançado) e reaplica calcularPrecoTabela contra a
  // NOVA tabela (ou null, se o cliente novo não tiver tabela — nesse caso
  // calcularPrecoTabela já devolve o preço de cadastro normal). Item com
  // desconto manual (preco_unit < preco_tabela) preserva o PERCENTUAL do
  // desconto, não o valor cru — assim o desconto continua valendo em cima
  // do preço novo, em vez de ser apagado ou ficar preso ao preço antigo.
  const recalcularPrecosPorTabela = useCallback(async novaTabela => {
    if (!itens.length) return;
    try {
      const frescos = await Promise.all(
        itens.map(it => api.get(`/produtos/${it.id}`).then(r => r.data).catch(() => null))
      );
      let mudou = false;
      const usaCusto = tipoMovFiscal?.usa_preco_custo;
      const novosItens = itens.map((it, i) => {
        const prod = frescos[i];
        if (!prod) return it;
        const novaRef = calcularPrecoTabela(prod, novaTabela, usaCusto);
        const refAtual = it.preco_tabela ?? it.preco_unit;
        if (Math.abs(novaRef - refAtual) < 0.005) return it;
        mudou = true;
        const descPct = refAtual > 0 ? Math.max(0, 1 - (it.preco_unit / refAtual)) : 0;
        const novoUnit = +(novaRef * (1 - descPct)).toFixed(2);
        return { ...it, preco_unit: novoUnit, preco_tabela: novaRef, total: Math.round(it.qtd * novoUnit * 100) / 100 };
      });
      if (mudou) {
        setItens(novosItens);
        alert(novaTabela
          ? `Preços dos itens já lançados foram atualizados conforme a tabela de preço "${novaTabela.descricao}" do cliente.`
          : 'Preços dos itens já lançados foram atualizados para o preço de cadastro (cliente sem tabela de preço vinculada).');
      }
    } catch { /* troca de cliente não pode travar por causa disso */ }
  }, [itens, tipoMovFiscal]);

  const selecionarCliente = async c => {
    setCliente(c);
    setModalCliente(false);
    setBuscaCliente('');
    setClientes([]);
    setIdxCli(null);
    buscaRef.current?.focus();
    cacheCliente(c);
    // Busca tabela de preço vinculada ao cliente
    let novaTabela = null;
    try {
      const { data } = await api.get(`/clientes/${c.id}/tabela-preco`);
      novaTabela = data || null;
    } catch {
      novaTabela = null;
    }
    setTabelaPrecoCliente(novaTabela);
    await recalcularPrecosPorTabela(novaTabela);
  };

  const limparCliente = async () => {
    setCliente(clientePadraoRef.current || null); // "limpar" volta ao 1-VENDA CONSUMIDOR
    setTabelaPrecoCliente(null);
    setModalCliente(false);
    setBuscaCliente('');
    setClientes([]);
    setIdxCli(null);
    buscaRef.current?.focus();
    await recalcularPrecosPorTabela(null);
  };

  // ── Render ───────────────────────────────────────────────────
  // Primeira abertura sem cache do layout: tela neutra até o servidor responder
  // (evita desenhar o layout errado e trocar na frente do operador)
  if (layoutPdv === null) {
    return (
      <div style={{
        height: '100vh', background: '#f1f5f9', display: 'flex',
        alignItems: 'center', justifyContent: 'center', color: '#94a3b8',
        fontFamily: 'Segoe UI, system-ui, sans-serif', fontSize: 14,
      }}>
        Abrindo o PDV…
      </div>
    );
  }

  const limparItens = () => {
    if (mesaAtiva || !itens.length) return;
    if (window.confirm('Limpar TODOS os itens da venda?')) { setItens([]); setItemIdx(null); }
  };

  return (
    <div className={s.pdvWrap}>

      {/* ════ Balança (agente local) — banner comum aos 4 layouts ════ */}
      {lendoBalanca && (
        <div className={s.balancaAviso}>⚖️ Lendo peso da balança…</div>
      )}
      {!lendoBalanca && erroBalanca && (
        <div className={`${s.balancaAviso} ${s.balancaAvisoErro}`} onClick={() => setErroBalanca('')}>
          ⚖️ {erroBalanca} <span className={s.balancaAvisoFechar}>✕</span>
        </div>
      )}
      {avisoOffline && (
        <div className={s.balancaAviso} onClick={() => setAvisoOffline('')}>
          📥 {avisoOffline} <span className={s.balancaAvisoFechar}>✕</span>
        </div>
      )}
      {avisoRascunho && (
        <div className={s.balancaAviso}>
          🗂️ Carrinho recuperado ({itens.length} {itens.length === 1 ? 'item' : 'itens'}) — a página
          recarregou com uma venda em andamento.
          <span
            style={{ marginLeft: 10, textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => { limparPDV(); setAvisoRascunho(false); }}
          >
            Descartar
          </span>
          <span className={s.balancaAvisoFechar} onClick={() => setAvisoRascunho(false)}>✕</span>
        </div>
      )}

      <PainelSincronizacao aberto={modalPendencias} onFechar={() => setModalPendencias(false)} sincronizarAgora={sincronizarAgora} />

      {/* ════ LAYOUT 3 — Moderno E+ Gestor (Parâmetros → PDV) ════ */}
      {(layoutPdv ?? 1) === 3 ? (
        <LayoutTres
          orgData={orgData} usuario={usuario} turnoAtual={turnoAtual}
          abrirCaixaClick={() => {
            if (!turnoAtual) {
              setSuprimentoCaixa('0,00'); setErroAbertura(''); setPendingVenda(false);
              setModalAbrirCaixa(true);
            } else setModalTotais(true);
          }}
          abrirTotais={() => setModalTotais(true)}
          cliente={cliente} setModalCliente={setModalCliente} fidelidadeCliente={fidelidadeCliente}
          obsTexto={obsTexto} setModalObs={setModalObs}
          busca={busca} handleBuscaChange={handleBuscaChange} handleBuscaKeyDown={handleBuscaKeyDown}
          buscaRef={buscaRef} produtoPendente={produtoPendente}
          sugestoes={sugestoes} idxSug={idxSug} setIdxSug={setIdxSug}
          selecionarProduto={selecionarProduto} setSugestoes={setSugestoes}
          qtd={qtd} setQtd={setQtd} qtdRef={qtdRef}
          precoUnit={precoUnit} setPrecoUnit={setPrecoUnit} precoRef={precoRef}
          totalCampo={totalCampo} unid={unid}
          pdvCfg={pdvCfg} gravarPendente={gravarPendente} cancelarPendente={cancelarPendente}
          itens={itens} itemIdx={itemIdx} setItemIdx={setItemIdx}
          removerItemPorIdx={removerItemPorIdx} limparItens={limparItens}
          qtdItens={qtdItens} totalVenda={totalVenda} fmtBRL={fmtBRL}
          abrirPreVenda={() => { setModalPreVenda(true); setBuscandoPrev(true); }}
          abrirF3={abrirF3} abrirMov={() => setModalBuscarMov(true)}
          verificarEAbrirVenda={verificarEAbrirVenda} setMenuOpcoes={setMenuOpcoes}
          mesaAtiva={mesaAtiva} abrirModalMesas={abrirModalMesas} abrirPesados={abrirPesados}
          balancaAtiva={balancaAtiva} toggleBalanca={toggleBalanca}
          online={online} pendentesCount={pendentesCount} abrirPendencias={() => setModalPendencias(true)}
        />
      ) : (
      <>
      {/* ════ PAINEL ESQUERDO ════ */}
      {(layoutPdv ?? 1) === 2 || layoutPdv === 4 ? (
        /* ── Layout 2: Sidebar com seções informativas — e Layout 4, que é
           essa MESMA marcação, só reestilizada em cores claras via
           body[data-pdv-tema='claro'] (ver PDV.module.css) ── */
        <div className={s.painelEsqL2}>

          {/* Logo — mesma área do Layout 1 */}
          <div className={s.l2LogoArea}>
            {orgData?.logo_url
              ? <img
                  src={orgData.logo_url.startsWith('http') ? orgData.logo_url : `${BACKEND_URL}${orgData.logo_url}`}
                  alt="Logo" className={s.l2LogoImg}
                />
              : <div className={s.l2LogoPlaceholder}>
                  {orgData?.nome_fantasia || orgData?.razao_social || 'SUA LOGO AQUI'}
                </div>
            }
          </div>

          {/* EMPRESA */}
          <div className={s.l2Section}>
            <span className={s.l2SectionLabel}>EMPRESA</span>
            <span className={s.l2EmpresaValue}>
              {usuario?.organizacao_nome || orgData?.nome_fantasia || orgData?.razao_social || '—'}
            </span>
          </div>

          {/* Caixa */}
          {turnoAtual !== undefined && (
            <div
              title={turnoAtual
                ? `Aberto às ${new Date(turnoAtual.abertura_em).toLocaleTimeString('pt-BR', {hour:'2-digit',minute:'2-digit'})}`
                : 'Clique para abrir o caixa'}
              onClick={() => {
                if (!turnoAtual) {
                  setSuprimentoCaixa('0,00'); setErroAbertura(''); setPendingVenda(false);
                  setModalAbrirCaixa(true);
                }
              }}
              className={turnoAtual ? s.l2CaixaBtnAberto : s.l2CaixaBtnFechado}
            >
              {turnoAtual ? '● Caixa Aberto' : '> Caixa Fechado — clique para abrir'}
            </div>
          )}
          <div className={s.l2Spacer} />

          {/* F10 — CLIENTE */}
          <div className={s.l2Section} onClick={() => setModalCliente(true)} style={{ cursor: 'pointer' }}>
            <span className={s.l2SectionLabel}>{mesaAtiva ? `MESA ${String(mesaAtiva.numero).padStart(2, '0')}` : 'F10 — CLIENTE'}</span>
            <span className={s.l2SectionValue}>
              {cliente ? cliente.nome : 'Não informado'}
            </span>
            {tabelaPrecoCliente && (
              <span className={s.l2SectionSub}>{tabelaPrecoCliente.descricao}</span>
            )}
            {fidelidadeCliente?.ativo && (
              <span className={s.l2SectionPontos} title="Saldo de pontos de fidelidade deste cliente">
                ⭐ {fidelidadeCliente.saldo_pontos} pts
              </span>
            )}
          </div>

          {/* VENDEDOR */}
          <div className={s.l2Section}>
            <span className={s.l2SectionLabel}>VENDEDOR</span>
            <span className={s.l2SectionValue}>
              {usuario?.nome || 'Não informado'}
            </span>
          </div>

          {/* FORMA DE PAGAMENTO */}
          <div className={s.l2Section}>
            <span className={s.l2SectionLabel}>FORMA DE PAGAMENTO</span>
            <span className={s.l2SectionValue}>
              {formasPgto.length > 0
                ? formasPgto.map(p => p.forma).join(', ')
                : formaPgtoSel || 'Não definida'}
            </span>
          </div>

          {/* OBSERVAÇÕES */}
          <div className={s.l2Section}>
            <span className={s.l2SectionLabel}>OBSERVAÇÕES</span>
            <span className={s.l2SectionValue}>
              {obsTexto || 'Nenhuma observação'}
            </span>
          </div>

          <button
            type="button"
            className={`${s.balancaToggleBtn} ${online ? s.balancaToggleBtnAtiva : s.balancaToggleBtnInativa}`}
            onClick={() => setModalPendencias(true)}
            title={online ? 'Conectado' : 'Sem conexão — vendas ficam salvas localmente até sincronizar'}
          >
            {online ? '🟢 Online' : '🟡 Offline'}{pendentesCount > 0 ? ` — ${pendentesCount} pendente${pendentesCount > 1 ? 's' : ''}` : ''}
          </button>

          <button
            type="button"
            className={`${s.balancaToggleBtn} ${balancaAtiva ? s.balancaToggleBtnAtiva : s.balancaToggleBtnInativa}`}
            onClick={toggleBalanca}
            title={balancaAtiva
              ? 'Balança ligada — clique pra desligar e informar o peso na mão'
              : 'Balança desligada — peso é digitado na mão. Clique pra voltar a capturar da balança'}
          >
            ⚖️ Balança: {balancaAtiva ? 'Ligada' : 'Desligada'}
          </button>

          {/* MOV badge — vai para o rodapé via margin-top:auto no CSS */}
          <div className={tipoMovFiscal ? s.l2MovBadgeImport : s.l2MovBadgeDireto}>
            <span className={s.l2MovLabel}>MOV.</span>
            <span className={s.l2MovNome}>
              {tipoMovFiscal ? tipoMovFiscal.nome : 'Venda Direta'}
            </span>
          </div>

        </div>
      ) : (
        /* ── Layout 1: Padrão (logo ampla) ── */
        <div className={s.painelEsq}>
          <div className={s.logoArea}>
            {orgData?.logo_url
              ? <img
                  src={orgData.logo_url.startsWith('http') ? orgData.logo_url : `${BACKEND_URL}${orgData.logo_url}`}
                  alt="Logo" className={s.logoImg}
                />
              : <div className={s.logoPlaceholder}>
                  {orgData?.nome_fantasia || orgData?.razao_social || 'SUA LOGO AQUI'}
                </div>
            }
          </div>
          {usuario?.organizacao_nome && (
            <div className={s.orgInfo}>
              <span className={s.orgInfoLabel}>Empresa</span>
              <span className={s.orgInfoNome}>{usuario.organizacao_nome}</span>
            </div>
          )}
          {/* Indicador de caixa */}
          {turnoAtual !== undefined && (
            <div
              title={turnoAtual ? `Caixa aberto às ${new Date(turnoAtual.abertura_em).toLocaleTimeString('pt-BR', {hour:'2-digit',minute:'2-digit'})}` : 'Clique para abrir o caixa'}
              onClick={() => {
                if (!turnoAtual) {
                  setSuprimentoCaixa('0,00'); setErroAbertura(''); setPendingVenda(false);
                  setModalAbrirCaixa(true);
                }
              }}
              style={{
                margin: '6px 8px 0', padding: '4px 10px',
                borderRadius: 5, fontSize: 11, fontWeight: 'bold', textAlign: 'center',
                background: turnoAtual ? '#dcfce7' : '#fee2e2',
                color: turnoAtual ? '#166534' : '#b91c1c',
                border: `1px solid ${turnoAtual ? '#86efac' : '#fca5a5'}`,
                cursor: turnoAtual ? 'default' : 'pointer',
                userSelect: 'none',
              }}
            >
              {turnoAtual ? '● Caixa Aberto' : '○ Caixa Fechado — clique para abrir'}
            </div>
          )}
          <button className={s.clienteBtn} onClick={() => setModalCliente(true)}>
            <span className={s.clienteF}>{mesaAtiva ? `MESA ${String(mesaAtiva.numero).padStart(2, '0')}` : 'F10 — Cliente'}</span>
            <span className={s.clienteNome}>
              {cliente ? cliente.nome : 'Não informado'}
            </span>
            {tabelaPrecoCliente && (
              <span className={s.clienteTabela}>{tabelaPrecoCliente.descricao}</span>
            )}
            {fidelidadeCliente?.ativo && (
              <span className={s.clientePontos} title="Saldo de pontos de fidelidade deste cliente">
                ⭐ {fidelidadeCliente.saldo_pontos} pts
              </span>
            )}
          </button>

          {/* Indicador de tipo de movimento */}
          <div className={tipoMovFiscal ? s.tipoMovBadgeImport : s.tipoMovBadgeDireto}>
            <span className={s.tipoMovLabel}>Mov.</span>
            <span className={s.tipoMovNome}>
              {tipoMovFiscal ? tipoMovFiscal.nome : 'Venda Direta'}
            </span>
          </div>

          <button
            type="button"
            className={`${s.balancaToggleBtn} ${online ? s.balancaToggleBtnAtiva : s.balancaToggleBtnInativa}`}
            onClick={() => setModalPendencias(true)}
            title={online ? 'Conectado' : 'Sem conexão — vendas ficam salvas localmente até sincronizar'}
          >
            {online ? '🟢 Online' : '🟡 Offline'}{pendentesCount > 0 ? ` — ${pendentesCount} pendente${pendentesCount > 1 ? 's' : ''}` : ''}
          </button>

          <button
            type="button"
            className={`${s.balancaToggleBtn} ${balancaAtiva ? s.balancaToggleBtnAtiva : s.balancaToggleBtnInativa}`}
            onClick={toggleBalanca}
            title={balancaAtiva
              ? 'Balança ligada — clique pra desligar e informar o peso na mão'
              : 'Balança desligada — peso é digitado na mão. Clique pra voltar a capturar da balança'}
          >
            ⚖️ Balança: {balancaAtiva ? 'Ligada' : 'Desligada'}
          </button>
        </div>
      )}

      {/* ════ ÁREA DIREITA ════ */}
      <div className={s.areaDir}>

        {/* Ticker CAIXA LIVRE — só quando caixa aberto, sem itens + 30s de inatividade */}
        {caixaLivre && turnoAtual && (
          <div className={s.caixaLivreBar}>
            <span className={s.caixaLivreTexto}>
              *** CAIXA LIVRE ***
            </span>
          </div>
        )}

        {/* Barra prévia DANFE — visível apenas quando há itens */}
        {itens.length > 0 && (
          <div className={s.infoBar}>
            <div className={s.previaBar}>
              <span className={s.previaBarLabel}>Prévia DANFE</span>
              <button className={s.previaBarBtn} onClick={() => gerarPreviewDanfeNfce(larguraCupom)}>NFC-e</button>
              <button className={s.previaBarBtn} onClick={gerarPreviewDanfeNfe}>NF-e</button>
            </div>
          </div>
        )}

        {/* Grade de itens */}
        <div className={s.gridWrap} ref={gridRef}>
          <table className={s.grid}>
            <colgroup>
              <col className={s.colCod}   />
              <col className={s.colDesc}  />
              <col className={s.colQtd}   />
              <col className={s.colUn}    />
              <col className={s.colPreco} />
              <col className={s.colTotal} />
            </colgroup>
            <thead>
              <tr>
                <th>Cód.</th>
                <th>Descrição</th>
                <th className={s.right}>Qtd.</th>
                <th>UN</th>
                <th className={s.right}>Preço Unit. R$</th>
                <th className={s.right}>Total R$</th>
              </tr>
            </thead>
            <tbody>
              {itens.length === 0 && (
                <tr>
                  <td colSpan={6} className={s.vazioMsg}>
                    Nenhum item adicionado — busque um produto abaixo
                  </td>
                </tr>
              )}
              {(() => {
                const renderLinhaPdv = (item, idx) => (
                  <tr
                    key={idx}
                    className={itemIdx === idx ? s.linhaSelecionada : ''}
                    onClick={() => setItemIdx(idx)}
                  >
                    <td>{item.codigo}</td>
                    <td>{item.descricao}</td>
                    <td className={s.right}>
                      {item.qtd % 1 === 0 ? item.qtd : Number(item.qtd).toFixed(3)}
                    </td>
                    <td>{item.un}</td>
                    <td className={s.right}>
                      {(item.preco_tabela ?? item.preco_unit) > item.preco_unit ? (
                        <>
                          <s style={{ color: '#9ca3af', fontSize: 11, marginRight: 4 }}
                            title={`Preço de tabela — desconto R$ ${fmtBRL((item.preco_tabela - item.preco_unit) * item.qtd)}`}>
                            {fmtBRL(item.preco_tabela)}
                          </s>
                          {fmtBRL(item.preco_unit)}
                        </>
                      ) : fmtBRL(item.preco_unit)}
                    </td>
                    <td className={s.right}>{fmtBRL(item.total)}</td>
                  </tr>
                );
                if (!agruparPdv) return itens.map((item, idx) => renderLinhaPdv(item, idx));

                const comIdx    = itens.map((item, idx) => ({ item, idx }));
                const produtos  = comIdx.filter(({ item }) => !ehServico(item.tipo));
                const servicos  = comIdx.filter(({ item }) => ehServico(item.tipo));
                const grupoHeader   = { textAlign: 'left', fontWeight: 700, padding: '5px 8px', background: '#eef2ff', color: '#1e3a8a' };
                const grupoVazio    = { textAlign: 'center', color: '#aaa', fontStyle: 'italic', padding: 8 };
                const subtotalLabel = { textAlign: 'right', fontWeight: 700, background: '#f8fafc', padding: '4px 8px' };
                const subtotalValor = { fontWeight: 700, background: '#f8fafc', padding: '4px 8px' };
                return (
                  <React.Fragment>
                    <tr><td colSpan={6} style={grupoHeader}>📦 Produtos</td></tr>
                    {produtos.length
                      ? produtos.map(({ item, idx }) => renderLinhaPdv(item, idx))
                      : <tr><td colSpan={6} style={grupoVazio}>Nenhum produto.</td></tr>}
                    <tr>
                      <td colSpan={5} style={subtotalLabel}>Subtotal Produtos:</td>
                      <td style={subtotalValor}>{fmtBRL(subtotalProdutosPdv)}</td>
                    </tr>
                    <tr><td colSpan={6} style={grupoHeader}>🧾 Serviços</td></tr>
                    {servicos.length
                      ? servicos.map(({ item, idx }) => renderLinhaPdv(item, idx))
                      : <tr><td colSpan={6} style={grupoVazio}>Nenhum serviço.</td></tr>}
                    <tr>
                      <td colSpan={5} style={subtotalLabel}>Subtotal Serviços:</td>
                      <td style={subtotalValor}>{fmtBRL(subtotalServicosPdv)}</td>
                    </tr>
                  </React.Fragment>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Barra de entrada */}
        <div className={s.barraEntrada}>
          {/* Linha 1: Qtd, Preço, Total */}
          <div className={s.entradaLinha1}>
            <div className={s.campoGrupo}>
              <span className={s.campoLabel}>Qtd.:</span>
              <input
                ref={qtdRef}
                className={s.inputQtd}
                type="number"
                min="0.001"
                step="any"
                value={qtd}
                style={produtoPendente ? { background: '#fffbeb', borderColor: '#f59e0b', color: '#1f2937' } : undefined}
                onChange={e => setQtd(e.target.value)}
                onFocus={e => e.target.select()}
                onKeyDown={e => {
                  if (!produtoPendente) return;
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (pdvCfg.pedir_valor_unitario || produtoPendente.permite_alterar_qtd_preco) precoRef.current?.focus();
                    else gravarPendente();
                  }
                  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelarPendente(); }
                }}
              />
            </div>
            <div className={s.campoGrupo}>
              <span className={s.campoLabel}>Preço Unit. R$:</span>
              <input
                ref={precoRef}
                className={s.inputPreco}
                type="number"
                min="0"
                step="0.01"
                value={precoUnit}
                style={produtoPendente ? { background: '#fffbeb', borderColor: '#f59e0b', color: '#1f2937' } : undefined}
                onChange={e => setPrecoUnit(e.target.value)}
                onFocus={e => e.target.select()}
                onKeyDown={e => {
                  if (!produtoPendente) return;
                  if (e.key === 'Enter') { e.preventDefault(); gravarPendente(); }
                  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelarPendente(); }
                }}
              />
            </div>
            <div className={s.campoGrupo}>
              <span className={s.campoLabel}>Total R$:</span>
              <input
                className={s.inputTotalCampo}
                readOnly
                tabIndex={-1}
                value={totalCampo}
              />
            </div>
          </div>

          {/* Linha 2: Busca, Unid, Logo */}
          <div className={s.entradaLinha2}>
            <div className={s.campoBuscaWrap}>
              {sugestoes.length > 0 && (
                <div className={s.sugestoes}>
                  {sugestoes.map((p, idx) => (
                    <div
                      key={p.id}
                      className={`${s.sugestaoItem} ${idxSug === idx ? s.sugestaoItemAtivo : ''}`}
                      onMouseDown={() => selecionarProduto(p)}
                      onMouseEnter={() => setIdxSug(idx)}
                    >
                      <span className={s.sugCod}>{p.codigo_interno}</span>
                      <span className={s.sugNome}>{p.nome}</span>
                      <span className={s.sugPreco}>
                        {tipoMovFiscal?.usa_preco_custo
                          ? <><span style={{fontSize:'0.7em',color:'#e65100'}}>CUSTO </span>{fmtBRL(p.preco_custo)}</>
                          : fmtBRL(p.preco_venda)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={buscaRef}
                className={s.inputBusca}
                placeholder="Código de barras ou descrição do produto… (Enter para adicionar)"
                value={busca}
                readOnly={!!produtoPendente}
                style={produtoPendente ? { background: '#fffbeb' } : undefined}
                onChange={handleBuscaChange}
                onKeyDown={handleBuscaKeyDown}
                onBlur={() => setTimeout(() => setSugestoes([]), 150)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className={s.campoUnidGrupo}>
              <span className={s.campoLabel}>Unid.:</span>
              <input className={s.inputUnid} readOnly tabIndex={-1} value={unid} />
            </div>
            <div className={s.logoSecundaria}>
              <span className={s.logoSecundariaMain}>E+</span>
              <span className={s.logoSecundariaSub}>{BRAND_SUFIXO_CURTO}</span>
            </div>
          </div>
        </div>

        {/* Resumo — Total e Itens (bottom) */}
        {agruparPdv && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, padding: '2px 12px', fontSize: 11, color: '#555' }}>
            <span>📦 Produtos: <strong>{fmtBRL(subtotalProdutosPdv)}</strong></span>
            <span>🧾 Serviços: <strong>{fmtBRL(subtotalServicosPdv)}</strong></span>
          </div>
        )}
        <div className={s.resumoBar}>
          <div className={s.resumoItens}>
            <span className={s.resumoItensLabel}>Itens</span>
            <span className={s.resumoItensValor}>
              {qtdItens % 1 === 0 ? qtdItens : qtdItens.toFixed(3)}
            </span>
          </div>
          <div className={s.resumoTotal}>
            <span className={s.resumoTotalLabel}>Total R$</span>
            <span className={s.resumoTotalValor}>{fmtBRL(totalVenda)}</span>
          </div>
        </div>

        {/* Rodapé */}
        <div className={s.rodape}>
          <div className={s.rodapeEsq}>
            <span className={s.rodapeUsuario}>{usuario?.nome || 'Usuário'}</span>
            <span className={s.rodapePerfilLabel}>{usuario?.perfil || ''}</span>
            {mesaAtiva && (
              <span className={s.rodapePerfilLabel} style={{ marginLeft: 10, color: 'var(--primary, #1565FF)', fontWeight: 700 }}>
                🍽️ MESA {String(mesaAtiva.numero).padStart(2, '0')}
              </span>
            )}
          </div>
          <div className={s.rodapeBtns}>
            {[
              ...(pdvCfg.botao_pesados
                ? [{ tecla: 'F4', label: 'Pesados', acao: abrirPesados, desabilitado: !!mesaAtiva }]
                : []),
              { tecla: 'F2',       label: 'Pré-venda',  acao: () => { setModalPreVenda(true); setBuscandoPrev(true); } },
              { tecla: 'F3',       label: 'Busca',      acao: () => abrirF3() },
              { tecla: 'F7',       label: 'Moviment.',  acao: () => setModalBuscarMov(true) },
              { tecla: 'F9',       label: 'Salvar',     acao: () => verificarEAbrirVenda() },
              { tecla: 'F10',      label: 'Cliente',    acao: () => setModalCliente(true) },
              { tecla: 'Ctrl+F12', label: 'Opções',     acao: () => setMenuOpcoes(true) },
              { tecla: 'Del',      label: 'Remover',    acao: () => { if (itemIdx !== null) removerItemPorIdx(itemIdx); }, desabilitado: !!mesaAtiva },
            ].map(({ tecla, label, acao, desabilitado }) => (
              <button key={tecla} className={s.rodapeBtnAtalho} onClick={acao} type="button" tabIndex={-1}
                disabled={desabilitado}
                title={desabilitado ? 'Venda importada da mesa — os itens não podem mais ser alterados aqui.' : undefined}>
                <span className={s.rodapeBtnTecla}>{tecla}</span>
                <span className={s.rodapeBtnLabel}>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      </>
      )}

      {/* ════ PESADOS (F4) — grade de fotos, peso da balança ou digitado ════ */}
      <ModalPesados
        aberto={modalPesados}
        filtro={pdvCfg.pesados_filtro}
        balancaAtiva={balancaAtiva}
        onToggleBalanca={toggleBalanca}
        precoDoProduto={precoPesado}
        onAdicionar={adicionarItem}
        onFechar={fecharPesados}
      />

      {/* ════ MODAL F3 — BUSCA AVANÇADA DE PRODUTOS ════ */}
      <ModalBuscaProdutos modalF3={modalF3} fecharF3={fecharF3} filtrosF3={filtrosF3} setFiltro={setFiltro} setFiltrosF3={setFiltrosF3} FILTROS_VAZIOS={FILTROS_VAZIOS} produtosF3={produtosF3} setProdutosF3={setProdutosF3} buscaRapidaF3={buscaRapidaF3} setBuscaRapidaF3={setBuscaRapidaF3} handleBuscaRapidaF3={handleBuscaRapidaF3} buscandoF3={buscandoF3} totalF3={totalF3} buscarF3={buscarF3} idxF3={idxF3} setIdxF3={setIdxF3} confirmarF3={confirmarF3} tipoMovFiscal={tipoMovFiscal} buscaRapidaRef={buscaRapidaRef} tabela={tabelaPrecoCliente} />

            {/* ════ MODAL CLIENTE (F10) ════ */}
      {/* Venda no VENDA CONSUMIDOR → escolher cliente p/ NF-e (422 exige_cliente) */}
      {modalSelecionarCli && (
        <ModalSelecionarCliente
          onSelect={async (c) => {
            try {
              await api.put(`/vendas/${modalSelecionarCli.vendaId}/cliente`, { cliente_id: c.id });
              const r = modalSelecionarCli.retry;
              setModalSelecionarCli(null);
              r();
            } catch (err) {
              alert(err.response?.data?.erro || 'Erro ao definir o cliente da venda.');
            }
          }}
          onClose={() => setModalSelecionarCli(null)}
        />
      )}

      {/* Cadastro do cliente incompleto p/ NF-e (422 cliente_incompleto) */}
      {modalCompletarCli && (
        <ModalCompletarCliente
          clienteId={modalCompletarCli.clienteId}
          faltando={modalCompletarCli.faltando}
          onSalvo={() => { const r = modalCompletarCli.retry; setModalCompletarCli(null); r(); }}
          onClose={() => setModalCompletarCli(null)}
        />
      )}

      <ModalCliente modalCliente={modalCliente} setModalCliente={setModalCliente} buscaRef={buscaRef} buscaCliRef={buscaCliRef} buscaCliente={buscaCliente} handleBuscaClienteChange={handleBuscaClienteChange} buscandoCli={buscandoCli} clientes={clientes} idxCli={idxCli} selecionarCliente={selecionarCliente} subModalNovoCli={subModalNovoCli} setSubModalNovoCli={setSubModalNovoCli} novoCliNome={novoCliNome} setNovoCliNome={setNovoCliNome} novoCliCpf={novoCliCpf} setNovoCliCpf={setNovoCliCpf} novoCliFone={novoCliFone} setNovoCliFone={setNovoCliFone} erroNovoCli={erroNovoCli} setErroNovoCli={setErroNovoCli} salvarNovoCli={salvarNovoCli} salvandiNovoCli={salvandiNovoCli} limparCliente={limparCliente} />
      {/* ════ MENU OPÇÕES (Ctrl+F12) ════ */}
      <MenuOpcoes menuOpcoes={menuOpcoes} setMenuOpcoes={setMenuOpcoes} turnoAtual={turnoAtual} setModalPreVenda={setModalPreVenda} setModalTotais={setModalTotais} setSaldoFechamento={setSaldoFechamento} setErroFechamento={setErroFechamento} setModalFecharCaixa={setModalFecharCaixa} setModalBuscarMov={setModalBuscarMov} verificarEAbrirVenda={verificarEAbrirVenda} setModalObs={setModalObs} ultimoTurnoFechadoId={ultimoTurnoFechadoId} reimprimirFechamento={reimprimirFechamento} gavetaHabilitada={gavetaHabilitada} acionarGaveta={acionarGaveta} configurarGaveta={configurarGaveta} impressaoDiretaLigada={impressaoDiretaAtiva()} alternarImpressaoDireta={alternarImpressaoDireta} configurarImpressao={configurarImpressao} configurarMargemImpressao={configurarMargemImpressao} sairDoPdv={sairDoPdv} />

            {/* ════ MODAL ABRIR CAIXA ════ */}
      <ModalAbrirCaixa modalAbrirCaixa={modalAbrirCaixa} setModalAbrirCaixa={setModalAbrirCaixa} contaAbertura={contaAbertura} setContaAbertura={setContaAbertura} contasAbertura={contasAbertura} suprimentoCaixa={suprimentoCaixa} setSuprimentoCaixa={setSuprimentoCaixa} erroAbertura={erroAbertura} abrindoCaixa={abrindoCaixa} abrirCaixa={abrirCaixa} setPendingVenda={setPendingVenda} />

            {/* ════ MODAL FECHAR CAIXA ════ */}
      <ModalFecharCaixa modalFecharCaixa={modalFecharCaixa} setModalFecharCaixa={setModalFecharCaixa} turnoAtual={turnoAtual} saldoFechamento={saldoFechamento} setSaldoFechamento={setSaldoFechamento} erroFechamento={erroFechamento} fechandoCaixa={fechandoCaixa} fecharCaixa={fecharCaixa} />

      {/* ════ MODAL SALVAR / FINALIZAR (F9) ════ */}
      <ModalSalvarVenda modalSalvar={modalSalvar} setModalSalvar={setModalSalvar} buscaRef={buscaRef} etapaSalvar={etapaSalvar} setEtapaSalvar={setEtapaSalvar} totalVenda={totalVenda} totalComDesconto={totalComDesconto} taxaEntregaHabilitada={!!orgData?.pdv_config?.taxa_entrega_habilitada} taxaEntregaPadrao={parsePT(orgData?.pdv_config?.taxa_entrega_padrao || 0)} taxaEntregaR={taxaEntregaR} handleTaxaEntrega={handleTaxaEntrega} descontoR={descontoR} descontoP={descontoP} handleDescontoR={handleDescontoR} handleDescontoP={handleDescontoP} descontoRRef={descontoRRef} descontoPRef={descontoPRef} avancarParaPgto={avancarParaPgto} semPagamento={semPagamento} tipoMovFiscal={tipoMovFiscal} formasPgto={formasPgto} setFormasPgto={setFormasPgto} restante={restante} subCartao={subCartao} setSubCartao={setSubCartao} selecionarDebito={selecionarDebito} confirmarDebito={confirmarDebito} selecionarCredito={selecionarCredito} bandeirasDebito={bandeirasDebito} bandeirasCredito={bandeirasCredito} bandeiraSel={bandeiraSel} setBandeiraSel={setBandeiraSel} parcelasCartao={parcelasCartao} formaPgtoSel={formaPgtoSel} setFormaPgtoSel={setFormaPgtoSel} salvarInputRef={salvarInputRef} valorPgtoAtual={valorPgtoAtual} setValorPgtoAtual={setValorPgtoAtual} adicionarPgto={adicionarPgto} pixContaId={pixContaId} setPixContaId={setPixContaId} boletoContaId={boletoContaId} setBoletoContaId={setBoletoContaId} contasAbertura={contasAbertura} cliente={cliente} fidelidadeCliente={fidelidadeCliente} creditoCliente={creditoCliente} parcelasCrediario={parcelasCrediario} setParcelasCrediario={setParcelasCrediario} parcelasBoleto={parcelasBoleto} setParcelasBoleto={setParcelasBoleto} totalPago={totalPago} troco={troco} erroSalvar={erroSalvar} pgtoCompleto={pgtoCompleto} salvando={salvando} confirmarPagamento={confirmarPagamento} confirmarBtnRef={confirmarBtnRef} online={online} codigoAutorizacaoCartao={codigoAutorizacaoCartao} setCodigoAutorizacaoCartao={setCodigoAutorizacaoCartao} pointAtivo={pointAtivo} cobrarNaMaquininha={cobrarNaMaquininha} removerPgto={removerPgto} />
      <ModalCobrancaPoint
        cobranca={cobrancaPoint}
        onCancelar={cancelarCobrancaPoint}
        onFechar={() => { setCobrancaPoint(null); setTimeout(focarValorPgto, 30); }}
        onTentarDeNovo={() => { setCobrancaPoint(null); setTimeout(() => salvarLiveRef.current.cobrarNaMaquininha(), 0); }}
      />
      {modalVendedor && (
        <ModalVendedor
          vendedores={vendedoresPdv}
          vendedorPadraoId={usuario?.id || null}
          onConfirmar={id => { setModalVendedor(false); confirmarPagamento(id ?? null); }}
          onFechar={() => setModalVendedor(false)}
        />
      )}

      {/* ════ MODAL MESAS/COMANDAS (F1) ════ */}
      <ModalMesasComandas
        aberto={modalMesas}
        abaInicial={abaMesasComandas}
        onFechar={() => setModalMesas(false)}
        onFinalizar={finalizarComandaNoPDV}
        toleranciaReservaMin={pdvCfg.tolerancia_reserva_min}
        nomeObrigatorio={!!pdvCfg.cliente_obrigatorio_comanda}
        larguraCupom={larguraCupom}
        taxaEntregaHabilitada={!!pdvCfg.taxa_entrega_habilitada}
        taxaEntregaPadrao={parsePT(pdvCfg.taxa_entrega_padrao || 0)}
        balancaAtiva={balancaAtiva}
        onToggleBalanca={toggleBalanca}
        online={online}
      />

      {/* ════ MODAL FISCAL (após confirmar pagamento) ════ */}
      <ModalFiscal modalFiscal={modalFiscal} vendaSalva={vendaSalva} emitindoFiscal={emitindoFiscal} resultadoFiscal={resultadoFiscal} dadosDiagnostico={dadosDiagnostico} setModalDiagnostico={setModalDiagnostico} gerarDanfeNfce={gerarDanfeNfce} gerarDanfeNfe={gerarDanfeNfe} gerarPreviewDanfeNfce={gerarPreviewDanfeNfce} gerarPreviewDanfeNfe={gerarPreviewDanfeNfe} fecharFiscalELimpar={fecharFiscalELimpar} larguraCupom={larguraCupom} setLarguraCupom={setLarguraCupom} finalizarVenda={finalizarVenda} imprimirBoleto={imprimirBoleto} itensServicoVenda={itensServicoVenda} emitirNfseItem={emitirNfseItem} nfseEmitindo={nfseEmitindo} nfseResultados={nfseResultados} clientePJ={cliente?.tipo_pessoa === 'J'} />

      {/* ════ MODAL BUSCAR MOVIMENTAÇÃO (F7) ════ */}
      <ModalBuscarMovimentacao modalBuscarMov={modalBuscarMov} setModalBuscarMov={setModalBuscarMov} filtrosMov={filtrosMov} setFiltroMov={setFiltroMov} setFiltrosMov={setFiltrosMov} FILTROS_MOV_VAZIOS={FILTROS_MOV_VAZIOS} tiposMovimentoMov={tiposMovimentoMov} buscarMovimentacoes={buscarMovimentacoes} buscandoMov={buscandoMov} movimentacoes={movimentacoes} setMovimentacoes={setMovimentacoes} movSelecionada={movSelecionada} setMovSelecionada={setMovSelecionada} resultadoDocMov={resultadoDocMov} setResultadoDocMov={setResultadoDocMov} dadosDiagnostico={dadosDiagnostico} setModalDiagnostico={setModalDiagnostico} abrirCorrecaoFiscal={abrirCorrecaoFiscal} emitindoDocMov={emitindoDocMov} emitirDocFiscalMov={emitirDocFiscalMov} cancelarDocFiscalMov={cancelarDocFiscalMov} cancelarVendaMov={cancelarVendaMov} imprimirDanfeNfceMov={imprimirDanfeNfceMov} previewDanfeMov={previewDanfeMov} imprimindoMov={imprimindoMov} imprimirMovimentacao={imprimirMovimentacao} abrirDanfeNfePdf={abrirDanfeNfePdf} gerarDanfeNfce={gerarDanfeNfce} larguraCupom={larguraCupom} itensServicoMov={itensServicoMov} emitirNfseItemMov={emitirNfseItemMov} nfseEmitindoMov={nfseEmitindoMov} nfseResultadosMov={nfseResultadosMov} enviarWhatsappMov={enviarWhatsappMov} enviarEmailMov={enviarEmailMov} enviandoEmailMov={enviandoEmailMov} />

      {/* ════ MODAL CORREÇÃO FISCAL ════ */}
      <ModalCorrecaoFiscal modalCorrecao={modalCorrecao} setModalCorrecao={setModalCorrecao} resultadoDocMov={correcaoDados || resultadoDocMov} correcoes={correcoes} setCorrecoes={setCorrecoes} salvandoCorrecao={salvandoCorrecao} salvarEReemitir={salvarEReemitir} correcaoTipo={correcaoTipo} />

            {/* ════ MODAL TOTAIS EM CAIXA ════ */}
      <ModalTotaisCaixa modalTotais={modalTotais} setModalTotais={setModalTotais} carregandoTotais={carregandoTotais}
        totaisCaixa={totaisCaixa}
        onImprimir={async () => {
          // Mesma fonte e mesmo relatório do Fechamento de Caixa oficial
          // (GET /financeiro/turnos/:id/resumo) — só que impresso com o
          // turno ainda aberto (imprimirFechamentoCaixaTermico já rotula
          // como "conferência parcial" nesse caso).
          if (!totaisCaixa?.turno) return;
          try {
            const { data: resumo } = await api.get(`/financeiro/turnos/${totaisCaixa.turno.id}/resumo`);
            imprimirFechamentoCaixaTermico(resumo, larguraCupom);
          } catch { alert('Erro ao carregar o resumo do turno para impressão.'); }
        }} />

            {/* ════ MODAL PRÉ-VENDA (F2) ════ */}
      <ModalPreVenda modalPreVenda={modalPreVenda} setModalPreVenda={setModalPreVenda} buscandoPrev={buscandoPrev} pedidosAprov={pedidosAprov} idxPrev={idxPrev} setIdxPrev={setIdxPrev} importarPreVenda={importarPreVenda} devolucoesPend={devolucoesPend} finalizarDevolucaoCaixa={finalizarDevolucaoCaixa} />

            {/* ════ MODAL DIAGNÓSTICO NFC-e ════ */}
      <ModalDiagnosticoNfce modalDiagnostico={modalDiagnostico} setModalDiagnostico={setModalDiagnostico} dadosDiagnostico={dadosDiagnostico} />

            {/* ════ MODAL OBSERVAÇÃO ════ */}
      <ModalObservacao modalObs={modalObs} setModalObs={setModalObs} obsTexto={obsTexto} setObsTexto={setObsTexto} />
    </div>
  );
}
